/**
 * useGlobalChat Hook
 * 
 * This custom hook manages all state and handlers for the global chat system.
 * It provides a complete, reusable chat state management solution.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { PlatformMessageService } from '../../services/PlatformMessageService';
import { openConstructionNotice } from '../../utils/constructionNotice';
import { useConstructionWaitlist, ConstructionWaitlistController } from '../../hooks/useConstructionWaitlist';
import {
  type GlobalChatState,
  type GlobalChatAction,
  type GlobalChatConfig,
  type GlobalChatHandlers,
  type ChatMessage,
  type SerializedAttachment,
  type SystemMessageInput,
  type TokenUsageSnapshot,
  DEFAULT_CHAT_CONFIG,
  getChatStorageKeys,
  buildPageStorageKey,
  buildLegacyPageStorageKey,
  GLOBAL_CHAT_HISTORY_KEY,
  GLOBAL_CHAT_DRAFT_KEY,
  GLOBAL_CHAT_OPEN_KEY,
  GLOBAL_CHAT_DOCK_KEY,
  GLOBAL_CHAT_FULLSCREEN_KEY,
} from './types';
import { calculateCost } from '../../config/aiModelPricing';
import { useTranscriptScroll, type TranscriptStepPosition } from '../../hooks/useTranscriptScroll';
import { useAgenticWorkflows, createAgenticWorkflowHandler, type AgenticWorkflowsHook } from '../../services/agenticWorkflows';
import { useGlobalTools, registerAllTools, type UseGlobalToolsReturn } from '../../services/globalTools';
import { usePrest0nSession } from '../../services/prest0nSession/usePrest0nSession';
import { publishWorkspaceSettledWithoutAccount } from '../../services/activeWorkspace';
// Point 6 (Option B) — AUTHENTICATED-path durable server-task re-attach client.
// startCloudIngest — ingest-on-upload (2026-06 reliability overhaul): kicks off
// server-side extraction the moment the GCS signed-URL uploads succeed.
import {
  getPrest0nTaskStatusDetailed,
  listActivePrest0nTasks,
  postPrest0nTaskInterjection,
  startCloudIngest,
  // R10 (instant send feedback): the turn-build bounded upload wait must
  // mirror the inline-base64 send guard in useGlobalTools — dispatching
  // before uploads finish is only safe below the inline hard cap.
  MAX_INLINE_DATAURL_TURN_CHARS,
  isInlineEligible,
  judgeAttachmentDelivery,
  sumInlineDataUrlChars,
  // Shared with drop, paste and picker below — the composer's file-count cap
  // lives beside the byte cap that does the real protecting.
  MAX_ATTACHMENTS_PER_MESSAGE,
} from '../../services/prest0nSession/client';
import { shouldUsePrest0nRuntimeForUser, resolveAccountIdFromAuth } from '../../services/prest0nRuntime';
import { isInternalPrest0nSurface } from '../../config/threadSwitcherCapability';
// Cross-tab "an answer is writing" lease — see ./streamingFlag for the design
// (per-tab keys, heartbeat-renewed; this tab's share is DERIVED from the
// per-thread processing flags + queued sends in an effect below).
import {
  noteStreamStarted,
  setLocalStreamingActive,
  refreshStreamingFlag,
  isAnyTabStreaming,
  sweepStaleStreamingFlag,
} from './streamingFlag';
import { getPendingWidgetHtml, getStoppedWidgetHtml } from '../../services/globalTools/widgetHtml';
import { getPendingWidgetWithBranchesHtml, orderBranchEntries } from '../../services/globalTools/branchLane';
// Point 6 (Option B) — server-side durable GUEST deep-research re-attach client.
import {
  pollGuestTaskUntilDone,
  type GuestServerResearchStage,
} from '../../services/globalTools/guestServerResearch';
import {
  formatResearchToHtml,
  ensureReferencesSection,
  stripThinkingBlocks,
  formatDeepResearchQualificationHtml,
} from '../../services/agenticWorkflows/helpers';
// INTERSTITIAL-MESSAGE ELIMINATION (2026-08-13): stable positional button id
// for the clarifying-card rebuild — same source useGlobalTools uses, so the
// resume-rebuilt card is byte-identical to the live-rendered one.
import { BUTTON_IDS as GLOBAL_VIDEO_SCRIPT_BUTTON_IDS } from '../../services/globalTools/tools/videoScript';
// P0 thread-durability (2026-09-02): the connect card is data; its html is a
// projection. The pure rebuild runs at the reducer's SET_MESSAGES boundary,
// the async refresh at hydrate. Same import graph as useGlobalTools already
// carries (the tool registry), so nothing new reaches the leaf hydrate helpers.
import {
  isRebuildableConnectCard,
  rebuildDroppedCardHtml,
  rehydrateConnectTile,
} from '../../services/globalTools/tools/connectTileRehydrate';
// 2026-06 reliability overhaul — per-turn language detector (same one the
// deep-research language mirror uses) so a failed authed resume can surface
// errorInfo.userMessageEs for Spanish-language prompts.
import { detectInputLanguage } from '../../services/agenticWorkflows/languageMirror';
import { findResumableServerTasks, liveServerTaskIdOf, threadHasNoLiveTurn, isInFlightPlaceholderMessage } from './interruptedTurn';
import { planStuckProcessingClears } from './turnIndicator';
// Round O (O2, 2026-07-15): shared body-portal mousedown predicate, also used
// by GlobalChatSystem's token-popup dismiss handler so the two document-level
// mousedown paths apply ONE portal rule.
import { isInsidePrest0nPortal } from './portalClickGuard';
import {
  decideAuthedResumeFinalize,
  buildAuthedInterruptedHtml,
  selectResumeMode,
  sanitizeServerErrorNotice,
  shouldSkipLiveTaskResume,
  planActiveTaskReattach,
  reconcileDiscoveryChips,
  taskMintTimestampMs,
  shouldTreatMissingTaskAsTerminal,
} from './serverTaskResume';
import { isLiveSendInFlight } from './liveSendRegistry';
import { rotateStandaloneConversationId } from '../../services/prest0nSession/conversationIdentity';
import {
  sanitizeHtmlInput,
  htmlToPlainText,
  normalizeChatHtml,
  validateFiles,
  getFromLocalStorage,
  setToLocalStorage,
  removeFromLocalStorage,
  dispatchChatEvent,
  CHAT_EVENTS,
} from './utils';
import { reviveSerializedAttachment } from './attachmentRecords';
import { filesFromClipboard } from './clipboardFiles';
import {
  ATTACHMENT_HEAL_ON_HYDRATE_DELAY_MS,
  attachmentHealCandidates,
  attachmentHealOnHydrateEnabled,
  runAttachmentHealOnHydrate,
} from './attachmentHealOnHydrate';
import {
  sanitizePastedHtml,
  sanitizePastedText,
  plainTextToComposerHtml,
  trimEdgeEmptyBlocks,
} from './pasteSanitizer';
import { enterShouldSend, isCoarsePointer } from './composerEnterPolicy';
import {
  applyProcessingState,
  isOrphanedGlobalProcessingFlag,
  THREAD_ISO_DEBUG_TAG,
  shouldRouteNewMessageToStorage,
  resolveSendHistoryFailClosed,
  resolveOriginatingThreadId,
} from './threadIsolation';
import {
  enqueueMessage,
  dequeueHead,
  flushQueue,
  removeThreadQueue,
  peekHead,
  getQueueDepth,
  isThreadQueueFull,
  MAX_QUEUE_DEPTH,
  THREAD_QUEUE_DEBUG_TAG,
  pendingFromChatMessage,
  type PendingMessage,
} from './threadQueue';
import { markdownToHtml } from '../../utils/markdownProcessor';
import { useLowBalanceWarning, type LowBalanceState } from './useLowBalanceWarning';
import {
  isStaleLowBalanceMessage_v20260513,
  migrateStaleLowBalanceLocalStorage_v20260513,
} from './staleLowBalanceFilter';
import { shouldTagAsTest } from '../../config/agenticWorkflowConfig';
import { clearConversationHistory } from '../../repositories/conversationHistoryRepository';
import { useAuth } from '../../context/AuthContext';
import {
  serializeAttachmentsWithStorage,
  getCurrentUserUid,
  uploadAttachmentViaGcsSignedUrl,
  mapWithConcurrency,
  UPLOAD_CONCURRENCY,
} from '../../utils/attachmentStorage';
import { fernandoTrace, isFernandoStrictMode, FERN_FE_CODES } from '../../services/prest0nRuntime/fernandoTrace';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
// BUG-DR-COMPOSER-REPOP-LONG-TURN (2026-05-20) — shared draft-suppression
// singleton. Coordinates the rehydrate gate across useGlobalChat and
// LandingPage so neither composer can be repopulated mid-turn or during
// the post-send / post-flight cooldown. See draftSuppressionState.ts for
// the full design rationale.
import {
  markSentNow as draftMarkSentNow,
  markSentText as draftMarkSentText,
  isSentTextMatch as draftIsSentTextMatch,
  markProcessingStarted as draftMarkProcessingStarted,
  markProcessingEnded as draftMarkProcessingEnded,
  shouldSuppressDraftRehydrate,
} from './draftSuppressionState';
import { reconcileInterruptedOnHydrate, dedupeMessagesById, clearStaleInterruptedCards, normalizeMessageList, mergeSyncedMessagesPreservingFinalized, messageListsEffectivelyIdentical } from './interruptedTurn';
import { persistFinalizedStreamingMessageToThreadStorage } from './streamFinalizePersist';
import { publishChatUiStateSync, subscribeToChatUiStateSync, sanitizeMessageForPersist } from './threadPersist';
// Round J (2026-07-14): send-ack watchdog + retryable send-failure bubble.
import { createSendAckWatchdog, type SendAckWatchdog, type SendAckEntry } from './sendAckWatchdog';
import {
  SEND_FAILURE_RETRY_SOURCE_TAG,
  sendFailureBubbleText,
  stripYoungestSendFailureBubble,
} from './sendFailureRetry';
// G1 (2026-07-15): real cloud-only guest turn for settled signed-out visitors
// (replaces the Round-J sign-in prompt at both dispatch sites).
import {
  buildGuestChatHistory,
  callGuestChatTurn,
  getGuestDeviceId,
  hasSignedInBeforeMarker,
  type GuestChatTurnResponse,
} from '../../services/guestChat/client';
// L3 (2026-07-15): warm sign-in invitation copy (returning-device vs
// never-signed-in, EN/ES) with a real clickable /auth route.
import { signInPromptText } from './signInPromptCopy';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import { mintClientTaskId } from '../../services/prest0nSession/client';
import { deliveryKey, deliveryLedger } from '../../services/messageDelivery';
import { orderTsFromClientTaskId, userTurnAnchorId } from '../../types/taskAnchor';
import { writeOwnUserTurnNow } from '../../repositories/chatThreadMessageDocsRepository';
import { useTrialStatus } from '../../hooks/useTrialStatus';
import { TRIAL_ENDED_CHAT_MESSAGE } from '../../hooks/trialStatus';

/**
 * Epoch millis from a Firestore Timestamp that crossed an HTTP boundary —
 * the transport ships {_seconds,_nanoseconds} or {seconds,nanoseconds} maps,
 * admin SDK objects expose toMillis(), and some paths re-serialize to ISO
 * strings. Returns null when the shape doesn't parse (callers fall back).
 */
function firestoreishTimestampMs(value: unknown): number | null {
  if (!value) return null;
  const v = value as any;
  if (typeof v.toMillis === 'function') {
    const ms = v.toMillis();
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  }
  const seconds = typeof v._seconds === 'number' ? v._seconds
    : typeof v.seconds === 'number' ? v.seconds
      : null;
  if (seconds !== null) return seconds * 1000;
  const parsed = new Date(v as string).getTime();
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Send-ack window for the GUEST chat dispatch (adversarial review 2026-07-15,
 * Finding 3). The guest turn legitimately runs up to the server's 45s model
 * budget inside a 70s function timeout — the default 25s watchdog fired
 * mid-turn, releasing processing and offering a tappable Retry while the
 * original BILLED call was still in flight (double-spend window). 75s covers
 * the 70s server ceiling plus transport margin.
 */
export const GUEST_SEND_ACK_TIMEOUT_MS = 75_000;

/**
 * Returns true while a trial user's wallet/summary doc may not yet exist,
 * to prevent false-positive "balance too low" blocks. Trial credits are
 * tracked server-side via creditRemainingCents — not via OLV wallet.
 */
function useIsTrialUser(): { isTrial: boolean, isLoading: boolean } {
  const [state, setState] = useState({ isTrial: false, isLoading: true });
  useEffect(() => {
    const auth = getAuth();
    const unsub = auth.onAuthStateChanged(async (fbUser) => {
      if (!fbUser) { setState({ isTrial: false, isLoading: false }); return; }
      try {
        const db = getFirestore();
        const userSnap = await getDoc(doc(db, 'users', fbUser.uid));
        const trial = userSnap.data()?.trial;
        setState({ isTrial: Boolean(trial?.active), isLoading: false });
      } catch {
        setState({ isTrial: false, isLoading: false });
      }
    });
    return unsub;
  }, []);
  return state;
}

// ============================================================================
// GlobalTools Registration
// ============================================================================

/**
 * Register all global tools on module load.
 * This ensures tools are available before any hook usage.
 */
try {
  registerAllTools();
} catch (err) {
  console.warn('[GlobalChat] Failed to register global tools:', err);
}

/**
 * Feature flag for using the new GlobalTools system.
 * Set to true to enable the new system (gradual migration).
 * When false, uses the old agentic workflows handler.
 */
const USE_GLOBAL_TOOLS = true;

// ============================================================================
// Cross-Tab Streaming Coordination
// ============================================================================
//
// The lease itself lives in ./streamingFlag (v3, 2026-09-02): one per-tab
// key, heartbeat-renewed while THIS tab has a turn in flight, where "in
// flight" is DERIVED from the per-thread processing flags + queued sends in
// an effect below — never counted. That module owns the keys, the heartbeat
// that bridges deep-research stage waits, and the self-heal paths (stale
// sweep, pagehide release, silent-turn forfeit). Readers call
// `isAnyTabStreaming()` from the module directly.

// ============================================================================
// Just-sent draft-rehydrate suppression
// ============================================================================
//
// Same-mount composer-repop regression (Slack C0AM8U804KZ/1778481193.723179,
// Malaika 2026-05-14): after handleSend clears the global CHAT_DRAFT key and
// resets composer state, an *adjacent* component still mounted on the same
// page (LandingPage hero composer, or any other consumer of
// GLOBAL_CHAT_DRAFT_KEY) can re-write the just-sent text back to localStorage
// 300-500 ms later -- its own debounced-persist effect still holds the stale
// `prompt`/`promptHtml` it picked up via cross-tab sync seconds earlier. The
// global cross-tab poll in this file then reads the resurrected value and
// dispatches SET_PROMPT_HTML, repopulating the composer with the user's just-
// sent text.
//
// PR #149 (cross-mount) only addressed the PER-THREAD draft race in
// GlobalChatSystem.tsx -- the legacy GLOBAL draft lane is still unguarded.
// The fix is a short post-send suppression window during which:
//   * cross-tab poll / storage-event sync skips dispatching SET_PROMPT
//   * the on-mount hydrate-draft effect skips rehydration
// 1500 ms covers the worst-case adjacent-component 500 ms debounce + our own
// 1000 ms poll cadence + a small safety margin, without blocking a legitimate
// late cross-tab restore (which arrives well after this window).
//
// STRUCTURAL EXTENSION (2026-05-20, BUG-DR-COMPOSER-REPOP-LONG-TURN):
// The 1500 ms window is too small for Deep Research turns (5-8 min). The
// rehydrate gate is now sourced from `shouldSuppressDraftRehydrate` in
// draftSuppressionState.ts, which composes THREE windows:
//   (a) the historical 1500 ms post-send window (this constant), AS WELL AS
//   (b) the entire duration of any in-flight turn (markProcessing*), AS WELL AS
//   (c) a 1500 ms post-flight cooldown after the last turn ended.
// The bare `isWithinJustSentWindow` predicate below is preserved unchanged
// for the existing composerRepopSuppress.test.ts which exercises just (a).
const JUST_SENT_DRAFT_SUPPRESS_MS = 1500;

/**
 * Pure predicate -- returns true if `now` falls inside the post-send
 * suppression window opened at `lastSendAt`. Exported for unit tests
 * (composerRepopSuppress.test.ts). Production code should prefer
 * `shouldSuppressDraftRehydrate()` from draftSuppressionState.ts,
 * which composes this window with the new in-flight + post-flight
 * gates added for BUG-DR-COMPOSER-REPOP-LONG-TURN.
 */
export function isWithinJustSentWindow(
  lastSendAt: number,
  now: number,
  windowMs: number = JUST_SENT_DRAFT_SUPPRESS_MS,
): boolean {
  if (!lastSendAt) return false;
  const delta = now - lastSendAt;
  return delta >= 0 && delta < windowMs;
}

// ============================================================================
// Cross-Tab Scroll Authority
// ============================================================================

/**
 * Generate unique tab ID for scroll authority tracking.
 * Uses sessionStorage so ID persists across soft refresh but is unique per tab.
 */
const getOrCreateTabId = (): string => {
  if (typeof window === 'undefined') return 'ssr';
  const KEY = 'prest0_chat_tab_id';
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `tab_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(KEY, id);
  }
  return id;
};

/** Unique identifier for this tab - used in scroll authority tracking */
const TAB_ID = getOrCreateTabId();

// ============================================================================
// State Reducer
// ============================================================================

// Exported for unit tests — callers that import this constant get the
// canonical empty-state shape (including the new thread-isolation
// `processingByThread` map) without having to construct it by hand.
export const initialState: GlobalChatState = {
  isChatOpen: false,
  isChatDockedRight: false,
  isChatFullScreen: false,
  messages: [],
  prompt: '',
  promptHtml: '',
  attachments: [],
  isAttachmentPanelVisible: false,
  userCollapsedAttachments: false,
  lastAttachmentVisibleOnHide: false,
  isDragging: false,
  isMobileViewport: false,
  inputState: { status: 'idle' },
  isProcessing: false,
  processingStartedAt: null,
  processingByThread: {},
  processingStartedAtByThread: {},
  turnStartedAtByThread: {},
  pendingByThread: {},
  showClearConfirmModal: false,
};

// ============================================================================
// Attachment Serialization Helpers
// ============================================================================

const serializeAttachments = async (files: File[]): Promise<SerializedAttachment[]> => {
  const tasks = files.map(
    file =>
      new Promise<SerializedAttachment>((resolve) => {
        // A file too large to ever ride inline is NOT read (2026-09-09).
        // The dataUrl has exactly two consumers and this file can serve
        // neither: the turn body strips it above the inline cap, and a
        // per-thread snapshot of a multi-MB base64 string is past what
        // localStorage will hold. Reading it anyway is what killed the tab
        // on the 398 MB / 50-clip run — ~530 MB of strings materialised at
        // once, beside the File buffers, before a single upload finished.
        // First paint is unaffected: ChatAttachment renders the raw File via
        // URL.createObjectURL. The record below still exists, so the
        // metadata skeleton and the delivery guard both see the file; its
        // storage upload supplies the locator that actually carries it.
        if (!isInlineEligible(file.size)) {
          resolve({
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
          });
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
            dataUrl: typeof reader.result === 'string' ? reader.result : undefined,
          });
        };
        reader.onerror = () => {
          resolve({
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
          });
        };
        reader.readAsDataURL(file);
      })
  );

  return Promise.all(tasks);
};

const deserializeAttachments = async (serialized: SerializedAttachment[]): Promise<File[]> => {
  // Only deserialize attachments with dataUrl (localStorage-backed)
  // Storage-backed attachments (with storageUrl) should use attachmentsSerialized directly
  const deserializable = serialized.filter(att => !!att.dataUrl);

  if (deserializable.length === 0) {
    // No dataUrls found - these are Storage-backed attachments
    // Return empty array so AttachmentsSection uses attachmentsSerialized
    console.log('[useGlobalChat] Skipping File deserialization - using Storage-backed attachmentsSerialized');
    return [];
  }

  const tasks = deserializable.map(async (att) => {
    try {
      const res = await fetch(att.dataUrl!);
      const blob = await res.blob();
      return new File([blob], att.name || 'attachment', {
        type: att.type || blob.type,
        lastModified: att.lastModified || Date.now()
      });
    } catch (err) {
      console.error('[useGlobalChat] Failed to deserialize attachment:', att.name, err);
      return null;
    }
  });

  const results = await Promise.all(tasks);
  return results.filter((f): f is File => f !== null);
};

// ============================================================================
// R10 — INSTANT SEND FEEDBACK (2026-08-06)
//
// handleSend adds the user bubble and ALL visible feedback (composer clear,
// chat-open, chip spinner, thinking pill) synchronously; the FileReader
// serialization, the Firebase/GCS upload upgrade, and the ingest kickoff all
// run in the background. This record carries their promises to wherever the
// turn body is actually built — the direct dispatch in handleSend, or
// firePendingMessage for sends queued behind an in-flight turn — so BOTH
// paths perform the same bounded waits and queued sends keep their
// gcsPath-upgraded attachments + upload-time ingestJobId.
// ============================================================================
type SendAttachmentResolution = {
  /** Local FileReader → dataUrl serialization. Local-only. Never rejects. */
  serializedPromise: Promise<SerializedAttachment[] | undefined> | null;
  /** Firebase Storage / GCS signed-URL upgrade chain. Never rejects. */
  uploadPromise: Promise<SerializedAttachment[] | undefined> | null;
  /**
   * prest0nStartIngest kickoff. Assigned INSIDE the upload chain only after
   * every signed-URL upload has settled — read it AFTER awaiting
   * uploadPromise (if the upload wait timed out it is usually still null;
   * the backend then extracts inline by contract). Never rejects.
   */
  ingestJobIdPromise: Promise<string | undefined> | null;
  /**
   * SHARED-THREAD FULL-UPLOAD WAIT (2026-09-07 owner report: a photo sent to
   * another user in a shared thread vanished when coming back). Stamped at
   * send time from the host's `isThreadSharedRef` when the originating
   * thread is a group thread. The turn body of a group send is what the
   * server copies into the shared doc every OTHER participant reads — an
   * inline dataUrl there is stripped (1 MiB doc limit) and a locator-free
   * record can never be healed, so for a shared thread the turn build waits
   * for the FULL upload chain instead of the 8 s patience race. Solo threads
   * keep the race + inline fallback (only this browser reads that record).
   * Since PR-6 (2026-09-09) ALSO true while the thread's membership is not
   * known yet — the safe side — and false only for a RESOLVED solo thread.
   */
  awaitFullUpload?: boolean;
};

/**
 * Patience for the storage-upgrade chain at turn-build time. Past this cap
 * we dispatch with inline dataUrls when they fit under the backend's hard
 * cap (the backend tolerates inline payloads; the background upgrade still
 * patches gcsPath into state/storage later). Above the cap waiting stays
 * mandatory — see resolveSendAttachmentsForTurn.
 */
const UPLOAD_FOR_SEND_PATIENCE_MS = 8_000;
/**
 * Patience for the ingest kickoff POST: normally sub-second, but a cold
 * function start is ~6-10s and a hung one must not wedge the turn — past
 * the cap we proceed without a jobId and the backend extracts inline.
 */
const INGEST_JOB_ID_PATIENCE_MS = 12_000;

/**
 * True when the record set carries NO payload and NO locator — i.e. it is the
 * synchronous metadata skeleton written at send time (2026-08-20 attachment-
 * durability round). Every upgrade guard keys on THIS instead of bare
 * presence, so the skeleton guarantees a record exists without ever blocking
 * the FileReader/upload patches from landing their richer copies.
 */
const isMetadataOnlyAttachments = (
  list: SerializedAttachment[] | undefined,
): boolean =>
  !list?.length
  || list.every((a) => !a?.dataUrl && !a?.storageUrl && !a?.storagePath && !a?.gcsPath);

/**
 * The turn-build bounded waits (R10). Runs AFTER every piece of visible
 * feedback is on screen, immediately before the turn body is constructed.
 * Mutates `target` (the live userMessage reference — the same object held
 * in state/history) in place. Never throws.
 */
const resolveSendAttachmentsForTurn = async (
  resolution: SendAttachmentResolution,
  target: ChatMessage,
): Promise<void> => {
  try {
    // 1) Local serialization — the inline-dataUrl fallback for the turn
    //    body. Pure local FileReader work; no patience cap needed.
    if (resolution.serializedPromise) {
      const serialized = await resolution.serializedPromise;
      // Skeleton-aware (2026-08-20): the send-time metadata skeleton counts
      // as ABSENT here — without this, the turn body would ship name/type/
      // size records with no content and the model would see nothing.
      if (serialized?.length && isMetadataOnlyAttachments(target.attachmentsSerialized)) {
        target.attachmentsSerialized = serialized;
      }
    }

    // 2) Storage upgrade — bounded patience. If the wait times out, the
    //    inline-base64 send guard decides: at or under the inline hard cap
    //    the turn may ship dataUrls (backend tolerates them, and the
    //    background chain still patches gcsPath into state/storage later);
    //    ABOVE the cap the guard would hard-refuse an inline dispatch
    //    ("didn't finish uploading — re-attach"), so waiting for the upload
    //    remains mandatory — same behaviour as the pre-R10 code.
    if (resolution.uploadPromise && resolution.awaitFullUpload) {
      // Shared thread: the FULL upload, no patience race — the group send's
      // turn body must carry storageUrl/storagePath/gcsPath for every other
      // participant (see SendAttachmentResolution.awaitFullUpload). The
      // optimistic bubble, composer clear and chip are already on screen;
      // the chip shows 'uploading…' until the record gains its locator.
      const fullyUpgraded = await resolution.uploadPromise;
      if (fullyUpgraded?.length) {
        target.attachmentsSerialized = fullyUpgraded;
      }
    } else if (resolution.uploadPromise) {
      let uploadPatienceTimer: ReturnType<typeof setTimeout> | undefined;
      try {
        const upgraded = await Promise.race([
          resolution.uploadPromise,
          new Promise<undefined>((resolve) => {
            uploadPatienceTimer = setTimeout(() => resolve(undefined), UPLOAD_FOR_SEND_PATIENCE_MS);
          }),
        ]);
        if (upgraded?.length) {
          target.attachmentsSerialized = upgraded;
        } else {
          // The patience lapsed. Waiting the rest of the way out is
          // mandatory whenever the turn could not carry these files as they
          // stand — otherwise the send path throws away a message whose
          // upload was seconds from finishing.
          //
          // This used to ask only "is the inline payload too big?", which
          // was the right question while every file had an inline copy. It
          // stopped being the right question the moment large files stopped
          // being read into one (2026-09-09): a 398MB packet then has ZERO
          // inline characters, the test is false, and an 8-second timer
          // decides the fate of an upload that needs minutes. Malaika's
          // 50-clip retest hit exactly that and was refused.
          //
          // So the test is now "can this turn actually deliver them?" —
          // the same judgement the send guard applies a moment later, asked
          // before giving up rather than after.
          const inlineChars = sumInlineDataUrlChars(target.attachmentsSerialized);
          const attachedNames = (target.attachments || []).map((f) => f?.name || '');
          const notDeliverable =
            judgeAttachmentDelivery(attachedNames, target.attachmentsSerialized).kind !== 'ok';
          if (inlineChars > MAX_INLINE_DATAURL_TURN_CHARS || notDeliverable) {
            const fullyUpgraded = await resolution.uploadPromise;
            if (fullyUpgraded?.length) {
              target.attachmentsSerialized = fullyUpgraded;
            }
          }
        }
      } finally {
        if (uploadPatienceTimer) clearTimeout(uploadPatienceTimer);
      }
    }

    // 3) Ingest jobId — read AFTER the upload wait above (it is assigned
    //    inside the upload chain once all signed-URL uploads settle). The
    //    jobId is consumed only by the turn body, never by any UI, so this
    //    patience sits safely behind the already-rendered feedback.
    const ingestJobIdPromise = resolution.ingestJobIdPromise;
    if (ingestJobIdPromise) {
      let ingestPatienceTimer: ReturnType<typeof setTimeout> | undefined;
      try {
        const resolvedIngestJobId = await Promise.race([
          ingestJobIdPromise,
          new Promise<undefined>((resolve) => {
            ingestPatienceTimer = setTimeout(() => resolve(undefined), INGEST_JOB_ID_PATIENCE_MS);
          }),
        ]);
        if (resolvedIngestJobId) {
          target.ingestJobId = resolvedIngestJobId;
        }
      } finally {
        if (ingestPatienceTimer) clearTimeout(ingestPatienceTimer);
      }
    }
  } catch {
    // Every promise above never rejects by construction; belt-and-suspenders
    // only — the turn must still dispatch (backend falls back to inline
    // extraction when the payload is degraded).
  }
};

// ============================================================================
// Token Usage Snapshot Helpers
// ============================================================================

const toNumber = (value: unknown): number | undefined => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const buildTokenUsageSnapshot = (message: ChatMessage): TokenUsageSnapshot | undefined => {
  const src = message.tokenUsage || message.tokenUsageSnapshot;
  if (!src) return undefined;

  const snapshot: TokenUsageSnapshot = {
    provider: src.provider,
    model: src.model,
    summary: src.summary,
    inputTokens: toNumber(src.inputTokens),
    outputTokens: toNumber(src.outputTokens),
    reasoningTokens: toNumber(src.reasoningTokens),
    citationTokens: toNumber((src as any)?.citationTokens),
    searchQueries: toNumber((src as any)?.searchQueries),
    totalTokens: toNumber(src.totalTokens),
    responseTimeMs: toNumber(src.responseTimeMs),
    // Persist the authoritative upstream cost — without it, reloaded history
    // recomputes from token counts alone and shows $0.00 (or a lower figure)
    // for turns the wallet actually debited at the upstream-cost rate.
    upstreamCostUsd: toNumber((src as any)?.upstreamCostUsd),
    timestampIso:
      src.timestamp instanceof Date
        ? src.timestamp.toISOString()
        : typeof (src as any)?.timestampIso === 'string'
        ? (src as any).timestampIso
        : typeof src.timestamp === 'string'
        ? src.timestamp
        : undefined,
  };

  const cost = calculateCost(snapshot as any);
  if (cost) {
    snapshot.cost = {
      inputCost: cost.inputCost,
      outputCost: cost.outputCost,
      reasoningCost: cost.reasoningCost,
      citationCost: cost.citationCost,
      searchCost: cost.searchCost,
      totalCost: cost.totalCost,
      model: cost.model,
      provider: cost.provider,
    };
  }

  const hasData =
    snapshot.inputTokens !== undefined ||
    snapshot.outputTokens !== undefined ||
    snapshot.reasoningTokens !== undefined ||
    snapshot.citationTokens !== undefined ||
    snapshot.searchQueries !== undefined ||
    snapshot.totalTokens !== undefined ||
    snapshot.responseTimeMs !== undefined ||
    snapshot.timestampIso !== undefined ||
    (snapshot.cost &&
      Object.values(snapshot.cost).some((v) => v !== undefined));

  return hasData ? snapshot : undefined;
};

const parseTokenUsageSnapshot = (raw: unknown): TokenUsageSnapshot | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const obj = raw as Record<string, unknown>;

  const costRaw = obj.cost;
  const cost =
    costRaw && typeof costRaw === 'object'
      ? {
          inputCost: toNumber((costRaw as any).inputCost),
          outputCost: toNumber((costRaw as any).outputCost),
          reasoningCost: toNumber((costRaw as any).reasoningCost),
          citationCost: toNumber((costRaw as any).citationCost),
          searchCost: toNumber((costRaw as any).searchCost),
          totalCost: toNumber((costRaw as any).totalCost),
          model: typeof (costRaw as any).model === 'string' ? (costRaw as any).model : undefined,
          provider: typeof (costRaw as any).provider === 'string' ? (costRaw as any).provider : undefined,
        }
      : undefined;

  const snapshot: TokenUsageSnapshot = {
    provider: typeof obj.provider === 'string' ? (obj.provider as any) : undefined,
    model: typeof obj.model === 'string' ? (obj.model as any) : undefined,
    summary: typeof obj.summary === 'string' ? obj.summary : undefined,
    inputTokens: toNumber(obj.inputTokens),
    outputTokens: toNumber(obj.outputTokens),
    reasoningTokens: toNumber(obj.reasoningTokens),
    citationTokens: toNumber((obj as any).citationTokens),
    searchQueries: toNumber((obj as any).searchQueries),
    totalTokens: toNumber(obj.totalTokens),
    responseTimeMs: toNumber(obj.responseTimeMs),
    upstreamCostUsd: toNumber((obj as any).upstreamCostUsd),
    timestampIso:
      typeof (obj as any).timestampIso === 'string'
        ? (obj as any).timestampIso
        : typeof obj.timestamp === 'string'
        ? (obj.timestamp as string)
        : undefined,
    cost,
  };

  const hasData =
    snapshot.inputTokens !== undefined ||
    snapshot.outputTokens !== undefined ||
    snapshot.reasoningTokens !== undefined ||
    snapshot.citationTokens !== undefined ||
    snapshot.searchQueries !== undefined ||
    snapshot.totalTokens !== undefined ||
    snapshot.responseTimeMs !== undefined ||
    snapshot.timestampIso !== undefined ||
    (snapshot.cost &&
      Object.values(snapshot.cost).some((v) => v !== undefined));

  return hasData ? snapshot : undefined;
};

/**
 * Build array of token usage snapshots from message (for multi-model responses)
 */
const buildTokenUsageSnapshots = (message: ChatMessage): TokenUsageSnapshot[] | undefined => {
  // If we have tokenUsages array, convert each to snapshot
  if (message.tokenUsages && message.tokenUsages.length > 0) {
    const snapshots = message.tokenUsages.map((src) => {
      const snapshot: TokenUsageSnapshot = {
        provider: src.provider,
        model: src.model,
        summary: src.summary,
        inputTokens: toNumber(src.inputTokens),
        outputTokens: toNumber(src.outputTokens),
        reasoningTokens: toNumber(src.reasoningTokens),
        citationTokens: toNumber((src as any)?.citationTokens),
        searchQueries: toNumber((src as any)?.searchQueries),
        totalTokens: toNumber(src.totalTokens),
        responseTimeMs: toNumber(src.responseTimeMs),
        upstreamCostUsd: toNumber((src as any)?.upstreamCostUsd),
        timestampIso:
          src.timestamp instanceof Date
            ? src.timestamp.toISOString()
            : typeof (src as any)?.timestampIso === 'string'
            ? (src as any).timestampIso
            : typeof src.timestamp === 'string'
            ? src.timestamp
            : undefined,
      };

      const cost = calculateCost(snapshot as any);
      if (cost) {
        snapshot.cost = {
          inputCost: cost.inputCost,
          outputCost: cost.outputCost,
          reasoningCost: cost.reasoningCost,
          citationCost: cost.citationCost,
          searchCost: cost.searchCost,
          totalCost: cost.totalCost,
          model: cost.model,
          provider: cost.provider,
        };
      }

      return snapshot;
    });
    return snapshots.length > 0 ? snapshots : undefined;
  }

  // If we have tokenUsageSnapshots array already, return it
  if (message.tokenUsageSnapshots && message.tokenUsageSnapshots.length > 0) {
    return message.tokenUsageSnapshots;
  }

  return undefined;
};

/**
 * Parse array of token usage snapshots from persisted data
 */
const parseTokenUsageSnapshots = (raw: unknown): TokenUsageSnapshot[] | undefined => {
  if (!Array.isArray(raw)) return undefined;

  const snapshots = raw
    .map((item) => parseTokenUsageSnapshot(item))
    .filter((s): s is TokenUsageSnapshot => s !== undefined);

  return snapshots.length > 0 ? snapshots : undefined;
};

// Exported for unit-test coverage of thread-isolation invariants. The
// hook itself is tested end-to-end via the app, but the per-thread
// processing reducer logic is critical (a regression here re-introduces
// the cross-thread spinner-smear bug Miles caught on the live demo)
// and deserves dedicated unit coverage.
export function chatReducer(state: GlobalChatState, action: GlobalChatAction): GlobalChatState {
  switch (action.type) {
    case 'SET_CHAT_OPEN':
    case 'SET_CHAT_OPEN_PRIORITY':
      // MOBILE OPENS FULLSCREEN (mobile UX round 2026-08-14): on a phone the
      // open chat is a fullscreen takeover — the floating panel over page
      // content is a desktop layout. Same reducer-boundary doctrine as the
      // MOBILE-NEVER-DOCKS guard below: every dispatch site (pill tap, send,
      // file drop, external globalchat:open, UI-state sync, mount restore —
      // present and future) gets the rule for free. Viewport override only:
      // the fullscreen persistence effect skips writes while mobile, so the
      // user's desktop mode preference is never overwritten by this.
      return {
        ...state,
        isChatOpen: action.payload,
        ...(action.payload && state.isMobileViewport
          ? { isChatFullScreen: true, isChatDockedRight: false }
          : {}),
      };
    case 'SET_CHAT_DOCKED_RIGHT':
      // MOBILE NEVER DOCKS (owner directive 2026-08-12: "when clearly in
      // mobile mode the right side chat view should be removed as an
      // option"). A narrow right rail on a phone is a squeezed column with a
      // truncated header — the mode belongs to desktop only.
      //
      // The guard lives at the REDUCER boundary, not at each call site, for
      // the same reason SET_MESSAGES guards here: the viewport-change effect
      // below only undocked on a media-query TRANSITION, so tapping the
      // mode-cycle button while already on a phone walked straight into the
      // docked rail and nothing undid it. No dispatch site — existing or
      // future — can bypass this.
      //
      // Docking is a VIEWPORT override, never a preference write (the
      // persistence effect skips while mobile), so the desktop dock choice
      // survives untouched and restores on the way back.
      return {
        ...state,
        isChatDockedRight: state.isMobileViewport ? false : action.payload,
      };
    case 'SET_CHAT_FULLSCREEN':
      // While the chat is OPEN on mobile, fullscreen cannot be turned off —
      // the only non-fullscreen mobile state is closed (the pill). Without
      // this guard the landing page's UI-state sync and the desktop
      // mode-cycle handler could push fullscreen:false onto an open mobile
      // chat and recreate the floating-panel-on-a-phone layout.
      return {
        ...state,
        isChatFullScreen: (state.isMobileViewport && state.isChatOpen)
          ? true
          : action.payload,
      };
    case 'SET_MESSAGES': {
      // BUG-FIX (mid-stream refresh QA regression, 2026-07): defensive guards
      // at the reducer boundary so NO dispatch site (existing or future) can
      // ever land duplicate-by-id turns OR keep a stale interrupted card when
      // a later completed answer for the same turn already exists.
      //
      // Symptom 2 hardening (report appears then vanishes): a snapshot
      // replacement must also be unable to DOWNGRADE an already-finalized
      // bubble to its earlier pending/placeholder copy for the same id.
      // The downgrade guard was previously wired into only one of the five
      // SET_MESSAGES dispatch sites (the BroadcastChannel re-read in
      // useChatThreads); the reconcile poll, hydrate-on-load, switchThread,
      // and legacy force-sync paths remained exposed. Applying it here at
      // the reducer boundary covers every site, present and future. It is
      // id-keyed and conservative: a snapshot with disjoint ids (thread
      // switch) or an empty payload (clear/reset) passes through untouched.
      // CARD RE-RENDER FROM PAYLOAD (P0 thread-durability, 2026-09-02): a
      // connect card whose html was blanked by the whole-array doc's card-html
      // backstop (cardHtmlDroppedForFirestore), dropped by the quota
      // last-resort, or filed as a data-only record is re-projected from its
      // persisted payload HERE — the one boundary every hydrate path crosses
      // (server page, IDB mirror, thread switch, load-earlier prepend,
      // cross-tab sync, both CHAT_HISTORY restores) — before it can reach the
      // render skip for contentless assistant messages. Pure, deterministic,
      // same reference when nothing changed, so the identity bail below holds.
      const replaced = rebuildDroppedCardHtml(normalizeMessageList(
        mergeSyncedMessagesPreservingFinalized(action.payload, state.messages),
      ));
      // PROD-185 identity bail (2026-08-19): a converged re-dispatch — same
      // messages, no observable change — must not mint a new array identity.
      // Identity churn from no-op SET_MESSAGES was the fuel for the nested
      // update cascade that crashed the surface with React #185 (sourcemap-
      // decoded: the transcript scroll controller's items effect re-ran per
      // churn and its edge-state setters crossed React's ceiling).
      if (messageListsEffectivelyIdentical(state.messages, replaced)) {
        return state;
      }
      return {
        ...state,
        messages: replaced,
      };
    }
    case 'ADD_MESSAGE':
      // BUG-FIX round 2 (2026-07-02): the refresh-resume answer can be appended
      // here while a hydrated interrupted card for the SAME turn still sits in
      // state — normalize so the stale card is dropped and no double-bubble
      // survives.
      return { ...state, messages: normalizeMessageList([...state.messages, action.payload]) };
    case 'ADD_MESSAGES':
      return { ...state, messages: normalizeMessageList([...state.messages, ...action.payload]) };
    case 'UPDATE_MESSAGE': {
      const { index, message: updates } = action.payload;
      if (index < 0 || index >= state.messages.length) {
        // Log race condition for diagnostics
        console.warn('[GlobalChat] UPDATE_MESSAGE race condition - index out of bounds:', {
          index,
          messagesLength: state.messages.length,
          sourceTag: (updates as { sourceTag?: string })?.sourceTag,
        });
        return state;
      }
      const updatedMessages = [...state.messages];
      updatedMessages[index] = { ...updatedMessages[index], ...updates };
      // BUG-FIX round 2 (2026-07-02): normalize index-based finalize too, so a
      // sibling interrupted card / duplicate id for the same turn is reconciled.
      return { ...state, messages: normalizeMessageList(updatedMessages) };
    }
    case 'UPDATE_MESSAGE_BY_ID': {
      const { id, message: updates, appendIfMissing } = action.payload;
      const idx = state.messages.findIndex(m => m.id === id);
      if (idx === -1) {
        // INCIDENT-20260818: a resumed turn whose placeholder lives in thread
        // storage but not yet in state used to no-op here — the whole turn
        // ran with no visible widget and the answer "just appeared".
        // appendIfMissing (set only for the ACTIVE thread by callers)
        // materializes the update as a message instead; normalizeMessageList
        // collapses it with the storage copy on the next hydrate.
        if (appendIfMissing) {
          const appended = {
            text: '',
            isUser: false,
            timestamp: new Date(),
            ...updates,
            id,
          } as ChatMessage;
          return { ...state, messages: normalizeMessageList([...state.messages, appended]) };
        }
        console.warn('[GlobalChat] UPDATE_MESSAGE_BY_ID: Message not found:', {
          id,
          messagesLength: state.messages.length,
          sourceTag: (updates as { sourceTag?: string })?.sourceTag,
        });
        return state;
      }
      const updatedMessages = [...state.messages];
      updatedMessages[idx] = { ...updatedMessages[idx], ...updates };
      // BUG-FIX round 2 (2026-07-02): this is the path the refresh-resume
      // finalize uses (finalizeStreamingMessageById). Once the answer flips to
      // status:'complete'/interrupted:false here, normalize so any sibling
      // interrupted card for the SAME turn is cleared and duplicate ids collapse.
      return { ...state, messages: normalizeMessageList(updatedMessages) };
    }
    case 'REMOVE_MESSAGE': {
      const { index } = action.payload;
      if (index < 0 || index >= state.messages.length) return state;
      const filteredMessages = state.messages.filter((_, i) => i !== index);
      return { ...state, messages: filteredMessages };
    }
    case 'REMOVE_MESSAGE_BY_ID': {
      const { id } = action.payload;
      const filtered = state.messages.filter(m => m.id !== id);
      if (filtered.length === state.messages.length) {
        console.warn('[GlobalChat] REMOVE_MESSAGE_BY_ID: Message not found:', { id });
        return state;
      }
      return { ...state, messages: filtered };
    }
    case 'SET_PROMPT':
      return { ...state, prompt: action.payload };
    case 'SET_PROMPT_HTML':
      return { ...state, promptHtml: action.payload };
    case 'SET_ATTACHMENTS':
      return { ...state, attachments: action.payload };
    case 'ADD_ATTACHMENTS':
      return { ...state, attachments: [...state.attachments, ...action.payload] };
    case 'REMOVE_ATTACHMENT':
      return {
        ...state,
        attachments: state.attachments.filter((_, i) => i !== action.payload),
      };
    case 'CLEAR_ATTACHMENTS':
      return { ...state, attachments: [], isAttachmentPanelVisible: false };
    case 'SET_ATTACHMENT_PANEL_VISIBLE':
      return { ...state, isAttachmentPanelVisible: action.payload };
    case 'SET_USER_COLLAPSED_ATTACHMENTS':
      return { ...state, userCollapsedAttachments: action.payload };
    case 'SET_LAST_ATTACHMENT_VISIBLE_ON_HIDE':
      return { ...state, lastAttachmentVisibleOnHide: action.payload };
    case 'SET_DRAGGING':
      return { ...state, isDragging: action.payload };
    case 'SET_MOBILE_VIEWPORT':
      // Crossing INTO mobile with the chat open upgrades it to the
      // fullscreen takeover (and clears any dock) right here, so both the
      // shrink-while-open case AND restore-on-reload are covered: the mount
      // restore effect dispatches its open/mode flags before the viewport
      // effect's first SET_MOBILE_VIEWPORT(true), which then lands on this
      // case and retro-forces fullscreen.
      return {
        ...state,
        isMobileViewport: action.payload,
        ...(action.payload && state.isChatOpen
          ? { isChatFullScreen: true, isChatDockedRight: false }
          : {}),
      };
    case 'RESET_PROMPT':
      return { ...state, prompt: '', promptHtml: '' };
    case 'CLEAR_CHAT':
      return { ...state, messages: [] };
    case 'SET_CLEAR_CONFIRM_MODAL':
      return { ...state, showClearConfirmModal: action.payload };
    case 'SET_INPUT_STATE':
      return {
        ...state,
        inputState: action.payload,
      };
    case 'SET_PROCESSING': {
      // Thread-keyed processing state. See `threadIsolation.ts` for the
      // pure-function implementation; the wrapper below adds the
      // optional DEBUG_THREAD_ISOLATION trace and merges the result
      // back into the GlobalChatState shape.
      const { isProcessing, timestamp, threadId, turnEpochMs } = action.payload;
      const updated = applyProcessingState(
        {
          processingByThread: state.processingByThread,
          processingStartedAtByThread: state.processingStartedAtByThread,
          turnStartedAtByThread: state.turnStartedAtByThread,
          isProcessing: state.isProcessing,
          processingStartedAt: state.processingStartedAt,
        },
        { isProcessing, threadId, timestamp, turnEpochMs },
      );
      if (typeof window !== 'undefined' && threadId) {
        try {
          const dbg = (typeof localStorage !== 'undefined') &&
            localStorage.getItem('DEBUG_THREAD_ISOLATION');
          if (dbg && dbg !== '0' && dbg !== 'false') {
            // eslint-disable-next-line no-console
            console.debug(
              `${THREAD_ISO_DEBUG_TAG} action=${isProcessing ? 'startProcessing' : 'stopProcessing'} threadId=${threadId} anyProcessing=${updated.isProcessing}`
            );
          }
        } catch { /* ignore */ }
      }
      return {
        ...state,
        processingByThread: updated.processingByThread,
        processingStartedAtByThread: updated.processingStartedAtByThread,
        turnStartedAtByThread: updated.turnStartedAtByThread,
        isProcessing: updated.isProcessing,
        processingStartedAt: updated.processingStartedAt,
      };
    }
    case 'ENQUEUE_PENDING': {
      // Same-thread queueing while streaming.
      // Miles directive (2026-05-09 23:44 UTC): "yes it should be allowed".
      // The user bubble for this message has ALREADY been appended to
      // state.messages by the addMessage() call in handleSend; this
      // action only mirrors a PendingMessage onto the per-thread queue
      // so the orchestrator can pick it up after the in-flight turn.
      const next = enqueueMessage(
        { queues: state.pendingByThread || {} },
        action.payload,
      );
      if (typeof window !== 'undefined') {
        try {
          const dbg = (typeof localStorage !== 'undefined') &&
            localStorage.getItem('DEBUG_THREAD_ISOLATION');
          if (dbg && dbg !== '0' && dbg !== 'false') {
            // eslint-disable-next-line no-console
            console.debug(
              `${THREAD_QUEUE_DEBUG_TAG} action=enqueue threadId=${action.payload.threadId} depth=${(next.queues[action.payload.threadId] || []).length}`
            );
          }
        } catch { /* ignore */ }
      }
      return { ...state, pendingByThread: next.queues };
    }
    case 'DEQUEUE_PENDING_HEAD': {
      const { threadId } = action.payload;
      const { state: nextQ } = dequeueHead(
        { queues: state.pendingByThread || {} },
        threadId,
      );
      return { ...state, pendingByThread: nextQ.queues };
    }
    case 'FLUSH_PENDING': {
      const { threadId } = action.payload;
      const { state: nextQ } = flushQueue(
        { queues: state.pendingByThread || {} },
        threadId,
      );
      return { ...state, pendingByThread: nextQ.queues };
    }
    case 'REMOVE_PENDING_FOR_THREAD': {
      const { threadId } = action.payload;
      const next = removeThreadQueue(
        { queues: state.pendingByThread || {} },
        threadId,
      );
      return { ...state, pendingByThread: next.queues };
    }
    default:
      return state;
  }
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Options for sendProgrammaticMessage (Round J send-path parity, 2026-07-14).
 * - hidden: no user bubble rendered (clarifying-skip / onboarding sends);
 *   failures are still surfaced visibly.
 * - skipUserBubble: retry path — the original user bubble already lives in
 *   the thread, so the resend must not duplicate it.
 */
export interface ProgrammaticSendOptions {
  hidden?: boolean;
  skipUserBubble?: boolean;
}

/** Internal-only extension: marks the post-auth-settle re-dispatch so the
 *  deferral can never loop. Not part of the public surface. */
interface InternalProgrammaticSendOptions extends ProgrammaticSendOptions {
  authRetry?: boolean;
  /**
   * Finding 2 (2026-07-15): pin the send to a specific thread instead of
   * resolving the ACTIVE thread at dispatch time. Used by the auth-deferral
   * re-dispatch so a send deferred on thread A cannot land on thread B just
   * because the user switched threads while auth settled (the user bubble
   * and the armed watchdog both live on A). `null` is a valid pin
   * (single-thread surfaces) — only `undefined` falls back to the active
   * thread.
   */
  forceThreadId?: string | null;
}

export interface UseGlobalChatResult {
  state: GlobalChatState;
  dispatch: React.Dispatch<GlobalChatAction>;
  config: Required<GlobalChatConfig>;
  handlers: GlobalChatHandlers;
  refs: {
    promptInputRef: React.RefObject<HTMLDivElement>;
    chatMessagesRef: React.RefObject<HTMLDivElement>;
    /**
     * Inner wrapper around all transcript content (styles.TranscriptContent).
     * The host must attach it so the scroll controller's ResizeObserver sees
     * content growth that changes no message identity (follow-up chips
     * resolving, image loads) and can keep a pinned viewport stuck to the
     * bottom.
     */
    transcriptContentRef: React.RefObject<HTMLDivElement>;
    fileInputRef: React.RefObject<HTMLInputElement>;
    promptBarRef: React.RefObject<HTMLDivElement>;
    floatingPromptBarRef: React.RefObject<HTMLDivElement>;
    /**
     * Host-installed send route (2026-08-07, INCIDENT: the summon gate never
     * ran on keyboard sends). The send BUTTONS call the host's
     * handleSendWithDraftHydration wrapper — draft sentinels, the @-mention
     * summon, the W3b summon gate — but Enter called handleSend directly, so
     * every keyboard send bypassed the gate and "Prest0n replies only when
     * mentioned" kept replying. The host installs its wrapper here; Enter then
     * takes the SAME path as the buttons. Null ⇒ direct handleSend (headless
     * and non-thread surfaces are unchanged).
     */
    sendRouteOverrideRef: React.MutableRefObject<(() => void | Promise<void>) | null>;
    chatContainerRef: React.RefObject<HTMLDivElement>;
    onProcessingBlockedRef: React.MutableRefObject<((source: 'main' | 'floating') => void) | null>;
    /**
     * Thread-isolation bridge refs. GlobalChatSystem fills these after
     * `useChatThreads()` mounts so the streaming pipeline can route
     * updates to the ORIGINATING thread (captured at send-time) rather
     * than blindly to whatever thread is currently active. See the
     * thread-isolation HARD RULE in PR
     * fix/thread-isolation-per-thread-state.
     */
    getActiveThreadIdRef: React.MutableRefObject<(() => string | null) | null>;
    writeThreadMessagesRef: React.MutableRefObject<((threadId: string, messages: ChatMessage[]) => void) | null>;
    readThreadMessagesRef: React.MutableRefObject<((threadId: string) => ChatMessage[]) | null>;
    /**
     * Shared-thread predicate (2026-09-07 photo-vanish fix). The host fills it
     * from the membership snapshot + the server-owned `sharedThread` marker;
     * the send path reads it to wait for the FULL attachment upload on group
     * sends. Null ⇒ solo behaviour everywhere.
     */
    isThreadSharedRef: React.MutableRefObject<((threadId: string) => boolean | null) | null>;
    /**
     * Personalized-greeting composer (2026-07-13). GlobalChatSystem fills
     * this with the same pure composer it hands useChatThreads, so the
     * once-per-mount welcome injector below can personalize the bootstrap
     * thread's welcome. Null / composer returning null → the static
     * `config.welcomeMessage` copy, exactly as before. The optional
     * `tokenUsage` carries the suggestion-distillation usage for the welcome
     * bubble's mandatory ⓘ (2026-07-13).
     */
    composeGreetingRef: React.MutableRefObject<(() => { text: string; html: string; tokenUsage?: TokenUsageSnapshot } | null) | null>;
    /**
     * SYNCHRONOUS mirror of `state.messages` (PR #97 — Bug #1 fix).
     * Updated eagerly inside the dispatch wrapper for every
     * message-mutation action, so consumers (e.g.
     * `useChatThreads.snapshotActive`) can read the post-dispatch
     * messages array within the same tick without waiting for the
     * React commit + useEffect chain. Use this instead of relying on
     * the `messages` prop when correctness depends on observing a
     * dispatch that just happened in the same event handler.
     */
    messagesSyncRef: React.MutableRefObject<ChatMessage[]>;
  };
  waitlistController: ConstructionWaitlistController;
  /** Whether to show the "jump to bottom" button (user scrolled up) */
  showScrollToBottom: boolean;
  /**
   * Synchronous read of the controller's live pinned state. The persisted
   * view-marker writer in GlobalChatSystem keys clear-vs-save on this
   * (pinned-to-bottom clears — bottom stays the default).
   */
  isPinnedNow: () => boolean;
  /** Whether user is scrolled near the top (for showing "previous X" button) */
  isNearTop: boolean;
  /** Number of new messages that arrived while user was scrolled up */
  newMessageCount: number;
  /** Whether there's unseen activity (streaming updates while scrolled up) */
  hasUnseenActivity: boolean;
  /** Scroll the chat messages container to the bottom */
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  /**
   * Re-pin the transcript to the bottom (alias of scrollToBottom). Send
   * paths call this so the user's own message always lands in view.
   */
  pinToBottom: (behavior?: ScrollBehavior) => void;
  /** Scroll to top with fast, momentum-based animation (like quickly scrolling up) */
  scrollToTopAnimated: (onComplete?: () => void) => void;
  /** Strict edge: viewport is parked at the absolute top of the transcript */
  isAtThreadTop: boolean;
  /** Strict edge: viewport is parked at the absolute bottom of the transcript */
  isAtThreadBottom: boolean;
  /**
   * Where the last ↑/↓ message step landed (1-based), or null before any
   * stepping — feeds the "12 / 40" orientation readout.
   */
  stepPosition: TranscriptStepPosition | null;
  /** Step the viewport up to the previous message bubble (nav bubbles) */
  stepToPreviousMessage: () => void;
  /** Step the viewport down to the next message bubble (nav bubbles) */
  stepToNextMessage: () => void;
  /** Agentic workflows state (deep research, formatting, translation) */
  agenticWorkflows: AgenticWorkflowsHook;
  /** GlobalTools state (new unified tool system) */
  globalTools: UseGlobalToolsReturn;
  /** Check if system is currently processing (disables send button) */
  isSystemProcessing: () => boolean;
  /** Start processing state (call before async operations) */
  startProcessing: (threadId?: string | null) => void;
  /** Stop processing state (call after async operations complete) */
  stopProcessing: (threadId?: string | null) => void;
  /**
   * Cancel the active thread's in-flight turn AND flush its pending
   * queue. Use from the chat "stop" UI so queued messages are not
   * silently fired after the user clicks stop.
   * (PR feat/same-thread-queue-while-streaming.)
   */
  cancelActiveThread: () => void;
  /**
   * Finding 8c (2026-07-15): disarm a thread's send-ack watchdog (and drop
   * its late-ack hold). MUST be called from the thread archive/delete paths
   * so a pending fire can't append a retry bubble into — or hold the queue
   * of — a thread that no longer exists.
   */
  cancelThreadSendWatchdog: (threadId: string | null) => void;
  /** Owner directive 2026-08-22 ("still working"): thread-open poke for
   *  active-task discovery — lights chips for server-started work in the
   *  newly opened thread within one round-trip. */
  pokeActiveTaskDiscovery: () => Promise<void>;
  /** Low balance warning state (balance below $3 USD threshold) */
  lowBalanceState: LowBalanceState;
  /** Expired-trial chat gate — true when a (subscription-gated) trial has ended */
  trialExpiredBlock: boolean;
  /** Programmatic message send (for onboarding welcome, etc.) */
  sendProgrammaticMessage: (text: string, options?: ProgrammaticSendOptions) => Promise<void>;
  /**
   * Clear the composer exactly the way handleSend does — prompt state, the
   * ATTACHMENT STRIP, the contenteditable DOM node, the pending draft-save
   * timer, and the global draft key — WITHOUT dispatching a turn. For send
   * paths that deliver the message themselves and return before handleSend
   * ever runs (the W3b suppressed-delivery branch). RESET_PROMPT alone is not
   * enough there: the composer is an uncontrolled contenteditable, so the
   * just-sent text survives in the DOM and the next input event re-saves it
   * as a draft (owner repro 2026-08-08: "hi" delivered to the group, yet
   * still sitting in the composer and the sidebar draft, send after send).
   * And the attachments are their own state: without CLEAR_ATTACHMENTS the
   * just-sent picture stayed in the strip — and a second send delivered it
   * again (owner repro 2026-09-09: "the image stays in the message bar draft
   * box, even though the message with the image already sent out").
   */
  clearComposerForSuppressedDelivery: () => void;
  /** True once the Prest0n VM session is fully initialized (for onboarding welcome timing) */
  isPrest0nSessionReady: boolean;
  /** Canonical resolved account / workspace id (null until the migration gate passes). */
  prest0nSessionAccountId: string | null;
  /**
   * ORG STICKINESS (2026-08-18): true when a SIGNED-IN user's workspace
   * identity is unresolved (gate error + no last-confirmed cache). The
   * surface must HOLD: no sends, no thread creation, no identity guessing.
   * A backoff retry (+ the window 'online' event) re-runs the gate.
   */
  workspaceUnresolved: boolean;
  /**
   * Cold-boot fail-closed (2026-08-21): true while the migration gate's
   * verdict for this auth identity is still in flight. Composed by the host
   * with the confirmed-workspace seed to hold thread subscriptions instead
   * of rendering the unfiltered cross-workspace union on a fresh profile.
   */
  isMigrationGateChecking: boolean;
}

export function useGlobalChat(userConfig?: Partial<GlobalChatConfig>): UseGlobalChatResult {
  // Merge user config with defaults
  const config: Required<GlobalChatConfig> = {
    ...DEFAULT_CHAT_CONFIG,
    ...userConfig,
  };

  // Agentic workflows (email-gated: deep research, formatting, translation)
  const agenticWorkflows = useAgenticWorkflows();

  // GlobalTools (new unified tool system)
  const globalTools = useGlobalTools();

  // ============================================================================
  // Prest0n Session (Phase 1 thin-client path for migrated accounts)
  // ============================================================================
  //
  // We eagerly resolve the account ID and call usePrest0nSession unconditionally
  // (React hooks cannot be called conditionally). The hook internally only creates
  // a session when accountId is non-null and the migration gate has passed.
  //
  // We pass null here initially; the useEffect below gates on migration status
  // and calls globalTools.setPrest0nSession once we know if this account is migrated.
  // ============================================================================

  // ── Reactive auth UID ────────────────────────────────────────────────────
  // resolveAccountIdFromAuth() is synchronous and returns null if Firebase Auth
  // hasn't resolved yet. Using it as a plain `const` means the component never
  // re-renders when auth state changes, so the migration gate and session init
  // never fire for users who weren't signed in at component mount time.
  // Fix: use reactive state subscribed to onAuthStateChanged.
  const [chatAccountId, setChatAccountId] = useState<string | null>(
    () => resolveAccountIdFromAuth() // seed with sync value if already available
  );

  // Point 6 auth-init race fix: a definitive "Firebase Auth has resolved at
  // least once" signal. On a hard refresh auth has NOT rehydrated yet, so
  // `chatAccountId` is transiently null — INDISTINGUISHABLE from a genuine
  // guest. The refresh-survival recovery effects must WAIT for this flag
  // before deciding guest-vs-authed, otherwise the guest path prematurely
  // finalizes an authenticated in-flight task as "session expired".
  // Seed true ONLY if a NON-empty uid was already available synchronously at
  // mount (auth was live). A null seed is ambiguous — leave it false until
  // onAuthStateChanged actually fires.
  const [authHasResolved, setAuthHasResolved] = useState<boolean>(
    () => Boolean(resolveAccountIdFromAuth())
  );

  // On mount: clear STALE leftover leases (tabs that died without a pagehide)
  // and the stale legacy shared key. Stale-only, never unconditional — a
  // FRESH key is another live tab's lease, and wiping it dropped that tab's
  // sync guards mid-answer.
  useEffect(() => {
    sweepStaleStreamingFlag();
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const uid = user?.uid ?? null;
      setChatAccountId(uid);
      // Auth has now fired at least once (uid null=guest OR a string=authed).
      // This unblocks the refresh-survival resume gate below.
      setAuthHasResolved(true);
      fernandoTrace('AUTH_RESOLVED', { uid, isFernando: isFernandoStrictMode(uid) });
    });
    return unsubscribe;
  }, []);

  // We'll only actually initialize the session for migrated accounts.
  // Use a state flag to track whether migration check has completed.
  const [prest0nSessionAccountId, setPrest0nSessionAccountId] = useState<string | null>(null);
  // Serverless-first (2026-08-15): bumped by useGlobalTools when a turn finds
  // the transport unwired but the turn-time gate says migrated — the mount
  // gate below re-runs instead of the user being told to refresh.
  const [gateRecheckNonce, setGateRecheckNonce] = useState(0);
  const [isMigrationGateChecking, setIsMigrationGateChecking] = useState<boolean>(true);
  const [cloudExtractEnabled, setCloudExtractEnabled] = useState(false);
  // ORG STICKINESS (INCIDENT-20260818): true when a signed-in user's
  // workspace identity could not be resolved AND no last-confirmed cache
  // exists — the surface HOLDS ("connecting to your workspace…"): no sends,
  // no thread creation, no filter/stamp identity. Cleared when a retry
  // resolves. Never true for genuine guests.
  const [workspaceUnresolved, setWorkspaceUnresolved] = useState(false);
  const workspaceRetryRef = useRef<{ timer: ReturnType<typeof setTimeout> | null; delayMs: number }>(
    { timer: null, delayMs: 2000 },
  );
  const armWorkspaceResolutionRetry = useCallback(() => {
    const state = workspaceRetryRef.current;
    if (state.timer) return; // one pending retry at a time
    const delay = state.delayMs;
    state.delayMs = Math.min(30_000, Math.round(state.delayMs * 2));
    state.timer = setTimeout(() => {
      workspaceRetryRef.current.timer = null;
      setGateRecheckNonce((n) => n + 1);
    }, delay);
  }, []);
  // Retry immediately when connectivity returns OR the tab wakes; reset
  // backoff on success. The wake nudge (2026-09-01): background-tab timer
  // throttling can hold the armed backoff retry long past the user's return,
  // so a wake into a failing gate looked like a permanently empty rail —
  // recheck on visibility too. Listeners exist ONLY while unresolved (the
  // early return above removes them on resolution), so healthy sessions pay
  // nothing; a recheck is one gate read, hence no hidden-duration threshold.
  useEffect(() => {
    if (!workspaceUnresolved) {
      workspaceRetryRef.current.delayMs = 2000;
      if (workspaceRetryRef.current.timer) {
        clearTimeout(workspaceRetryRef.current.timer);
        workspaceRetryRef.current.timer = null;
      }
      return;
    }
    if (typeof window === 'undefined') return;
    const recheck = () => setGateRecheckNonce((n) => n + 1);
    const onOnline = recheck;
    const onVisible = () => {
      if (document.visibilityState === 'visible') recheck();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [workspaceUnresolved]);

  // Perform the async migration gate check on mount / auth change
  useEffect(() => {
    if (!chatAccountId) {
      // Point 6 auth-init race fix: a null chatAccountId is AMBIGUOUS until
      // auth has fired at least once. On a hard refresh Firebase Auth is still
      // rehydrating, so a transient null here is NOT yet a genuine guest.
      // Declaring guest now (isMigrationGateChecking=false) lets the guest
      // resume path prematurely finalize an authenticated in-flight task as
      // "session expired". Stay in the checking state until authHasResolved.
      if (!authHasResolved) {
        setIsMigrationGateChecking(true);
        return;
      }
      // Auth has resolved and there is still no account id → genuine guest.
      setPrest0nSessionAccountId(null);
      setWorkspaceUnresolved(false); // guests are never "unresolved" — no org to hold for
      setIsMigrationGateChecking(false);
      // Settled with no workspace: release any boot-continuation overlay
      // promptly (2026-08-20 — a guest tab with a stale transition marker
      // previously hung on the overlay until its watchdog).
      publishWorkspaceSettledWithoutAccount();
      return;
    }
    let cancelled = false;
    setIsMigrationGateChecking(true);
    shouldUsePrest0nRuntimeForUser(chatAccountId).then((gate) => {
      if (!cancelled) {
        // ORG STICKINESS (INCIDENT-20260818): the old `gate.accountId ||
        // chatAccountId` fallback was the client half of the incident — a
        // gate hiccup silently swapped the session identity for the AUTH UID
        // (the member's personal org), re-filtering the thread list (read as
        // a spontaneous org switch) and permanently stamping new threads
        // into a view the member never opens. Identity is now exactly what
        // the gate resolved or last CONFIRMED — never a guess. Source 'none'
        // (unresolved) holds the surface until a retry succeeds.
        const unresolved = gate.isMigrated && !gate.accountId;
        const chosenAccountId = gate.isMigrated ? gate.accountId : null;
        // IDENTITY HOLD (2026-08-20, live rail-churn incident): an
        // error-shaped negative (gate.authoritative === false) must never
        // LOWER a live session identity to null — a null workspace unfilters
        // the thread rail and hands the reconciler full drop authority, which
        // is the owner-reported "threads keep disappearing and new threads
        // keep appearing". Hold the identity this session already had, arm
        // the retry, and let a real (authoritative) verdict change it.
        // `prest0nSessionAccountId` is the closure from this effect run's
        // render — fresh on every auth/nonce re-run, which is when this
        // executes.
        const errorShapedNegative = !gate.isMigrated && gate.authoritative === false;
        const holdPriorIdentity = errorShapedNegative && Boolean(prest0nSessionAccountId);
        // COLD-BOOT HOLD (2026-08-21, incognito churn diagnosis): an
        // error-shaped negative with NOTHING to hold (fresh profile — no
        // prior identity, no caches) used to settle the surface as
        // "no workspace" (workspaceUnresolved stayed false and the
        // settled-without-account release fired), so the rail ran identity-
        // less and the session rode the guest lane until the retry landed.
        // Treat it exactly like the thrown-gate path instead: hold the
        // surface unresolved; the already-armed retry delivers the verdict.
        const coldErrorHold = errorShapedNegative && !prest0nSessionAccountId;
        const isStrict = isFernandoStrictMode(chatAccountId) || isFernandoStrictMode(gate.accountId);
        fernandoTrace(
          'MIGRATION_GATE',
          {
            authUid: chatAccountId,
            isMigrated: gate.isMigrated,
            accountId: gate.accountId,
            accountIdSource: gate.accountIdSource,
            reason: gate.reason,
            chosenAccountId,
            heldPriorIdentity: holdPriorIdentity ? prest0nSessionAccountId : undefined,
            code: (!gate.isMigrated && isStrict) ? FERN_FE_CODES.MIGRATION_GATE_REJECTED : undefined,
          },
          isStrict
        );
        if (!holdPriorIdentity) setPrest0nSessionAccountId(chosenAccountId);
        setWorkspaceUnresolved(unresolved || coldErrorHold);
        setIsMigrationGateChecking(false);
        // Re-verify soon whenever this run's identity is anything but a clean
        // resolution: unresolved holds, error-shaped negatives (held or not),
        // and the cached-explicit hold all need the follow-up sample — it is
        // also what lets the gate's permission-denied debounce distinguish a
        // rules-deploy blip from real revocation.
        if (unresolved || errorShapedNegative || gate.accountIdSource === 'cached-explicit') {
          armWorkspaceResolutionRetry();
        } else if (gate.accountIdSource === 'resolved') {
          // Clean resolution: reset the retry backoff for the next incident.
          workspaceRetryRef.current.delayMs = 2000;
          if (workspaceRetryRef.current.timer) {
            clearTimeout(workspaceRetryRef.current.timer);
            workspaceRetryRef.current.timer = null;
          }
        }
        // Gate settled but no workspace will be keyed this load (unmigrated
        // account): same prompt release as the guest settle. NOT published
        // for `unresolved` — a retry is armed there and may still resolve —
        // nor while holding a prior identity through an error-shaped negative,
        // nor through the cold-boot hold (its retry may still resolve an org).
        if (!unresolved && !coldErrorHold && !chosenAccountId && !holdPriorIdentity) publishWorkspaceSettledWithoutAccount();
      }
    }).catch(() => {
      if (!cancelled) {
        fernandoTrace('MIGRATION_GATE', { authUid: chatAccountId, error: 'gate check threw', isMigrated: false });
        // IDENTITY HOLD (2026-08-20): a thrown gate check is infrastructure
        // failure, never a verdict — keep whatever identity this session
        // already had (see the hold note above) instead of nulling it, and
        // only mark the surface unresolved when there is nothing to hold.
        if (!prest0nSessionAccountId) {
          setPrest0nSessionAccountId(null);
          // The gate itself threw (not a resolution outcome): treat as
          // unresolved-with-retry rather than silently settling as a guest.
          setWorkspaceUnresolved(true);
        }
        setIsMigrationGateChecking(false);
        armWorkspaceResolutionRetry();
      }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatAccountId, authHasResolved, gateRecheckNonce]);

  // Call the session hook (passes null when account is not migrated — hook is a no-op in that case)
  useEffect(() => {
    if (!prest0nSessionAccountId) {
      setCloudExtractEnabled(false);
      return;
    }
    const db = getFirestore();
    const flagsRef = doc(db, `accounts/${prest0nSessionAccountId}/vm/flags`);
    getDoc(flagsRef)
      .then(async (snap) => {
        // Tri-state (2026-08-18 lane-unification): explicit per-account value
        // wins in either direction; ABSENT falls to the runtime default knob
        // system/prest0Core/config/main.cloudExtractDefaultOn. R19 made the
        // server-owned attachment lane the default for every account while
        // this flag — the only door to GCS upload + cloud ingest — stayed a
        // hand-set per-account opt-in, which left production attachments on
        // the worst path (no browser extraction AND no cloud pipeline). The
        // knob flips the fleet without a deploy; --disable stays an opt-out.
        const flagValue = snap.exists() ? snap.data()?.cloudExtractEnabled : undefined;
        if (flagValue === true) { setCloudExtractEnabled(true); return; }
        if (flagValue === false) { setCloudExtractEnabled(false); return; }
        try {
          const sysSnap = await getDoc(doc(db, 'system', 'prest0Core', 'config', 'main'));
          setCloudExtractEnabled(sysSnap.exists() && sysSnap.data()?.cloudExtractDefaultOn === true);
        } catch {
          setCloudExtractEnabled(false);
        }
      })
      .catch(() => setCloudExtractEnabled(false));
  }, [prest0nSessionAccountId]);

  // H1 (owner report 2026-07-14, spinner-outlives-turn): latest-ref bridge for
  // the recovery poll's terminal release. The recovery poll (initSession, empty
  // callbacks + durable thread write) finalizes a resumed task's content with
  // NO onDone — this callback dispatches the same thread-keyed stopProcessing
  // the live completion path would. Assigned (below, after stopProcessing and
  // the discovery-lit set exist) via effect; the indirection keeps the hook
  // call order valid.
  const recoveredTaskTerminalRef = useRef<(threadId: string | null, taskId: string) => void>(
    () => undefined,
  );

  // Call the session hook (passes null when account is not migrated — hook is a no-op in that case)
  const prest0nSessionHook = usePrest0nSession(
    prest0nSessionAccountId,
    globalTools.pageContextRef?.current,
    undefined,
    {
      onRecoveredTaskTerminal: (threadId, taskId) =>
        recoveredTaskTerminalRef.current(threadId, taskId),
    },
  );

  // Wire the session into globalTools so createMessageHandler can reach it.
  // Re-runs whenever the session object or sendTurn function reference changes.
  useEffect(() => {
    if (globalTools.setPrest0nSession) {
      globalTools.setPrest0nSession(prest0nSessionHook.session, prest0nSessionHook.sendTurn);
    }
    if (globalTools.setRequestSessionRewire) {
      globalTools.setRequestSessionRewire(() => setGateRecheckNonce((n) => n + 1));
    }
  }, [prest0nSessionHook.session, prest0nSessionHook.sendTurn, globalTools.setPrest0nSession]);

  // Low balance warning (blocks chat when the wallet is EMPTY — <= $0)
  const rawLowBalanceState = useLowBalanceWarning();

  // Expired-trial hard gate (owner directive 2026-07-31): once a trial has
  // genuinely ended (subscription-gated — activated accounts with legacy
  // users/{uid}.trial fields never count), the user cannot chat; the
  // trial-ended message injected in GlobalChatSystem explains next steps.
  const trialGateStatus = useTrialStatus();
  const trialExpiredBlock = trialGateStatus.isTrialUser && trialGateStatus.isExpired;

  // Override low-balance state for billing-exempt instances (e.g. citadel)
  // Also bypass for Prest0n-migrated accounts — their billing is handled server-side,
  // not via the frontend OLV wallet gate (avoids false-positive blocks if wallet
  // doc is missing/zero during auth resolution or the OLV rate is momentarily absent).
  // Note: While the async migration gate is checking, we temporarily bypass the balance
  // to avoid a race condition where the wallet resolves 0 before the migration gate completes,
  // causing a momentary false-positive "balance is too low" error that permanently injects into the UI.
  const { isTrial: isTrialUser, isLoading: isTrialLoading } = useIsTrialUser();
  // Trial users are billed via server-side creditRemainingCents, not OLV tokens.
  // Their wallet/summary doc may not exist yet → bypass OLV gate to avoid false blocks.
  const shouldBypassBalance = config.skipBalanceCheck || config.disableBalanceCheck || Boolean(prest0nSessionAccountId) || isMigrationGateChecking || isTrialUser || isTrialLoading;
  // OUT of PRO Work Units (2026-08-14, server-hard-rule alignment): the
  // composer blocks only when the wallet is actually EMPTY (<= $0) for a real
  // signed-in user whose summary has settled. Unlike the legacy $3 gate, the
  // ENTITLEMENT bypass does NOT waive this state — the server balance stop
  // pauses those accounts too (the broader shouldBypassBalance lane above,
  // e.g. server-billed prest0n accounts and trial billing, is unchanged).
  const outOfWorkUnits =
    rawLowBalanceState.hasUser === true &&
    rawLowBalanceState.balanceSettled === true &&
    rawLowBalanceState.balanceUsd <= 0;
  const lowBalanceState: LowBalanceState = shouldBypassBalance
    ? {
        isWarningBalance: false,
        isCriticalBalance: false,
        isLowBalance: false,
        balanceUsd: rawLowBalanceState.balanceUsd,
        balanceOlv: rawLowBalanceState.balanceOlv,
        shouldWarn: false,
        shouldBlock: false,
        isLoading: false,
        isTestingMode: rawLowBalanceState.isTestingMode,
        // Preserve entitlement bypass introspection from the raw hook
        // (or default to "no bypass" when the legacy `useIsTrialUser`
        // override is the source of the bypass). Downstream consumers
        // should treat `shouldBlock` as the source of truth; these
        // fields exist for observability.
        hasEntitlementBypass: rawLowBalanceState.hasEntitlementBypass,
        entitlementBypassReasons: rawLowBalanceState.entitlementBypassReasons,
        hasUser: rawLowBalanceState.hasUser,
        // Balance is not the billing truth on this lane — chat balance chrome
        // (nudge/out states) must stay silent for bypassed instances.
        balanceSettled: false,
      }
    : {
        ...rawLowBalanceState,
        isCriticalBalance: outOfWorkUnits,
        shouldBlock: outOfWorkUnits && !rawLowBalanceState.isTestingMode,
      };

  // Authentication state for secure hydration
  const { isAuthenticated, user } = useAuth();

  // Helper to compute per-page storage keys (without useCallback for initial state)
  const computePageStorageKey = (baseKey: string) => {
    return buildPageStorageKey(baseKey);
  };

  // Get storage keys with global history support
  const storageKeys = getChatStorageKeys(config.instanceId, config.useGlobalHistory);

  // Note: the cross-tab streaming lease lives in ./streamingFlag (per-tab keys).

  // State reducer (initialize from per-page storage to avoid flash/opening)
  const [state, rawDispatch] = useReducer(chatReducer, undefined, () => {
    let initialOpen = config.initialOpen;
    let initialDock = config.initialDocked;

    if (typeof window !== 'undefined') {
      try {
        const pageOpenKey = computePageStorageKey(GLOBAL_CHAT_OPEN_KEY);
        const storedOpen = getFromLocalStorage<string>(pageOpenKey, initialOpen ? '1' : '0');
        initialOpen = storedOpen === '1';

        if (!config.disableDocking) {
          const pageDockKey = computePageStorageKey(GLOBAL_CHAT_DOCK_KEY);
          const storedDock = getFromLocalStorage<string>(pageDockKey, initialDock ? '1' : '0');
          initialDock = storedDock === '1';
        }
      } catch {
        // fall back to defaults
      }
    }

    // Seed the mobile bit HERE (not in the module-level initialState, which
    // unit tests import without a matchMedia stub) so a phone reload that
    // restores an open chat boots straight into the fullscreen takeover with
    // no floating-panel flash before the viewport effect's first dispatch.
    let initialMobile = false;
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      try {
        initialMobile = window.matchMedia('(max-width: 820px)').matches;
      } catch {
        initialMobile = false;
      }
    }

    const storedFullscreen = typeof window !== 'undefined'
      ? getFromLocalStorage<string>(computePageStorageKey(GLOBAL_CHAT_FULLSCREEN_KEY), '0') === '1'
      : false;

    return {
      ...initialState,
      isChatOpen: initialOpen,
      isMobileViewport: initialMobile,
      isChatDockedRight: initialMobile ? false : initialDock,
      isChatFullScreen: (initialMobile && initialOpen) ? true : storedFullscreen,
    };
  });

  // ────────────────────────────────────────────────────────────────────
  // SYNCHRONOUS message-state mirror (PR #97 — Bug #1 fix)
  //
  // React's useReducer is asynchronous: dispatch() schedules the reducer
  // run, but state.messages doesn't update until the next React commit.
  // useChatThreads.switchThread() snapshots the outgoing thread by
  // reading a ref that's updated via useEffect AFTER React commit. If a
  // user types in thread A and immediately switches to thread B in the
  // same tick, the snapshot writes the PRE-dispatch messages array to
  // A's per-thread storage — the new userMsg is lost.
  //
  // Fix: maintain `messagesSyncRef` updated SYNCHRONOUSLY by running the
  // reducer eagerly on every message-mutation dispatch. Expose it via
  // refs so useChatThreads.snapshotActive can read the post-dispatch
  // value without waiting for React to commit.
  //
  // The eager reducer call is harmless: chatReducer is pure, React still
  // runs the canonical reducer pass during commit, and the result is
  // always the same. We only do this for message-mutation actions to
  // avoid waste; other actions (open/close/scroll/processing/etc.) skip
  // the eager pass.
  // ────────────────────────────────────────────────────────────────────
  const messagesSyncRef = useRef<ChatMessage[]>(state.messages);
  // Keep a sync mirror of the canonical state so the eager reducer pass
  // can compose multiple dispatches in the same tick without missing
  // intermediate updates.
  const stateSyncRef = useRef<GlobalChatState>(state);
  useEffect(() => {
    stateSyncRef.current = state;
    messagesSyncRef.current = state.messages;
  }, [state]);

  /**
   * Action types that mutate `state.messages`. For these we eagerly run
   * `chatReducer` against `stateSyncRef.current` so `messagesSyncRef`
   * captures the post-dispatch messages array within the same tick.
   */
  const MESSAGE_MUTATION_TYPES = useRef<Set<string>>(new Set([
    'SET_MESSAGES',
    'ADD_MESSAGE',
    'ADD_MESSAGES',
    'UPDATE_MESSAGE',
    'UPDATE_MESSAGE_BY_ID',
    'REMOVE_MESSAGE',
    'REMOVE_MESSAGE_BY_ID',
  ]));

  // Stable dispatch wrapper. Identity is preserved across renders so
  // existing useCallback/useEffect dep arrays that omit `dispatch`
  // (the previous useReducer dispatch was always stable) keep their
  // behavior. We achieve stability by stashing rawDispatch in a ref
  // and using empty useCallback deps.
  const rawDispatchRef = useRef(rawDispatch);
  useEffect(() => { rawDispatchRef.current = rawDispatch; }, [rawDispatch]);
  const dispatch = useCallback((action: GlobalChatAction) => {
    if (action && MESSAGE_MUTATION_TYPES.current.has((action as { type: string }).type)) {
      try {
        const next = chatReducer(stateSyncRef.current, action);
        stateSyncRef.current = next;
        messagesSyncRef.current = next.messages;
      } catch {
        // If the eager reducer throws, fall back to async-only — React
        // will still surface the error via its own dispatch path.
      }
    }
    rawDispatchRef.current(action);
  }, []);

  // Refs
  const promptInputRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  /**
   * Inner wrapper around all transcript content (see styles.TranscriptContent).
   * useTranscriptScroll's ResizeObserver watches it so post-paint growth —
   * follow-up chips resolving, images decoding — re-sticks a pinned viewport.
   */
  const transcriptContentRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const promptBarRef = useRef<HTMLDivElement>(null);
  const floatingPromptBarRef = useRef<HTMLDivElement>(null);
  /** See UseGlobalChatResult.refs.sendRouteOverrideRef — the host's send wrapper. */
  const sendRouteOverrideRef = useRef<(() => void | Promise<void>) | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  // Internal refs for tracking
  const chatRestoredRef = useRef(false);
  // AUTH-SETTLE (2026-09-01): true after the hydration effect cleared legacy
  // residue on a SIGNED-OUT Firebase verdict. A later sign-in re-arms the
  // (otherwise mount-latched) hydration effect — see the re-arm effect above
  // the hydration effect.
  const clearedForSignedOutRef = useRef(false);
  const promptRestoredRef = useRef(false);
  const draftLastSavedRef = useRef<string | null>(null);
  const draftPersistTimeoutRef = useRef<number | null>(null);
  // Payload of a debounced draft save that has been scheduled but not yet
  // fired — flushed synchronously on pagehide so a reload inside the 500 ms
  // debounce window (most visibly the update banner's "refresh now" click)
  // cannot lose the just-typed tail of the composer. Cleared at fire time
  // and on every send-path cancel. See the pagehide effect below.
  const draftPendingFlushRef = useRef<{ html: string; text: string } | null>(null);
  // Stamped at the synchronous start of every send so the cross-tab draft
  // poll + on-mount hydrate effects can suppress draft rehydration for a
  // short window after send. See JUST_SENT_DRAFT_SUPPRESS_MS at module top.
  const lastSendAtRef = useRef<number>(0);
  const prevAttachmentCountRef = useRef(0);
  const promptFocusedRef = useRef(false);
  const pageStateRestoredRef = useRef(false); // Track if per-page state has been restored
  const userInitiatedOpenRef = useRef(false); // Track if user explicitly opened chat (for focus)
  const onProcessingBlockedRef = useRef<((source: 'main' | 'floating') => void) | null>(null); // Callback for Enter key blocked during processing
  const messageCountRef = useRef(0); // Track message count synchronously to avoid stale closure issues
  // Thread-isolation: when handleSend captures a thread id at send-time,
  // it pushes the id here so startProcessing / stopProcessing /
  // startStreamingMessage can write the per-thread processing flag for
  // the ORIGINATING thread, not the currently-active one. The ref is
  // popped when the send completes. Per-call closures still capture
  // their own `originatingThreadId` constant; this ref is the bridge
  // for the heartbeat helpers exposed via globalTools sharedContext
  // (which can fire mid-handler before the helper bundle is wired).
  const currentSendThreadIdRef = useRef<string | null>(null);
  /**
   * Set by GlobalChatSystem after the thread store mounts. Returns the
   * id of the currently-active thread, or null when the thread switcher
   * is disabled (legacy single-thread instances). handleSend reads this
   * at send-time to capture the originating thread id; the streaming
   * routing helpers read it on every dispatch to route updates to the
   * originating thread when it differs from the now-active one.
   */
  const getActiveThreadIdRef = useRef<(() => string | null) | null>(null);
  /**
   * Set by GlobalChatSystem after the thread store mounts. Allows the
   * thread-routing streaming helpers to write directly into a non-active
   * thread's per-thread localStorage when the user has switched away
   * mid-stream. Without this, an update for thread A landing while
   * thread B is active would be dispatched into state.messages (which
   * is now B's view) and either: (a) be silently dropped because the
   * id can't be found, or (b) corrupt B's chat with A's chunks.
   */
  const writeThreadMessagesRef = useRef<((threadId: string, messages: ChatMessage[]) => void) | null>(null);
  const readThreadMessagesRef = useRef<((threadId: string) => ChatMessage[]) | null>(null);
  /**
   * Set by GlobalChatSystem after the thread store mounts: is this thread a
   * SHARED (group) thread — registered in the membership snapshot, or
   * carrying the server-owned `sharedThread` index marker? Read once per
   * send to decide the attachment upload wait (see
   * SendAttachmentResolution.awaitFullUpload). Three answers (PR-6, 2026-09-09):
   * true = shared, null = the membership read has not answered yet (wait — the
   * safe side), false = a resolved solo thread. No predicate at all ⇒ solo.
   */
  const isThreadSharedRef = useRef<((threadId: string) => boolean | null) | null>(null);
  /**
   * Set by GlobalChatSystem: the personalized-greeting composer (pure, zero
   * network). Consulted by the welcome injector; null → static welcome.
   */
  const composeGreetingRef = useRef<(() => {
    text: string;
    html: string;
    tokenUsage?: TokenUsageSnapshot;
    /** Monotonic copy rank + persisted chips (2026-08-09 greeting parity). */
    greetingRank?: number;
    starters?: string[];
  } | null) | null>(null);
  const welcomeMessageInjectedRef = useRef(false); // Track if welcome message has been injected this mount
  const welcomeMessageAbortRef = useRef(false); // True after 5s timeout — stop waiting for async config
  const lastPersistTimeRef = useRef(0); // Throttle persist operations during rapid updates (streaming)
  const pendingPersistRef = useRef<number | null>(null); // Deferred persist timeout for throttled writes
  const lastStorageValueRef = useRef<string | null>(null); // Track localStorage content for content-based sync (not just count)

  // Auth-aware hydration refs (security: prevent showing user-specific data before authentication)
  const isFreshSessionRef = useRef<boolean | null>(null); // null = not yet checked, true = no local data, false = has local data
  const sessionMessagesRef = useRef<ChatMessage[]>([]); // Buffer for messages typed before authentication
  const authAwareHydrationCompleteRef = useRef(false); // Track if auth-aware hydration decision has been made

  // Keep messageCountRef in sync with state.messages.length
  useEffect(() => {
    messageCountRef.current = state.messages.length;
  }, [state.messages.length]);

  // Track navigation changes to reset chat visibility/dock between pages
  const [navPath, setNavPath] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const getPath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const handleLocationChange = () => setNavPath(getPath());

    const winHistory = window.history;
    const originalPushState = winHistory.pushState;
    const originalReplaceState = winHistory.replaceState;

    const wrapHistory =
      (originalFn: typeof winHistory.pushState) =>
      (...args: Parameters<typeof winHistory.pushState>) => {
        const result = originalFn.apply(winHistory, args);
        window.dispatchEvent(new Event('locationchange'));
        return result;
      };

    winHistory.pushState = wrapHistory(originalPushState);
    winHistory.replaceState = wrapHistory(originalReplaceState);
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('locationchange', handleLocationChange);
    handleLocationChange();

    return () => {
      winHistory.pushState = originalPushState;
      winHistory.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('locationchange', handleLocationChange);
    };
  }, []);

  // Construction waitlist hook
  const waitlistController = useConstructionWaitlist();

  // ============================================================================
  // Per-Page Chat Preference Persistence
  // ============================================================================

  // Helper to get page-specific storage keys
  const getPageStorageKey = useCallback((baseKey: string) => {
    return buildPageStorageKey(baseKey);
  }, []);

  // Helper to persist page-specific open state immediately (avoids losing state on fast nav)
  const persistPageOpen = useCallback(
    (isOpen: boolean) => {
      if (typeof window === 'undefined') return;
      const pageOpenKey = getPageStorageKey(GLOBAL_CHAT_OPEN_KEY);
      setToLocalStorage(pageOpenKey, isOpen ? '1' : '0');
    },
    [getPageStorageKey]
  );

  // Helper to persist page-specific dock state immediately
  const persistPageDock = useCallback(
    (isDocked: boolean) => {
      if (typeof window === 'undefined' || config.disableDocking) return;
      const pageDockedKey = getPageStorageKey(GLOBAL_CHAT_DOCK_KEY);
      setToLocalStorage(pageDockedKey, isDocked ? '1' : '0');
    },
    [config.disableDocking, getPageStorageKey]
  );

  // Restore GLOBAL chat open/dock/fullscreen state (P4: mode is a user
  // preference, identical on every route — the widget no longer "forgets" its
  // position on navigation).
  // This effect MUST run before the persist effects to prevent overwriting saved state.
  //
  // Per-tab view independence (2026-08-09): the shared mode flags are read
  // ONCE per mount (new-tab initialization from the last-saved view). The
  // navPath-keyed re-run used to RE-READ shared localStorage mid-session, so
  // navigating in one tab silently adopted whatever view a sibling tab had
  // persisted last — the "all my windows changed view together" report. After
  // the first hydrate, this tab's own state is authoritative; the persist
  // effects keep writing, so NEW tabs still initialize last-write-wins.
  const modeFlagsHydratedRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (modeFlagsHydratedRef.current) {
      pageStateRestoredRef.current = true;
      return undefined;
    }
    modeFlagsHydratedRef.current = true;

    // Disable persistence temporarily while we restore
    pageStateRestoredRef.current = false;

    // One-time migration: when a global key is unset, seed it from the
    // CURRENT page's legacy per-pathname key (pre-P4 shape). Legacy keys are
    // left in place (never deleted) for rollback.
    const readModeFlag = (globalKey: string): boolean => {
      const stored = getFromLocalStorage<string>(globalKey, '');
      if (stored === '1' || stored === '0') return stored === '1';
      const legacy = getFromLocalStorage<string>(buildLegacyPageStorageKey(globalKey), '');
      if (legacy === '1' || legacy === '0') {
        setToLocalStorage(globalKey, legacy);
        return legacy === '1';
      }
      return false;
    };

    dispatch({ type: 'SET_CHAT_OPEN', payload: readModeFlag(GLOBAL_CHAT_OPEN_KEY) });
    if (!config.disableDocking) {
      dispatch({ type: 'SET_CHAT_DOCKED_RIGHT', payload: readModeFlag(GLOBAL_CHAT_DOCK_KEY) });
    }
    dispatch({ type: 'SET_CHAT_FULLSCREEN', payload: readModeFlag(GLOBAL_CHAT_FULLSCREEN_KEY) });

    // Mark restored synchronously; we restored from storage above so persist is safe now
    pageStateRestoredRef.current = true;
    return undefined;
  }, [navPath, config.disableDocking, getPageStorageKey]);

  // Listen for external requests to open chat (e.g., from tool tiles on justiceisurright page)
  // This provides a reliable way to open the chat without relying on DOM click() which may not trigger React handlers
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleExternalOpen = (e: Event) => {
      const customEvent = e as CustomEvent<{ triggerPhrase?: string; openFile?: boolean }>;
      const { triggerPhrase, openFile } = customEvent.detail || {};

      // Open the chat (bypass pageStateRestoredRef check since this is an explicit user action)
      userInitiatedOpenRef.current = true;
      dispatch({ type: 'SET_CHAT_OPEN', payload: true });
      persistPageOpen(true);

      // If trigger phrase provided, set it after a short delay for DOM to update
      if (triggerPhrase) {
        setTimeout(() => {
          if (promptInputRef.current) {
            promptInputRef.current.innerHTML = triggerPhrase;
            dispatch({ type: 'SET_PROMPT_HTML', payload: triggerPhrase });
            dispatch({ type: 'SET_PROMPT', payload: triggerPhrase });
            promptInputRef.current.focus({ preventScroll: true });
            // Place cursor at end
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(promptInputRef.current);
            range.collapse(false);
            selection?.removeAllRanges();
            selection?.addRange(range);
          }
        }, 150);
      }

      // If file upload requested, trigger file select after delay
      if (openFile) {
        setTimeout(() => {
          fileInputRef.current?.click();
        }, 200);
      }
    };

    document.addEventListener('globalchat:open', handleExternalOpen);
    return () => {
      document.removeEventListener('globalchat:open', handleExternalOpen);
    };
  }, [persistPageOpen]);

  // Persist chat open state per-page (only after initial restore is complete)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Don't persist until we've restored the per-page state to avoid overwriting with initial state
    if (!pageStateRestoredRef.current) return;
    
    const pageOpenKey = getPageStorageKey(GLOBAL_CHAT_OPEN_KEY);
    setToLocalStorage(pageOpenKey, state.isChatOpen ? '1' : '0');
  }, [state.isChatOpen, getPageStorageKey]);

  // Persist dock state (global key — only after initial restore is complete)
  useEffect(() => {
    if (config.disableDocking || typeof window === 'undefined') return;
    // Don't persist until we've restored the per-page state to avoid overwriting with initial state
    if (!pageStateRestoredRef.current) return;
    // P4: the mobile force-undock is a VIEWPORT override, not a preference
    // change — never write while mobile, so returning to desktop restores the
    // user's docked choice instead of finding it silently erased.
    if (state.isMobileViewport) return;

    const pageDockedKey = getPageStorageKey(GLOBAL_CHAT_DOCK_KEY);
    setToLocalStorage(pageDockedKey, state.isChatDockedRight ? '1' : '0');
  }, [state.isChatDockedRight, state.isMobileViewport, config.disableDocking, getPageStorageKey]);

  // P4: returning to desktop restores the dock preference the mobile
  // override displaced (the override never wrote it — see the guard above).
  //
  // Per-tab view independence (2026-08-09): restore THIS TAB's own last
  // desktop dock choice, continuously tracked below — the shared storage key
  // may have been rewritten by a sibling tab while this one was mobile, and
  // re-reading it here was the one remaining path where a live tab silently
  // adopted another tab's view.
  // SEEDED from the stored preference (review 2026-08-14) — same rationale
  // as lastDesktopFullscreenRef below: a mobile-boot mount must still know
  // the displaced desktop choice or the first crossing persists the forced
  // value over it.
  const lastDesktopDockRef = useRef<boolean | null>(null);
  if (lastDesktopDockRef.current === null && !config.disableDocking && typeof window !== 'undefined') {
    try {
      lastDesktopDockRef.current =
        getFromLocalStorage<string>(computePageStorageKey(GLOBAL_CHAT_DOCK_KEY), '0') === '1';
    } catch {
      // leave null — the restore effect treats null as "nothing observed"
    }
  }
  useEffect(() => {
    if (config.disableDocking || typeof window === 'undefined') return;
    if (state.isMobileViewport) return;
    if (!pageStateRestoredRef.current) return;
    const remembered = lastDesktopDockRef.current;
    if (remembered === null) return; // no desktop dock choice observed yet this mount
    if (remembered !== state.isChatDockedRight) {
      dispatch({ type: 'SET_CHAT_DOCKED_RIGHT', payload: remembered });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isMobileViewport]);
  // Tracker declared AFTER the restore effect: on the mobile→desktop commit
  // the restore reads the remembered pre-mobile value BEFORE this records the
  // (still force-undocked) current state; the restore's dispatch then brings
  // the tracker back in line on the next render.
  useEffect(() => {
    if (!state.isMobileViewport) {
      lastDesktopDockRef.current = state.isChatDockedRight;
    }
  }, [state.isChatDockedRight, state.isMobileViewport]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!pageStateRestoredRef.current) return;
    // The mobile fullscreen takeover is a VIEWPORT override, not a
    // preference change (same doctrine as the dock guard above) — never
    // write while mobile, so a phone session doesn't force the user's
    // desktop tabs into fullscreen through the shared global key.
    if (state.isMobileViewport) return;
    const pageFullscreenKey = getPageStorageKey(GLOBAL_CHAT_FULLSCREEN_KEY);
    setToLocalStorage(pageFullscreenKey, state.isChatFullScreen ? '1' : '0');
  }, [state.isChatFullScreen, state.isMobileViewport, getPageStorageKey]);

  // Returning to desktop restores the fullscreen preference the mobile
  // takeover displaced — the exact per-tab pattern lastDesktopDockRef uses:
  // restore from THIS TAB's continuously-tracked last desktop choice, never
  // by re-reading the shared storage key a sibling tab may have rewritten.
  //
  // SEEDED from the stored preference (review 2026-08-14): a session that
  // BOOTS at ≤820px never runs the desktop tracker, so with a null seed the
  // first mobile→desktop crossing found nothing to restore and the persist
  // effect wrote the forced fullscreen as the user's durable preference —
  // the exact write the viewport-override doctrine forbids. The seed is the
  // same one-time storage read the lazy init performs; the tracker below
  // overwrites it with the live desktop choice as soon as one is observed.
  const lastDesktopFullscreenRef = useRef<boolean | null>(null);
  if (lastDesktopFullscreenRef.current === null && typeof window !== 'undefined') {
    try {
      lastDesktopFullscreenRef.current =
        getFromLocalStorage<string>(computePageStorageKey(GLOBAL_CHAT_FULLSCREEN_KEY), '0') === '1';
    } catch {
      // leave null — the restore effect treats null as "nothing observed"
    }
  }
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (state.isMobileViewport) return;
    if (!pageStateRestoredRef.current) return;
    const remembered = lastDesktopFullscreenRef.current;
    if (remembered === null) return; // no desktop fullscreen choice observed yet this mount
    if (remembered !== state.isChatFullScreen) {
      dispatch({ type: 'SET_CHAT_FULLSCREEN', payload: remembered });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isMobileViewport]);
  useEffect(() => {
    if (!state.isMobileViewport) {
      lastDesktopFullscreenRef.current = state.isChatFullScreen;
    }
  }, [state.isChatFullScreen, state.isMobileViewport]);

  const lastAppliedUiSyncRef = useRef<{
    isChatOpen: boolean;
    isChatDockedRight: boolean;
    isChatFullScreen: boolean;
  } | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pagePath = window.location.pathname;
    return subscribeToChatUiStateSync(
      { instanceId: config.instanceId, pagePath },
      (payload) => {
        const currentState = stateSyncRef.current;
        const nextOpen = typeof payload.isChatOpen === 'boolean' ? payload.isChatOpen : currentState.isChatOpen;
        const nextDock = typeof payload.isChatDockedRight === 'boolean'
          ? payload.isChatDockedRight
          : currentState.isChatDockedRight;
        const nextFullscreen = typeof payload.isChatFullScreen === 'boolean'
          ? payload.isChatFullScreen
          : currentState.isChatFullScreen;
        lastAppliedUiSyncRef.current = {
          isChatOpen: nextOpen,
          isChatDockedRight: nextDock,
          isChatFullScreen: nextFullscreen,
        };
        if (typeof payload.isChatOpen === 'boolean') {
          // DOCKED = PINNED (2026-08-22): another surface's close never pulls
          // a docked pane shut — only this pane's own ✕ does.
          if (!(payload.isChatOpen === false && currentState.isChatDockedRight)) {
            dispatch({ type: 'SET_CHAT_OPEN', payload: payload.isChatOpen });
          }
        }
        if (typeof payload.isChatDockedRight === 'boolean') {
          dispatch({ type: 'SET_CHAT_DOCKED_RIGHT', payload: payload.isChatDockedRight });
        }
        if (typeof payload.isChatFullScreen === 'boolean') {
          dispatch({ type: 'SET_CHAT_FULLSCREEN', payload: payload.isChatFullScreen });
        }
      },
    );
  }, [config.instanceId, dispatch]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const lastApplied = lastAppliedUiSyncRef.current;
    if (
      lastApplied
      && lastApplied.isChatOpen === state.isChatOpen
      && lastApplied.isChatDockedRight === state.isChatDockedRight
      && lastApplied.isChatFullScreen === state.isChatFullScreen
    ) {
      lastAppliedUiSyncRef.current = null;
      return;
    }
    publishChatUiStateSync({
      instanceId: config.instanceId,
      pagePath: window.location.pathname,
      payload: {
        isChatOpen: state.isChatOpen,
        isChatDockedRight: state.isChatDockedRight,
        isChatFullScreen: state.isChatFullScreen,
      },
      source: 'useGlobalChat',
    });
  }, [
    config.instanceId,
    state.isChatOpen,
    state.isChatDockedRight,
    state.isChatFullScreen,
  ]);

  // Hotfix #2 (2026-05-13) — one-time-on-mount sweep of localStorage for
  // legacy stale low-balance banner rows that the older sanitizer
  // persisted WITHOUT their `sourceTag` (so the existing tag-based filter
  // can't see them). Idempotent: on subsequent mounts there is nothing
  // to remove. See `staleLowBalanceFilter.ts` header for the trace.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const result = migrateStaleLowBalanceLocalStorage_v20260513();
      if (result.rowsRemoved > 0) {
        // eslint-disable-next-line no-console
        console.log(
          '[GlobalChat] Stale low-balance migration removed',
          result.rowsRemoved,
          'rows across',
          result.keysRewritten,
          'localStorage keys (scanned',
          result.keysScanned,
          'keys total).'
        );
      }
    } catch (err) {
      // Quota / read-only / non-storage env — non-fatal.
      // eslint-disable-next-line no-console
      console.warn('[GlobalChat] Stale low-balance migration skipped:', err);
    }
  }, []);

  // Re-arm legacy hydration when the user signs in AFTER a signed-out clear
  // (AUTH-SETTLE, 2026-09-01): the wipe latches chatRestoredRef, so without
  // this a later sign-in re-runs the hydration effect (isAuthenticated dep)
  // only to hit the first-line early return — legacy-lane hydration never
  // happened for the rest of the session. Placed ABOVE the hydration effect
  // so the re-arm lands before its same-commit re-run.
  useEffect(() => {
    if (!isAuthenticated || !clearedForSignedOutRef.current) return;
    clearedForSignedOutRef.current = false;
    isFreshSessionRef.current = null;
    authAwareHydrationCompleteRef.current = false;
    chatRestoredRef.current = false; // hydration effect re-runs via its isAuthenticated dep
  }, [isAuthenticated]);

  // Hydrate chat history (with attachments) from storage
  // SECURITY: Auth-aware hydration to prevent showing user-specific data before authentication
  // Deferred during streaming to prevent blocking page load in other tabs
  useEffect(() => {
    if (chatRestoredRef.current || !config.persistHistory) return;
    let cancelled = false;

    // Check if any tab (including this one) is actively streaming. The lease
    // heartbeat bridges long API calls (Stage 3: 3-5 min between chunks), so
    // the shared short freshness window in ./streamingFlag is safe here.
    const isOtherTabStreaming = (): boolean => isAnyTabStreaming();

    // Use requestIdleCallback to defer heavy JSON parsing during streaming
    // This prevents blocking page navigation when another tab is streaming
    const scheduleHydration = (task: () => void) => {
      const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, opts?: { timeout: number }) => number);
      if (typeof ric === 'function') {
        ric(task, { timeout: 3000 }); // Max 3s delay
      } else {
        setTimeout(task, 50); // Yield to browser first
      }
    };

    const doHydration = async () => {
      if (cancelled) return;

      try {
        // THREADING surfaces (extended view included): the per-thread store
        // owns the pane — the legacy CHAT_HISTORY hydrate must neither
        // populate nor blank it (its SET_MESSAGES dispatches race
        // useChatThreads' own hydrate, and the auth-timeout wipe blanked a
        // restored pane). Mark restored so the welcome injection and
        // downstream gates open; per-thread hydrate is the pane's sole owner
        // here. Same raw-flag precedent as the cross-tab-sync / persist /
        // beforeunload skips below. Internal Prest0n surfaces keep legacy
        // hydration: GlobalChatSystem force-disables threading there, so the
        // raw flag alone would strand them with NO hydration from either
        // path — the shared guard keeps this skip in lockstep.
        if (config.enableThreadSwitcher && !isInternalPrest0nSurface(config.source, config.instanceId)) {
          if (!cancelled) {
            chatRestoredRef.current = true;
            authAwareHydrationCompleteRef.current = true;
          }
          return;
        }

        // Get raw string first (fast)
        const raw = localStorage.getItem(storageKeys.CHAT_HISTORY);

        // SECURITY: Detect fresh session on first check
        if (isFreshSessionRef.current === null) {
          isFreshSessionRef.current = !raw;
        }

        // SECURITY: Fresh session with no authentication = show empty chat
        // This ensures new visitors / incognito users see a clean slate
        if (isFreshSessionRef.current && !isAuthenticated) {
          if (!cancelled) {
            chatRestoredRef.current = true;
            authAwareHydrationCompleteRef.current = true;
          }
          return;
        }

        // SECURITY: Has localStorage but not authenticated - wait for auth to settle
        // This prevents showing previous user's data before we know who's viewing
        // Exception: skipBalanceCheck pages use their own page-level access gate
        // instead of Firebase auth. No route sets this today — /justiceisurright's
        // page-password gate became a Firebase ACCOUNT gate in Round M (2026-07-15),
        // so the auth-settle security wait now applies there like everywhere else.
        if (!isFreshSessionRef.current && !isAuthenticated && !authAwareHydrationCompleteRef.current) {
          if (config.skipBalanceCheck) {
            // Client-facing page with its own auth gate — skip the security timeout
            if (!cancelled) {
              chatRestoredRef.current = true;
              authAwareHydrationCompleteRef.current = true;
            }
            return;
          }
          // AUTH-SETTLE (PLAN-chat-reliability §Phase 3 item 4, 2026-09-01):
          // wait for Firebase's first onAuthStateChanged verdict instead of a
          // wall-clock 2s — a slow token restore no longer wipes a real
          // user's pane. No timer: the effect re-runs when authHasResolved /
          // isAuthenticated flip and delivers the verdict then.
          if (!authHasResolved) return;
          // Firebase settled signed-out while residue exists: prior user's
          // data on a shared browser — clear it (unchanged security
          // behavior, now verdict-driven instead of clock-driven).
          chatRestoredRef.current = true;
          authAwareHydrationCompleteRef.current = true;
          clearedForSignedOutRef.current = true;
          dispatch({ type: 'SET_MESSAGES', payload: [] });
          console.log('[GlobalChat] auth settled signed-out — cleared stale localStorage data for security');
          return;
        }

        // Mark hydration decision as complete (user is authenticated or we've made a decision)
        authAwareHydrationCompleteRef.current = true;

        if (!raw) {
          if (!cancelled) chatRestoredRef.current = true;
          return;
        }

        // If streaming, defer the heavy JSON parsing
        if (isOtherTabStreaming()) {
          scheduleHydration(() => { doHydration(); });
          return;
        }

        // Parse JSON - this is the blocking operation
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            // Strip stale system messages that should never persist across sessions.
            // Hotfix #2 (2026-05-13): also strip rows whose tag was lost by an older
            // sanitizer but whose text content matches the canonical low-balance
            // banner phrase — see `staleLowBalanceFilter.ts` header.
            const filteredParsed = parsed.filter((m: Record<string, unknown>) =>
              (m as any)?.sourceTag !== 'system:onboarding_welcome' &&
              !isStaleLowBalanceMessage_v20260513(m as any)
            );
            const restored = await Promise.all(
              filteredParsed.map(async (m: Record<string, unknown>) => {
                // PR-6 (2026-09-09): ONE revive mapper — every durable locator survives a
                // reload (`gcsPath` used to be dropped right here, so a cloud-ingest upload
                // lost its only locator on the next load), the URL's mint stamp rides along.
                const attachmentsSerialized: SerializedAttachment[] = Array.isArray((m as any)?.attachmentsSerialized)
                  ? (m as any).attachmentsSerialized
                      .map((a: unknown) => reviveSerializedAttachment(a))
                      .filter((a: SerializedAttachment | null): a is SerializedAttachment => a !== null)
                  : [];

                // Validate attachments: log warning if attachment relies only on storageUrl (no dataUrl)
                // This indicates cross-device restore where attachment requires network to download
                attachmentsSerialized.forEach((att) => {
                  const hasDataUrl = !!att.dataUrl;
                  const hasStorageUrl = !!att.storageUrl;
                  if (hasStorageUrl && !hasDataUrl) {
                    console.log(`[GlobalChat] Attachment "${att.name}" will be downloaded from Storage (no local dataUrl)`);
                  } else if (!hasStorageUrl && !hasDataUrl) {
                    console.warn(`[GlobalChat] Attachment "${att.name}" has no downloadable content (no storageUrl or dataUrl)`);
                  }
                });

                const attachments = attachmentsSerialized.length ? await deserializeAttachments(attachmentsSerialized) : undefined;
                const tokenUsageSnapshot = parseTokenUsageSnapshot((m as any)?.tokenUsageSnapshot);
                const tokenUsageSnapshots = parseTokenUsageSnapshots((m as any)?.tokenUsageSnapshots);

                return {
                  // Cross-view ⓘ fix (Miles 2026-07-12): preserve the message id
                  // and the RAW usage fields — the snapshot-only whitelist lost
                  // usage for any message persisted before the throttled
                  // snapshot conversion ran (finalize <2s before a view switch),
                  // making the popup show "no extensive compute" on rebuilt views.
                  id: (m as any)?.id ? String((m as any).id) : undefined,
                  tokenUsage: (m as any)?.tokenUsage,
                  tokenUsages: Array.isArray((m as any)?.tokenUsages) ? (m as any).tokenUsages : undefined,
                  text: String(m?.text ?? ''),
                  isUser: !!m?.isUser,
                  timestamp: m?.timestamp ? new Date(m.timestamp as string) : new Date(),
                  html: m?.html ? String(m.html) : undefined,
                  attachments,
                  attachmentsSerialized: attachmentsSerialized.length ? attachmentsSerialized : undefined,
                  tokenUsageSnapshot,
                  tokenUsageSnapshots,
                  sourceTag: (m as any)?.sourceTag ? String((m as any).sourceTag) : undefined,
                  workflowOutput: (m as any)?.workflowOutput ? {
                    type: (m as any).workflowOutput.type,
                    content: String((m as any).workflowOutput.content ?? ''),
                    title: (m as any).workflowOutput.title ? String((m as any).workflowOutput.title) : undefined,
                    timestamp: (m as any).workflowOutput.timestamp ? new Date((m as any).workflowOutput.timestamp as string) : new Date(),
                    sourceWorkflow: String((m as any).workflowOutput.sourceWorkflow ?? ''),
                  } : undefined,
                  // Preserve tags for test data identification
                  tags: Array.isArray((m as any)?.tags) ? (m as any).tags : undefined,
                  // Preserve messageType for special message rendering (e.g., payment forms)
                  messageType: (m as any)?.messageType,
                  // Preserve action buttons for SpecialMessageTile rendering after refresh
                  actionButtons: (m as any)?.actionButtons,
                  title: (m as any)?.title ? String((m as any).title) : undefined,
                  priority: (m as any)?.priority,
                  workflowActionButtonsHtml: (m as any)?.workflowActionButtonsHtml ? String((m as any).workflowActionButtonsHtml) : undefined,
                  // Data-only cards (connect offer/sign-in) live in payload —
                  // the reload dedupe guard reads it (2026-08-22).
                  payload: (m as any)?.payload || undefined,
                  selectedActionButtonIndex: (m as any)?.selectedActionButtonIndex,
                } as ChatMessage;
              })
            );

            const filtered = restored
              .filter((m) => m.text || m.html || m.messageType || m.workflowActionButtonsHtml || m.payload || (m.attachmentsSerialized && m.attachmentsSerialized.length) || (m.attachments && m.attachments.length))
              .sort((a: ChatMessage, b: ChatMessage) => {
                const at = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
                const bt = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
                return at - bt;
              });

          if (!cancelled) {
            // Initialize content-tracking ref so cross-tab sync doesn't re-process this data
            lastStorageValueRef.current = raw;
            // Point 6 (mid-stream hard-refresh data-loss fix): on the global
            // (non-thread) reload path — which the public homepage / guest chat
            // uses — an in-flight turn killed by a hard refresh rehydrates as a
            // dead "thinking" pending widget that can never finalize. Convert it
            // to a clearly-marked interrupted message here too (the thread path
            // is handled in useChatThreads). Pure + synchronous; no-op when none.
            const reconciled = reconcileInterruptedOnHydrate(filtered);
            const hydrated = reconciled.changed ? reconciled.messages : filtered;
            const dedupedHydrated = dedupeMessagesById(hydrated);
            const clearedHydrated = clearStaleInterruptedCards(dedupedHydrated);
            dispatch({
              type: 'SET_MESSAGES',
              payload: clearedHydrated.changed ? clearedHydrated.messages : dedupedHydrated,
            });
          }
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) chatRestoredRef.current = true;
      }
    };

    // Initial hydration - use requestIdleCallback if streaming to avoid blocking
    if (isOtherTabStreaming()) {
      scheduleHydration(() => { doHydration(); });
    } else {
      doHydration();
    }

    return () => {
      cancelled = true;
    };
  }, [config.persistHistory, storageKeys.CHAT_HISTORY, isAuthenticated, authHasResolved, config.enableThreadSwitcher]);

  // ── Welcome message injection (config-driven, fires once on fresh sessions) ──
  // Uses no dependency array because chatRestoredRef is a mutable ref that
  // doesn't trigger re-renders. The welcomeMessageInjectedRef guard ensures
  // the injection logic runs at most once per mount.
  useEffect(() => {
    // Set a 5-second abort after mount so we don't wait forever for async config
    const abortTimer = setTimeout(() => { welcomeMessageAbortRef.current = true; }, 5000);
    return () => clearTimeout(abortTimer);
  }, []);

  useEffect(() => {
    if (welcomeMessageInjectedRef.current) return;
    if (!chatRestoredRef.current) return;
    // Don't mark as done if we don't have a welcome message yet and the abort
    // timer hasn't fired — async tenant config may still be loading.
    if (!config.welcomeMessage) {
      if (!welcomeMessageAbortRef.current) return; // still waiting
      welcomeMessageInjectedRef.current = true;    // give up after 5s
      return;
    }
    // If there's existing chat history, skip the welcome but mark as done.
    if (state.messages.length > 0) {
      welcomeMessageInjectedRef.current = true;
      return;
    }
    // WRITE-THROUGH INJECTION (session-hardening S1, Miles 2026-07-12): a
    // pane-only welcome is doomed on threading surfaces — every later rebuild
    // derives from the per-thread STORE (addMessage reads storage, appends,
    // rewrites), so the un-persisted welcome vanished at the first send. The
    // welcome must be a REAL persisted first message of the active thread.
    // On threading surfaces, wait (within the same 5s abort budget) for the
    // storage hooks to wire and auth to settle so it lands in the RIGHT
    // thread's store under the RIGHT uid-scoped key; surfaces where the
    // switcher never wires (internal Prest0n) fall back to today's pane-only
    // injection when the abort fires.
    const readThreadMessages = readThreadMessagesRef.current;
    const writeThreadMessages = writeThreadMessagesRef.current;
    const getActiveTid = getActiveThreadIdRef.current;
    const threadingWired = !!(readThreadMessages && writeThreadMessages && getActiveTid);
    if (config.enableThreadSwitcher && !welcomeMessageAbortRef.current) {
      if (!threadingWired || !authHasResolved) return; // wait — effect re-runs every render
      // 2026-08-09 hotfix: an unsettled tenant/account config must defer the
      // injector too — its static-config fallback otherwise persists the
      // pre-load marketing copy (unranked, chip-less) into an account thread,
      // and a user message arriving before config settles freezes it there.
      // The existing 5s abort budget still guarantees a greeting eventually.
      if (config.welcomeReady === false) return;
    }
    // Personalized greeting (2026-07-13): when GlobalChatSystem wired the
    // composer, the mount welcome uses the SAME personalized copy freshly
    // minted threads get (grounded in the account's recent projects). The
    // composer is pure/synchronous, guarantees the no-history case returns
    // EXACTLY the configured welcome copy, and any absence/null/throw falls
    // back to the static config text — so the S1 write-through contract
    // below is unchanged either way.
    let personalizedGreeting: {
      text: string;
      html: string;
      tokenUsage?: TokenUsageSnapshot;
      greetingRank?: number;
      starters?: string[];
    } | null = null;
    try {
      personalizedGreeting = composeGreetingRef.current ? composeGreetingRef.current() : null;
    } catch {
      personalizedGreeting = null;
    }
    const welcome: ChatMessage = {
      id: 'welcome',
      text: personalizedGreeting?.text ?? config.welcomeMessage.text,
      html: personalizedGreeting?.html ?? config.welcomeMessage.html,
      isUser: false,
      timestamp: new Date(),
      sourceTag: 'welcome',
      // Multi-tab greeting parity (2026-08-09): rank + chips persist with the
      // copy (see greetingRank.ts / the ranked reconciliation in useChatThreads).
      ...(typeof personalizedGreeting?.greetingRank === 'number'
        ? { greetingRank: personalizedGreeting.greetingRank }
        : {}),
      ...(personalizedGreeting?.starters?.length
        ? { starters: [...personalizedGreeting.starters] }
        : {}),
      // AI-distilled greeting: the distillation usage powers the welcome
      // bubble's mandatory ⓘ (2026-07-13). Absent for static welcomes.
      ...(personalizedGreeting?.tokenUsage ? { tokenUsage: personalizedGreeting.tokenUsage } : {}),
    };
    if (threadingWired) {
      const tid = getActiveTid();
      if (tid) {
        const stored = readThreadMessages(tid) ?? [];
        // The store is the truth: an already-populated thread gets no welcome
        // (its history hydrates over the empty pane momentarily), and a
        // welcome already on disk must not duplicate.
        if (stored.length > 0) {
          welcomeMessageInjectedRef.current = true;
          const storedWelcome = stored.find(
            (m) => m?.id === 'welcome' || m?.sourceTag === 'welcome',
          );
          if (!storedWelcome) return;
          // Store holds a welcome (fresh thread, prior visit): surface the
          // PERSISTED copy — never a locally recomposed one (2026-08-09
          // multi-tab greeting parity: each tab re-composing over the stored
          // greeting is how the same thread greeted differently per window;
          // upgrades flow only through the ranked reconciliation in
          // useChatThreads, which persists first).
          dispatch({ type: 'ADD_MESSAGE', payload: storedWelcome });
          return;
        }
        welcomeMessageInjectedRef.current = true;
        writeThreadMessages(tid, [welcome]);
      }
    }
    welcomeMessageInjectedRef.current = true;
    dispatch({ type: 'ADD_MESSAGE', payload: welcome });
  });

  // SECURITY: Merge session messages with Firestore history on authentication
  // When a user types messages before logging in, we buffer them in sessionMessagesRef.
  // Once they authenticate, we merge those messages into the current state.
  // ConversationHistoryContext will then sync these to Firestore.
  useEffect(() => {
    // Only run when user becomes authenticated
    if (!isAuthenticated || !user?.id) return;

    // Check if we have buffered session messages to merge
    if (sessionMessagesRef.current.length === 0) return;

    // User just authenticated with pending session messages - merge them
    const sessionMessages = [...sessionMessagesRef.current];
    sessionMessagesRef.current = []; // Clear the buffer

    // Add session messages to state - they'll be merged with Firestore data
    // via ConversationHistoryContext's sync logic
    sessionMessages.forEach(msg => {
      dispatch({ type: 'ADD_MESSAGE', payload: msg });
    });

    console.log(`[GlobalChat] Merged ${sessionMessages.length} session message(s) after authentication`);
  }, [isAuthenticated, user?.id]);

  // Cross-tab/page sync for global chat history
  // This ensures all GlobalChatSystem instances stay in sync across different tabs and pages
  // Optimized to prevent blocking page navigation during streaming
  useEffect(() => {
    if (!config.persistHistory || !config.useGlobalHistory) return;
    // PR #97 — Bug #1 fix: when the per-thread switcher is on, the legacy
    // `CHAT_HISTORY` storage key is a parallel persistence path that
    // races with the per-thread localStorage writes. Under heavy
    // thread-switching, this poll can SET_MESSAGES with a stale
    // snapshot and blow away just-dispatched user messages on the
    // active thread. Per-thread storage already provides durability
    // (see useChatThreads), so disable the legacy poll entirely when
    // threading is enabled.
    if (config.enableThreadSwitcher) return;

    // Ref to track last known message count to avoid infinite update loops
    const lastMessageCountRef = { current: state.messages.length };
    let syncTimeout: number | null = null;
    let pollTimeoutId: number | null = null;

    // Check if another tab is actively streaming (to defer sync and avoid
    // blocking). Guard purpose unchanged: syncFromStorage must not fire
    // SET_MESSAGES over an in-memory streaming message before finalize().
    // The window question moved into ./streamingFlag: the lease heartbeat
    // keeps a REAL stream fresh through multi-minute stage waits, so the
    // short shared freshness window no longer expires mid-call.
    const isOtherTabStreaming = (): boolean => isAnyTabStreaming();

    // Use requestIdleCallback for non-critical syncs (with setTimeout fallback)
    // This allows navigation and other critical tasks to proceed first
    const scheduleIdleSync = (task: () => void) => {
      const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, opts?: { timeout: number }) => number);
      if (typeof ric === 'function') {
        ric(task, { timeout: 2000 }); // Max 2s delay before forcing execution
      } else {
        setTimeout(task, 50); // Small delay for responsiveness on older browsers
      }
    };

    // Parse stored history and update state if different
    const syncFromStorage = () => {
      // Skip sync if another tab is streaming - prevents blocking during active streams
      if (isOtherTabStreaming()) {
        return;
      }

      try {
        const raw = localStorage.getItem(storageKeys.CHAT_HISTORY);
        if (!raw) return;

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return;

        // Only update if the stored history content is different from what we last synced
        // Using raw string comparison (not just count) to detect content changes like
        // attachment URLs, token data, and button selection state from cross-device updates
        if (raw !== lastStorageValueRef.current) {
          const restored = parsed
            // Strip stale system messages that should never persist across sessions.
            // Hotfix #2 (2026-05-13): tag-less stale low-balance banners (legacy
            // sanitizer dropped the sourceTag) are caught by the text-content
            // fallback inside `isStaleLowBalanceMessage_v20260513`.
            .filter((m: Record<string, unknown>) =>
              (m as any)?.sourceTag !== 'system:onboarding_welcome' &&
              !isStaleLowBalanceMessage_v20260513(m as any)
            )
            .map((m: Record<string, unknown>) => ({
              // Cross-view ⓘ fix (Miles 2026-07-12): keep id + raw usage —
              // see the rehydrate mapper above for the failure mode.
              id: (m as any)?.id ? String((m as any).id) : undefined,
              tokenUsage: (m as any)?.tokenUsage,
              tokenUsages: Array.isArray((m as any)?.tokenUsages) ? (m as any).tokenUsages : undefined,
              text: String(m?.text ?? ''),
              isUser: !!m?.isUser,
              timestamp: m?.timestamp ? new Date(m.timestamp as string) : new Date(),
              html: m?.html ? String(m.html) : undefined,
              tokenUsageSnapshot: parseTokenUsageSnapshot((m as any)?.tokenUsageSnapshot),
              tokenUsageSnapshots: parseTokenUsageSnapshots((m as any)?.tokenUsageSnapshots),
              // PR-6 (2026-09-09): the same revive mapper as the hydrate above.
              attachmentsSerialized: Array.isArray((m as any)?.attachmentsSerialized)
                ? (m as any).attachmentsSerialized
                    .map((a: unknown) => reviveSerializedAttachment(a))
                    .filter((a: SerializedAttachment | null): a is SerializedAttachment => a !== null)
                : undefined,
              // Preserve tags for test data identification
              tags: Array.isArray((m as any)?.tags) ? (m as any).tags : undefined,
              // Preserve messageType for special message rendering (e.g., payment forms)
              messageType: (m as any)?.messageType,
              // Preserve fields that persist effect saves but were previously missing here
              sourceTag: (m as any)?.sourceTag ? String((m as any).sourceTag) : undefined,
              workflowOutput: (m as any)?.workflowOutput || undefined,
              actionButtons: (m as any)?.actionButtons || undefined,
              title: (m as any)?.title ? String((m as any).title) : undefined,
              priority: (m as any)?.priority || undefined,
              workflowActionButtonsHtml: (m as any)?.workflowActionButtonsHtml
                ? String((m as any).workflowActionButtonsHtml)
                : undefined,
              payload: (m as any)?.payload || undefined,
              selectedActionButtonIndex: (m as any)?.selectedActionButtonIndex,
              isContextOnly: (m as any)?.isContextOnly || undefined,
            }))
            .filter((m) => m.text || m.html || m.messageType || m.workflowActionButtonsHtml || m.payload || (m.attachmentsSerialized && m.attachmentsSerialized.length))
            .sort((a: ChatMessage, b: ChatMessage) => {
              const at = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
              const bt = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
              return at - bt;
            });

          lastStorageValueRef.current = raw;
          lastMessageCountRef.current = restored.length;
          // Point 6: cross-tab storage-restore path — same interrupted-turn
          // reconcile as the initial hydrate above (orphaned in-flight widget
          // -> recoverable interrupted message). Pure + no-op when none.
          const reconciledRestored = reconcileInterruptedOnHydrate(restored);
          const restoredAfterReconcile = reconciledRestored.changed ? reconciledRestored.messages : restored;
          const dedupedRestored = dedupeMessagesById(restoredAfterReconcile);
          const clearedRestored = clearStaleInterruptedCards(dedupedRestored);
          dispatch({
            type: 'SET_MESSAGES',
            payload: clearedRestored.changed ? clearedRestored.messages : dedupedRestored,
          });
        }
      } catch {
        // Silently ignore parse errors
      }
    };

    // Debounced sync to prevent rapid-fire updates during streaming
    const debouncedSync = () => {
      if (syncTimeout) {
        clearTimeout(syncTimeout);
      }
      syncTimeout = window.setTimeout(() => {
        scheduleIdleSync(syncFromStorage);
      }, 300); // 300ms debounce
    };

    // Handler for storage events from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKeys.CHAT_HISTORY) {
        debouncedSync();
      }
    };

    // Poll for changes using setTimeout (more controllable than setInterval)
    // This handles cases where user navigates between pages in the same tab
    const schedulePoll = () => {
      // Adaptive interval: slower when streaming detected to reduce blocking
      const streaming = isOtherTabStreaming();
      const interval = streaming ? 3000 : 1000;
      pollTimeoutId = window.setTimeout(() => {
        // Skip ALL work during streaming to prevent JSON parsing overhead
        // The streaming tab will update localStorage when done, triggering a storage event
        if (!isOtherTabStreaming()) {
          try {
            const raw = localStorage.getItem(storageKeys.CHAT_HISTORY);
            // Content-based comparison: detect any change (not just count)
            // so attachment URLs, token data, and button state propagate cross-device
            if (raw && raw !== lastStorageValueRef.current) {
              debouncedSync();
            }
          } catch {
            // Silently ignore
          }
        }
        schedulePoll();
      }, interval);
    };

    // Handler for force sync events (e.g., from test data cleanup,
    // or from ConversationHistoryContext when a Firestore snapshot
    // resolves with new content).
    //
    // Threading-mode guard (Miles 2026-05-12, Slack thread
    // C0AM8U804KZ/1778481193.723179): when the surface has the thread
    // switcher enabled, the legacy `prest0_global_chat_history`
    // localStorage key is NOT being written (see line ~1653 below),
    // so the payload arriving via this event is the snapshot of a
    // single shared bucket that does NOT include the active thread's
    // in-flight user/assistant bubbles. Wholesale-replacing
    // `state.messages` with that payload after a workflow card
    // finalize wipes the user bubble that was just appended in this
    // same tick — the exact "user bubble disappears after a workflow
    // card completes" repro Malaika reported.
    //
    // Per-thread persistence (`safelyPersistThreadMessages`) is the
    // single source of truth in threading mode, so we drop the
    // SET_MESSAGES dispatch entirely and let the per-thread mirror
    // effect own React state for the active thread. Single-thread
    // surfaces keep the legacy behaviour because that's where this
    // event was originally designed to plumb cross-tab Firestore
    // updates.
    const handleForceSync = (e: Event) => {
      if (config.enableThreadSwitcher) {
        // Threading owns persistence; ignore the global-bucket
        // snapshot to avoid wiping in-flight bubbles.
        return;
      }
      const customEvent = e as CustomEvent<{ messages: ChatMessage[] }>;
      if (!customEvent.detail?.messages) return;

      const incoming: ChatMessage[] = customEvent.detail.messages.map(m => ({
        ...m,
        timestamp: m.timestamp instanceof Date ? m.timestamp : new Date(m.timestamp as string),
      }));

      // ────────────────────────────────────────────────────────────────────
      // Hotfix #2 (2026-05-13, Malaika live repro on Fernando's tenant) —
      // user bubble disappears after `handleSend` round-trip on
      // single-thread surfaces.
      //
      // Trace
      // ─────
      // 1. handleSend → addMessage → ADD_MESSAGE → state.messages ←
      //    [..., userBubble("Hello")].
      // 2. ConversationHistoryContext.onConversationHistorySnapshot fires
      //    on auth/hydrate (Firestore doc lags behind in-memory state by
      //    the throttled persist interval).
      // 3. Its merge filter only preserves messages tagged
      //    pending/streaming/thinking from localStorage — a plain user
      //    "Hello" bubble has no such tag, so the merged payload it
      //    dispatches via `global-chat-force-sync` is firestore-only and
      //    omits the just-sent bubble.
      // 4. Pre-fix `handleForceSync` wholesale-replaced
      //    `state.messages` with that payload via SET_MESSAGES,
      //    wiping the user bubble from React state.
      //
      // PR #144 closed this for threading-mode surfaces by returning
      // early when `enableThreadSwitcher` is true. Landing-page /
      // marketing / unauthed surfaces have `enableThreadSwitcher` false,
      // so the guard never fired there and the regression remained for
      // every non-threaded surface (including the Fernando login flow
      // that initially routes through `/`).
      //
      // Surgical fix
      // ────────────
      // MERGE the incoming payload with any in-flight in-memory
      // messages that are NOT yet represented in the snapshot. Two
      // matching rules:
      //   (a) by `id` when both sides have one
      //   (b) by a content fingerprint (isUser+text+timestamp-ms) as a
      //       fallback for legacy rows without ids
      // Only "recent" in-memory rows (timestamp >= now - 5 min) are
      // preserved so we don't leak rows the source-of-truth has
      // intentionally pruned across a long absence. The 5-minute
      // window comfortably covers any practical Firestore propagation
      // / debounce interval (the throttled persist runs every 2s; the
      // ConvHistory poll runs every 2s; Firestore latency is sub-second).
      // ────────────────────────────────────────────────────────────────────
      const fingerprint = (m: ChatMessage): string => {
        const ts = m.timestamp instanceof Date
          ? m.timestamp.getTime()
          : new Date(m.timestamp as unknown as string).getTime();
        return `${m.isUser ? 'u' : 'a'}|${(m.text || '').slice(0, 200)}|${ts}`;
      };

      const incomingIds = new Set<string>();
      const incomingFingerprints = new Set<string>();
      for (const m of incoming) {
        if (m.id) incomingIds.add(m.id);
        incomingFingerprints.add(fingerprint(m));
      }

      const RECENT_WINDOW_MS = 5 * 60 * 1000;
      const now = Date.now();
      const current = messagesSyncRef.current || [];
      const preserved: ChatMessage[] = [];
      for (const m of current) {
        // Already in payload? (id match wins; fingerprint as fallback)
        if (m.id && incomingIds.has(m.id)) continue;
        if (incomingFingerprints.has(fingerprint(m))) continue;
        const ts = m.timestamp instanceof Date
          ? m.timestamp.getTime()
          : new Date(m.timestamp as unknown as string).getTime();
        if (!Number.isFinite(ts)) continue;
        if (ts < now - RECENT_WINDOW_MS) continue;
        preserved.push(m);
      }

      let merged: ChatMessage[];
      if (preserved.length === 0) {
        merged = incoming;
      } else {
        merged = [...incoming, ...preserved].sort((a, b) => {
          const ta = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp as unknown as string).getTime();
          const tb = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp as unknown as string).getTime();
          return ta - tb;
        });
      }

      // BUG-FIX (mid-stream refresh QA regression, 2026-07): the concat above
      // can carry the SAME turn twice (once from `incoming`/storage, once from
      // `preserved`/in-memory) under the SAME message id — dedupe before this
      // ever reaches the reducer so the same turn can never double-render.
      const deduped = dedupeMessagesById(merged);
      // Point 6: force-sync merge path — reconcile orphaned in-flight widgets
      // surfaced from storage so they never render as a dead spinner.
      const reconciledMerged = reconcileInterruptedOnHydrate(deduped);
      const workingMessages = reconciledMerged.changed ? reconciledMerged.messages : deduped;
      // BUG-FIX: drop any stale "Interrupted" card whose turn actually
      // finished (a completed answer for the same preceding prompt or
      // serverTaskId exists later in the array) so the false Regenerate
      // card never persists above the real answer.
      const cleared = clearStaleInterruptedCards(workingMessages);
      const mergedForDispatch = cleared.changed ? cleared.messages : workingMessages;
      lastMessageCountRef.current = mergedForDispatch.length;
      dispatch({ type: 'SET_MESSAGES', payload: mergedForDispatch });
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('global-chat-force-sync', handleForceSync);
    schedulePoll();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('global-chat-force-sync', handleForceSync);
      if (pollTimeoutId) clearTimeout(pollTimeoutId);
      if (syncTimeout) clearTimeout(syncTimeout);
    };
  }, [config.persistHistory, config.useGlobalHistory, config.enableThreadSwitcher, storageKeys.CHAT_HISTORY, state.messages.length]);

  // Persist chat history (including attachments)
  // Throttled to prevent blocking during rapid streaming updates
  useEffect(() => {
    if (!chatRestoredRef.current || !config.persistHistory) return;
    // PR fix/thread-history-loss-and-multi-tab-sync: skip the legacy
    // `CHAT_HISTORY` persist when threading is on. Each thread now
    // owns its own per-thread storage key (see `useChatThreads` +
    // `safelyPersistThreadMessages`). Writing the active thread's
    // `state.messages` to the legacy global key cross-pollinates
    // across threads (the global key reflects whichever thread was
    // most recently active) and re-opens the cross-tab regression
    // PR #97 was already mitigating. The per-thread persist path
    // is now the canonical durability vector under threading mode;
    // the legacy global key is left untouched so it cannot become
    // a parallel write race.
    if (config.enableThreadSwitcher) return;

    const PERSIST_THROTTLE_MS = 2000; // Persist every 2s to reduce I/O during streaming (up from 500ms for performance)
    const now = Date.now();
    const timeSinceLastPersist = now - lastPersistTimeRef.current;

    // Core persist function
    const doPersist = async () => {
      let cancelled = false;
      try {
        const payload = await Promise.all(
          state.messages.map(async (m) => {
            let attachmentsSerialized = m.attachmentsSerialized;

            // If we don't yet have serialized attachments but we do have File objects, serialize them
            if (!attachmentsSerialized && m.attachments?.length) {
              try {
                attachmentsSerialized = await serializeAttachments(m.attachments);
              } catch {
                attachmentsSerialized = undefined;
              }
            }

            const tokenUsageSnapshot = buildTokenUsageSnapshot(m);
            const tokenUsageSnapshots = buildTokenUsageSnapshots(m);

            return {
              text: m.text,
              isUser: m.isUser,
              timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
              html: m.html,
              attachmentsSerialized,
              tokenUsageSnapshot,
              tokenUsageSnapshots,
              sourceTag: m.sourceTag,
              workflowOutput: m.workflowOutput,
              isContextOnly: m.isContextOnly, // Persist context-only flag for document analysis
              tags: m.tags, // Persist tags for test data cleanup
              messageType: m.messageType, // Persist messageType for special message rendering (e.g., payment)
              actionButtons: m.actionButtons, // Persist action buttons for SpecialMessageTile rendering
              title: m.title, // Persist title for SpecialMessageTile header
              priority: m.priority, // Persist priority for message ordering
              workflowActionButtonsHtml: m.workflowActionButtonsHtml, // Persist workflow button HTML
              payload: m.payload, // Persist data-only card payloads (connect offers, 2026-08-22)
              selectedActionButtonIndex: m.selectedActionButtonIndex, // Persist selected button for visual state
            };
          })
        );

        if (!cancelled) {
          lastPersistTimeRef.current = Date.now();

          // Proactively strip dataUrl from all serialized attachments BEFORE persisting.
          // Base64 data URLs for PDFs/images are typically 1-10MB each; with 13 files that's
          // 50-100MB+ which vastly exceeds the ~5MB localStorage quota. The dataUrl is only
          // useful for in-memory rendering during the current session — storageUrl (if present)
          // is the durable cross-device reference, and for non-uploaded files the data is
          // already sent to the VM and doesn't need to survive page reload.
          // FIX (MT-QA-BUG-SYNC-NEEDED): ONLY strip dataUrl if storageUrl is present.
          // If the cloud upload failed (e.g. Firebase rules/quota), dataUrl is the ONLY
          // way to re-download the file. Stripping it breaks the attachment completely ("sync needed").
          const persistPayload = payload.map((msg: any) => {
            if (!msg.attachmentsSerialized?.length) return msg;
            return {
              ...msg,
              attachmentsSerialized: msg.attachmentsSerialized.map((att: any) => {
                if (att.dataUrl && att.storageUrl) {
                  const { dataUrl: _stripped, ...rest } = att;
                  return rest;
                }
                return att;
              }),
            };
          });

          try {
            localStorage.setItem(storageKeys.CHAT_HISTORY, JSON.stringify(persistPayload));
          } catch (storageError) {
            // Fallback: if still too large, log the error (shouldn't happen after stripping dataUrl)
            if (storageError instanceof DOMException && storageError.name === 'QuotaExceededError') {
              console.warn('[useGlobalChat] localStorage quota exceeded even after stripping dataUrl');
              // Last resort: strip attachmentsSerialized entirely and large fields.
              // Data-only cards KEEP their identity + payload + card html (P0
              // thread-durability, 2026-09-02): the quota class is attachments,
              // never the ~4 KB of a card, and dropping payload/html here turned
              // the card into an empty assistant message that the next hydrate
              // filtered away — the connect tile vanished with no breadcrumb.
              const minimalPayload = persistPayload.map((msg: any) => ({
                text: msg.text,
                isUser: msg.isUser,
                timestamp: msg.timestamp,
                html: msg.html,
                sourceTag: msg.sourceTag,
                ...(msg.payload
                  ? {
                    id: msg.id,
                    payload: msg.payload,
                    workflowActionButtonsHtml: msg.workflowActionButtonsHtml,
                    isContextOnly: msg.isContextOnly,
                  }
                  : {}),
              }));
              try {
                localStorage.setItem(storageKeys.CHAT_HISTORY, JSON.stringify(minimalPayload));
                console.log('[useGlobalChat] Minimal persist succeeded (attachments dropped)');
              } catch {
                // The card-bearing minimal payload STILL did not fit. Cards are
                // ~4 KB each, so this needs a pathological transcript — but
                // losing the whole history to save the cards would be a worse
                // trade than the one this rung exists to make. Fall back to the
                // text-only shape (the pre-2026-09-02 behaviour) before giving up.
                try {
                  localStorage.setItem(storageKeys.CHAT_HISTORY, JSON.stringify(persistPayload.map((msg: any) => ({
                    text: msg.text,
                    isUser: msg.isUser,
                    timestamp: msg.timestamp,
                    html: msg.html,
                    sourceTag: msg.sourceTag,
                  }))));
                  console.warn('[useGlobalChat] Text-only persist succeeded (cards dropped — payload rebuild unavailable for this surface)');
                } catch {
                  console.error('[useGlobalChat] localStorage persist failed even with minimal payload');
                }
              }
            }
          }
        }
      } catch {
        // ignore outer errors (serialization failures, etc.)
      }
    };

    // Throttle: if we persisted recently, schedule a deferred persist
    if (timeSinceLastPersist < PERSIST_THROTTLE_MS) {
      // Clear any existing pending persist
      if (pendingPersistRef.current) {
        clearTimeout(pendingPersistRef.current);
      }
      // Schedule deferred persist to ensure final state is saved
      const delay = PERSIST_THROTTLE_MS - timeSinceLastPersist;
      pendingPersistRef.current = window.setTimeout(() => {
        pendingPersistRef.current = null;
        doPersist();
      }, delay);
      return () => {
        if (pendingPersistRef.current) {
          clearTimeout(pendingPersistRef.current);
          pendingPersistRef.current = null;
        }
      };
    }

    // Not throttled - persist immediately
    doPersist();
    return undefined;
  }, [state.messages, config.persistHistory, config.enableThreadSwitcher, storageKeys.CHAT_HISTORY]);

  // ============================================================================
  // Ensure attachment data reaches localStorage before page closes
  // This guards against the race where finalizeStreamingMessageById updates
  // React state but the persist effect hasn't fired yet when the user navigates away.
  // ============================================================================
  useEffect(() => {
    if (!config.persistHistory) return;
    // Same threading carve-out as the throttled persist above — the
    // beforeunload writer is just a synchronous-as-possible mirror
    // of the same legacy `CHAT_HISTORY` payload, and under threading
    // mode each thread owns its own per-thread key.
    if (config.enableThreadSwitcher) return;

    const handleBeforeUnload = () => {
      // Always do a synchronous-as-possible persist on unload to capture final state
      // The persist effect may be throttled, so this ensures we don't lose attachment data
      try {
        const payload = state.messages.map((m) => ({
          text: m.text,
          isUser: m.isUser,
          timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
          html: m.html,
          attachmentsSerialized: m.attachmentsSerialized,
          // Build snapshots from the message (falls back to m.tokenUsages) instead of
          // reading the possibly-unbuilt m.tokenUsageSnapshot(s). Fixes deep-research
          // report cost vanishing on reload (Point 5, 2026-06-02).
          tokenUsageSnapshot: buildTokenUsageSnapshot(m) ?? m.tokenUsageSnapshot,
          tokenUsageSnapshots: buildTokenUsageSnapshots(m) ?? m.tokenUsageSnapshots,
          sourceTag: m.sourceTag,
          workflowOutput: m.workflowOutput,
          isContextOnly: m.isContextOnly,
          tags: m.tags,
          messageType: m.messageType,
          actionButtons: m.actionButtons,
          title: m.title,
          priority: m.priority,
          workflowActionButtonsHtml: m.workflowActionButtonsHtml,
          payload: m.payload,
          selectedActionButtonIndex: m.selectedActionButtonIndex,
        }));
        localStorage.setItem(storageKeys.CHAT_HISTORY, JSON.stringify(payload));
        console.log('[GlobalChat] beforeunload: persisted', payload.length, 'messages');
      } catch (e) {
        console.warn('[GlobalChat] beforeunload persist failed:', e);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.messages, config.persistHistory, config.enableThreadSwitcher, storageKeys.CHAT_HISTORY]);

  // ============================================================================
  // Cross-tab/page sync for prompt draft (global draft)
  // Optimized to prevent blocking page navigation
  // ============================================================================
  useEffect(() => {
    if (!config.persistDraft) return;

    let syncTimeout: number | null = null;
    let pollTimeoutId: number | null = null;

    // Use requestIdleCallback for non-critical syncs (with setTimeout fallback)
    const scheduleIdleSync = (task: () => void) => {
      const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, opts?: { timeout: number }) => number);
      if (typeof ric === 'function') {
        ric(task, { timeout: 1000 }); // Max 1s delay for draft sync
      } else {
        setTimeout(task, 50);
      }
    };

    const syncDraftFromStorage = () => {
      try {
        // Don't sync if user is actively typing (input is focused)
        if (promptFocusedRef.current) return;

        // Suppress draft rehydration for a short window after send -- see
        // JUST_SENT_DRAFT_SUPPRESS_MS at module top. Another component still
        // mounted on the same page (e.g. LandingPage hero composer) may
        // re-write the just-sent text to the global CHAT_DRAFT key from its
        // own stale state ~500 ms after our synchronous remove. Without this
        // guard the next poll dispatches SET_PROMPT_HTML and repopulates the
        // composer with the just-sent prompt (Malaika 2026-05-14 repro).
        //
        // BUG-DR-COMPOSER-REPOP-LONG-TURN (2026-05-20): the 1500 ms window
        // alone is too short for Deep Research turns (5-8 min) — by the time
        // the response lands the window has long expired and the
        // LandingPage debounced persist effect can re-write the just-sent
        // text. shouldSuppressDraftRehydrate() composes the 1500 ms window
        // with the entire in-flight turn + a 1500 ms post-flight cooldown.
        if (shouldSuppressDraftRehydrate()) return;

        const raw = localStorage.getItem(storageKeys.CHAT_DRAFT);
        // Skip if no stored draft or if it matches what we last saved
        if (!raw || raw === draftLastSavedRef.current) return;

        const parsed = JSON.parse(raw) as { html?: string; text?: string };
        const safeHtml = parsed?.html ? sanitizeHtmlInput(parsed.html) : '';
        const plain = parsed?.text ?? (safeHtml ? htmlToPlainText(safeHtml) : '');

        // FH-QA composer-refill race on LONG NON-attachment prompts
        // (2026-05-21 16:25 UTC): content-domain sentinel check — if the
        // CHAT_DRAFT body matches the just-sent sentinel, treat the
        // stored draft as a stale resurrection (some other composer in
        // the same browser tab rewrote it during a window the
        // time-domain gate let slip) and refuse to dispatch it into
        // composer state. HEAL the key too (2026-08-12 sticky-composer
        // round): skipping alone left the poisoned payload in storage to
        // strike after the 60 s sentinel expired — next poll, another
        // tab, or tomorrow's page-load hydrate.
        if (draftIsSentTextMatch(plain, safeHtml)) {
          try { localStorage.removeItem(storageKeys.CHAT_DRAFT); } catch {}
          draftLastSavedRef.current = null;
          return;
        }

        // Update our tracking ref
        draftLastSavedRef.current = raw;
        dispatch({ type: 'SET_PROMPT_HTML', payload: safeHtml });
        dispatch({ type: 'SET_PROMPT', payload: plain });
        // Update DOM input (only if not focused)
        requestAnimationFrame(() => {
          if (promptInputRef.current && !promptFocusedRef.current) {
            promptInputRef.current.innerHTML = safeHtml || plain || '';
            try {
              const range = document.createRange();
              const sel = window.getSelection();
              range.selectNodeContents(promptInputRef.current);
              range.collapse(false);
              sel?.removeAllRanges();
              sel?.addRange(range);
            } catch {}
          }
        });
      } catch {
        // ignore
      }
    };

    // Debounced sync to prevent rapid-fire updates
    const debouncedSyncDraft = () => {
      if (syncTimeout) {
        clearTimeout(syncTimeout);
      }
      syncTimeout = window.setTimeout(() => {
        scheduleIdleSync(syncDraftFromStorage);
      }, 300); // 300ms debounce
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKeys.CHAT_DRAFT) {
        debouncedSyncDraft();
      }
    };

    // Poll using setTimeout (more controllable than setInterval)
    const schedulePoll = () => {
      pollTimeoutId = window.setTimeout(() => {
        try {
          // Same suppression as syncDraftFromStorage -- bail before we
          // schedule a debouncedSync that would fire after another component
          // re-writes the just-sent text to CHAT_DRAFT. See
          // BUG-DR-COMPOSER-REPOP-LONG-TURN for the in-flight gate.
          if (shouldSuppressDraftRehydrate()) {
            schedulePoll();
            return;
          }
          const raw = localStorage.getItem(storageKeys.CHAT_DRAFT);
          if (raw && raw !== draftLastSavedRef.current) {
            debouncedSyncDraft();
          }
        } catch {
          // ignore
        }
        schedulePoll();
      }, 1000); // Increased from 500ms to 1000ms
    };

    window.addEventListener('storage', handleStorageChange);
    schedulePoll();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (pollTimeoutId) clearTimeout(pollTimeoutId);
      if (syncTimeout) clearTimeout(syncTimeout);
    };
  }, [config.persistDraft, storageKeys.CHAT_DRAFT, promptInputRef]);

  // Hydrate draft
  useEffect(() => {
    if (promptRestoredRef.current || !config.persistDraft) return;

    // Same suppression -- covers the cross-tab StrictMode remount case
    // where the hook re-mounts within the post-send/in-flight/cooldown
    // window and would otherwise re-hydrate from a CHAT_DRAFT value an
    // adjacent component re-wrote after our handleSend cleared it. We DO
    // NOT flip promptRestoredRef here so a legitimate later hydrate can
    // still run. See BUG-DR-COMPOSER-REPOP-LONG-TURN.
    if (shouldSuppressDraftRehydrate()) return;

    try {
      const raw = localStorage.getItem(storageKeys.CHAT_DRAFT);
      if (raw) {
        const parsed = JSON.parse(raw) as { html?: string; text?: string };
        const safeHtml = parsed?.html ? sanitizeHtmlInput(parsed.html) : '';
        const plain = parsed?.text ?? (safeHtml ? htmlToPlainText(safeHtml) : '');
        // FH-QA composer-refill race on LONG NON-attachment prompts
        // (2026-05-21 16:25 UTC): content-domain sentinel check on
        // hydrate-on-mount. If the stored draft body matches the
        // just-sent sentinel (StrictMode remount, fast-tab-switch,
        // hot-reload within 60 s of send), refuse to restore — and HEAL
        // the key (2026-08-12) so the resurrection can't outlive the
        // sentinel window in storage.
        if (draftIsSentTextMatch(plain, safeHtml)) {
          try { localStorage.removeItem(storageKeys.CHAT_DRAFT); } catch {}
          promptRestoredRef.current = true;
          return;
        }
        if (safeHtml || plain) {
          dispatch({ type: 'SET_PROMPT_HTML', payload: safeHtml });
          dispatch({ type: 'SET_PROMPT', payload: plain });
          
          // Restore draft content without forcing focus to avoid auto-opening UX
          requestAnimationFrame(() => {
            if (promptInputRef.current) {
              promptInputRef.current.innerHTML = safeHtml || plain || '';
            }
          });
        }
      }
    } catch {}
    promptRestoredRef.current = true;
  }, [config.persistDraft, storageKeys.CHAT_DRAFT]);

  // ============================================================================
  // Viewport Detection
  // ============================================================================

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 820px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      const mobile = 'matches' in e ? e.matches : (e as MediaQueryList).matches;
      dispatch({ type: 'SET_MOBILE_VIEWPORT', payload: mobile });
      if (mobile && state.isChatDockedRight && !config.forceDockButtonOnMobile) {
        dispatch({ type: 'SET_CHAT_DOCKED_RIGHT', payload: false });
      }
    };
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [state.isChatDockedRight]);

  // ============================================================================
  // Focus Management
  // ============================================================================
  // NOTE: Automatic focus on page load/navigation is intentionally disabled.
  // Focus only occurs when the user explicitly clicks into the prompt input.

  // ============================================================================
  // Attachment Panel Management
  // ============================================================================

  useEffect(() => {
    if (state.attachments.length === 0 && state.isAttachmentPanelVisible) {
      dispatch({ type: 'SET_ATTACHMENT_PANEL_VISIBLE', payload: false });
    }
  }, [state.attachments.length, state.isAttachmentPanelVisible]);

  useEffect(() => {
    const prev = prevAttachmentCountRef.current;
    if (state.attachments.length > prev) {
      dispatch({ type: 'SET_USER_COLLAPSED_ATTACHMENTS', payload: false });
    }
    prevAttachmentCountRef.current = state.attachments.length;
  }, [state.attachments.length]);

  // ============================================================================
  // Scroll Management (2026-08 redesign — shared pinned-to-bottom controller)
  // ============================================================================
  //
  // All transcript scroll behavior lives in the shared useTranscriptScroll
  // controller (src/hooks/useTranscriptScroll.ts) so every chat surface ships
  // the exact same policy: the pinned-to-bottom state machine (small bottom
  // band, not the old 600px), immediate unpin on upward user intent, the
  // "N new"/unseen pills while unpinned, per-thread IN-MEMORY reading
  // positions restored on switch, ResizeObserver re-stick for growth that
  // changes no message identity (follow-up chips resolving, image loads,
  // keyboard resizes), and the [data-msg-nav] step navigation.
  //
  // There is intentionally NO localStorage scroll persistence anymore — the
  // old CHAT_SCROLL blob was one global key shared across threads AND
  // surfaces (LandingPage's demo chat wrote the same key) and cross-clobbered
  // restores. A page load always opens at the bottom of the active thread,
  // which also guarantees the follow-up chips are visible.

  const {
    showScrollToBottom,
    isPinnedNow,
    isNearTop,
    newMessageCount,
    hasUnseenActivity,
    isAtThreadTop,
    isAtThreadBottom,
    scrollToBottom,
    pinToBottom,
    scrollToTopAnimated,
    stepPosition,
    stepToPreviousMessage,
    stepToNextMessage,
  } = useTranscriptScroll({
    containerRef: chatMessagesRef,
    contentRef: transcriptContentRef,
    items: state.messages,
    // Thread-isolation bridge: the store's SYNCHRONOUS accessor, so a thread
    // switch is visible to the controller in the same commit as SET_MESSAGES
    // (state-based ids lag a transition — see the bridge note in
    // GlobalChatSystem.tsx).
    getContextKey: () => (getActiveThreadIdRef.current ? getActiveThreadIdRef.current() : null),
    isOpen: state.isChatOpen,
  });

  // ============================================================================
  // Click Outside Handler
  // ============================================================================

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!state.isChatOpen) return;
      // DOCKED = PINNED (owner directive 2026-08-22): the docked-right pane
      // stays open while the user clicks and navigates anywhere in the app —
      // pages, org switches, everything. Only the explicit ✕ / mode buttons
      // close or move it. Outside-click auto-close is a FLOATING-overlay
      // behavior only.
      if (state.isChatDockedRight) return;
      const target = event.target as Node | null;
      if (!target || !(target instanceof Element)) return;

      const insideChat = chatContainerRef.current?.contains(target);
      const insidePrompt = promptBarRef.current?.contains(target);
      const insideFloating = floatingPromptBarRef.current?.contains(target);
      // Round O (O2): shared predicate (portalClickGuard) — body-portaled chat
      // surfaces (token ⓘ popups, tile popups) are part of the chat, never
      // "outside" it. Same rule GlobalChatSystem's popup-dismiss handler uses.
      const insidePortal = isInsidePrest0nPortal(target);

      if (!insideChat && !insidePrompt && !insideFloating && !insidePortal) {
        dispatch({
          type: 'SET_LAST_ATTACHMENT_VISIBLE_ON_HIDE',
          payload: state.isAttachmentPanelVisible && !state.userCollapsedAttachments && state.attachments.length > 0,
        });
        dispatch({ type: 'SET_CHAT_OPEN', payload: false });
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [state.isChatOpen, state.isChatDockedRight, state.isAttachmentPanelVisible, state.userCollapsedAttachments, state.attachments.length]);

  // ============================================================================
  // Round J (2026-07-14) — send-ack watchdog + auth-hydration deferral infra
  // ============================================================================

  // Watchdog: every dispatched turn must produce a turn-scoped ack (streaming
  // started / assistant-system message appended / processing released by a
  // terminal) within DEFAULT_SEND_ACK_TIMEOUT_MS, or the timeout handler
  // releases the thread and appends a visible retryable error bubble.
  // Handler is indirected through a ref because stopProcessing /
  // appendSendFailureBubble are defined later in the hook body.
  const sendAckTimeoutHandlerRef = useRef<(threadId: string | null, entry: { text: string }) => void>(
    () => undefined,
  );
  // Round J.1 (Finding 1): late-ack reconciliation handler — indirected the
  // same way as the timeout handler (strip helper is defined later).
  const sendAckLateAckHandlerRef = useRef<(threadId: string | null, entry: SendAckEntry) => void>(
    () => undefined,
  );
  const sendAckWatchdogRef = useRef<SendAckWatchdog | null>(null);
  if (!sendAckWatchdogRef.current) {
    sendAckWatchdogRef.current = createSendAckWatchdog({
      onTimeout: (threadId, entry) => sendAckTimeoutHandlerRef.current(threadId, entry),
      onLateAck: (threadId, entry) => sendAckLateAckHandlerRef.current(threadId, entry),
    });
  }
  const armSendAckWatchdog = useCallback((threadId: string | null, text: string, timeoutMs?: number, turnKey?: string) => {
    // Finding 3 (2026-07-15): optional per-arm window for dispatches whose
    // legitimate turn outlives the 25s default (guest chat arms with
    // GUEST_SEND_ACK_TIMEOUT_MS). Slice 1 (2026-08-19): a parallel-
    // authorized second dispatch arms a PER-TURN slot (turnKey) so it can
    // never cancel the first turn's window.
    sendAckWatchdogRef.current?.arm(threadId, text, timeoutMs, turnKey);
  }, []);
  // Round J.1 (Finding 1b): re-evaluation tick for queue drains held by a
  // fired watchdog. Bumped when a hold clears via late ack (which may not
  // otherwise re-render, e.g. a cross-thread addMessage) and by the drain
  // effect's own expiry timer.
  const [drainHoldTick, setDrainHoldTick] = useState(0);
  const bumpDrainHoldTick = useCallback(() => setDrainHoldTick((t) => t + 1), []);
  // Finding 8c (2026-07-15): thread delete/archive must disarm that thread's
  // watchdog (and drop its late-ack hold) so a fire can't append a retry
  // bubble into — or hold the queue of — a thread that no longer exists.
  const cancelThreadSendWatchdog = useCallback((threadId: string | null) => {
    sendAckWatchdogRef.current?.cancelThread(threadId);
  }, []);
  useEffect(() => () => { sendAckWatchdogRef.current?.cancelAll(); }, []);

  // Auth-hydration deferral (Round J addendum): a signed-in user can click a
  // starter chip before the auth context hydrates — globalTools.isAuthorized
  // is momentarily false. Those sends must eventually SEND, not error. The
  // refs below give the async deferral loop access to LATEST auth/gate state
  // (the dispatching closure is frozen at click time).
  const authHasResolvedRef = useRef<boolean>(authHasResolved);
  useEffect(() => { authHasResolvedRef.current = authHasResolved; }, [authHasResolved]);
  const globalToolsAuthorizedRef = useRef<boolean>(globalTools.isAuthorized);
  useEffect(() => { globalToolsAuthorizedRef.current = globalTools.isAuthorized; });
  const sendProgrammaticMessageRef = useRef<
    ((text: string, options?: ProgrammaticSendOptions) => Promise<void>) | null
  >(null);
  // Finding 8a (2026-07-15): unmount cancellation for in-flight auth
  // deferrals. The deferral loop polls with awaited timeouts that outlive an
  // unmounted hook instance; flipping this flag makes every pending deferral
  // exit without re-dispatching (and the resend ref is nulled so a straggler
  // can't dispatch through a dead instance's envelope).
  const authDeferralCancelledRef = useRef(false);
  useEffect(() => () => {
    authDeferralCancelledRef.current = true;
    sendProgrammaticMessageRef.current = null;
  }, []);

  const AUTH_SETTLE_MAX_WAIT_MS = 8000;
  const AUTH_SETTLE_POLL_MS = 150;
  const AUTH_SETTLE_GRACE_MS = 1000;
  // Waits for Firebase auth to settle (plus a short grace window for the
  // auth context to propagate into fresh render closures), then re-dispatches
  // through the LATEST safe envelope with skipUserBubble so the already-
  // appended user bubble is never duplicated. If auth settles signed-out,
  // the re-dispatch's own gate surfaces the sign-in prompt.
  //
  // Finding 2 (2026-07-15): the deferral carries the ORIGINATING thread id
  // captured at defer time. The old code recomputed the thread from the
  // ACTIVE thread at settle time, so a send deferred on thread A while the
  // user switched to thread B re-dispatched on B — cross-thread misdelivery
  // (plus an eventual double-send when A's orphaned watchdog fired). The
  // re-dispatch is pinned via forceThreadId so it lands where the user
  // bubble and the armed watchdog already live.
  const deferSendUntilAuthSettles = useCallback(async (
    text: string,
    options: { hidden?: boolean },
    originatingThreadId: string | null,
  ) => {
    const start = Date.now();
    while (
      Date.now() - start < AUTH_SETTLE_MAX_WAIT_MS
      && !globalToolsAuthorizedRef.current
      && !authHasResolvedRef.current
      && !authDeferralCancelledRef.current
    ) {
      await new Promise((r) => setTimeout(r, AUTH_SETTLE_POLL_MS));
    }
    const graceEnd = Date.now() + AUTH_SETTLE_GRACE_MS;
    while (
      Date.now() < graceEnd
      && !globalToolsAuthorizedRef.current
      && !authDeferralCancelledRef.current
    ) {
      await new Promise((r) => setTimeout(r, AUTH_SETTLE_POLL_MS));
    }
    if (authDeferralCancelledRef.current) return;
    const resend = sendProgrammaticMessageRef.current;
    if (resend) {
      await resend(text, {
        ...options,
        skipUserBubble: true,
        authRetry: true,
        forceThreadId: originatingThreadId,
      } as InternalProgrammaticSendOptions);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // Handler Functions
  // ============================================================================

  const addMessage = useCallback((message: ChatMessage, explicitOriginatingThreadId?: string | null): ChatMessage => {
    // Validate content - prevent blank messages
    const hasContent = (message.text && message.text.trim().length > 0) ||
                       (message.html && message.html.trim().length > 0);

    if (!hasContent) {
      console.warn('[GlobalChat] Attempted to add message with empty content', {
        isUser: message.isUser,
        sourceTag: (message as any)?.sourceTag,
      });

      // If it's a system/assistant message with no content, provide fallback.
      // NEVER the old bare "Message received." next to a real card (owner
      // repro 2026-08-14: post-connect scope card rendered as "Message
      // received." + button): a payload-bearing message gets situational
      // copy, and a card-bearing message needs no filler at all — the card
      // IS the content.
      if (!message.isUser) {
        const payloadKind = String((message as any)?.payload?.kind || '');
        const payloadData = (message as any)?.payload?.data || {};
        const hasActionCard = Boolean((message as any)?.workflowActionButtonsHtml);
        if (payloadKind === 'composio.connection_complete' && payloadData?.displayName) {
          const name = String(payloadData.displayName);
          const line = payloadData?.isStorageToolkit
            ? `${name} is connected — you're all set. I can use everything in it by default; use the button below to choose specific folders or change access anytime.`
            : `${name} is connected — you're all set. You can change what I can reach anytime on the Integrations page.`;
          console.log('[GlobalChat] Empty connection-complete message — using situational copy');
          message.text = message.text || line;
          message.html = message.html || `<p>${line}</p>`;
        } else if (hasActionCard) {
          console.log('[GlobalChat] Empty message carries an action card — no filler text added');
        } else if (payloadKind) {
          // DATA-ONLY RECORD (owner repro 2026-08-29: the clean-sweep batch
          // send-all summary — renderPolicy:'never', payload, no card, no
          // text — got stamped "Message received." by the branch below and
          // surfaced as a bare noise bubble). A payload-bearing empty
          // message is a deliberate silent record for the model/history;
          // it needs no filler. isContextOnly is the existing "in AI
          // context, hidden from UI" lane (GlobalChatSystem skips it at
          // render and it never retires CTA cards) — exactly what a silent
          // record is. Never invent words for it.
          message.isContextOnly = true;
          console.log('[GlobalChat] Empty message carries a data payload — silent record, no filler');
        } else {
          console.log('[GlobalChat] Adding fallback content to empty assistant message');
          message.text = message.text || 'Message received.';
          message.html = message.html || '<p>Message received.</p>';
        }
      }

      // For user messages, allow empty (could be attachment-only)
    }

    // Tag chat messages when runtime or compile-time testing is active
    const baseTags = message.tags || [];
    const normalizedTags = shouldTagAsTest()
      ? [...baseTags, 'test example'].filter((v, i, a) => a.indexOf(v) === i)
      : baseTags.length > 0 ? baseTags : undefined;

    // Thread-isolation / PR #71 follow-up: every USER message gets a
    // stable, unique id at addMessage time. Without this, retries of
    // the same text on the same index land on identical fingerprints
    // (idx + ts + text-prefix) and the thread-label inference effect's
    // de-dup tracker treats the retry as already-incorporated — so the
    // chip label / topic summary fail to refresh after a misfire. The
    // assistant streaming pipeline already generates ids via
    // generateMessageId(); user messages now do too.
    const ensuredId = message.id || (
      typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function'
        ? (crypto as Crypto).randomUUID()
        : `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    );
    const normalized: ChatMessage = {
      ...message,
      id: ensuredId,
      isUser: !!message.isUser,
      timestamp: message.timestamp ? new Date(message.timestamp) : new Date(),
      tags: normalizedTags,
    };

    // ────────────────────────────────────────────────────────────────────
    // THREAD ISOLATION — route NEW messages to originating thread's
    // per-thread storage when the active thread has switched away.
    //
    // Without this guard, ADD_MESSAGE dispatches into `state.messages`
    // (which is the ACTIVE thread's slot) every time — so an
    // assistant-message addition fired by an in-flight orchestrator on
    // thread A while the user is viewing thread B materializes as a
    // GHOST BUBBLE in thread B. That's the empty/cross-thread bubble
    // Miles reported on the 5/10/2026 live test.
    //
    // Routing decision is made by `shouldRouteNewMessageToStorage` and
    // is unit-tested in tests/hooks/threadIsolation.test.ts.
    // ────────────────────────────────────────────────────────────────────
    // Phase 5: prefer the EXPLICIT originating thread (captured in the send's
    // closure) over the shared, race-prone currentSendThreadIdRef.
    const originatingTid = resolveOriginatingThreadId(explicitOriginatingThreadId, currentSendThreadIdRef.current);
    const activeTid = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    const hasStorageHooks = !!(readThreadMessagesRef.current && writeThreadMessagesRef.current);
    const isCrossThread = shouldRouteNewMessageToStorage(originatingTid, activeTid, hasStorageHooks);

    // Round J: an assistant/system message landing on the originating thread
    // is a turn-scoped acknowledgment — cancel that thread's send watchdog.
    if (!normalized.isUser) {
      sendAckWatchdogRef.current?.ack(originatingTid);
    }

    if (originatingTid && activeTid && originatingTid !== activeTid && !hasStorageHooks) {
      console.warn(
        `${THREAD_ISO_DEBUG_TAG} cross-thread addMessage blocked: missing storage hooks`,
        { originatingTid, activeTid, messageId: normalized.id },
      );
      dispatchChatEvent(
        normalized.isUser ? CHAT_EVENTS.USER_MESSAGE : CHAT_EVENTS.ADD_SYSTEM_MESSAGE,
        normalized,
      );
      return normalized;
    }

    // PR #114 (Miles 2026-05-10) — Sub-fix A: ALWAYS synchronously
    // persist new bubbles to the originating thread's per-thread
    // storage when storage hooks + an originating thread id are
    // available. Both the cross-thread case (active !== originating)
    // AND the same-thread case (active === originating, the normal
    // case) get the immediate write.
    //
    // The residual race we are closing: in the same-thread path we
    // previously relied entirely on the centerpiece mirror-effect in
    // useChatThreads.ts to debounce-persist `state.messages` to the
    // active thread's key. If the user sent on thread A and switched
    // to thread B before that effect fired, SET_MESSAGES on switch
    // would replace `messages` with B's content, the mirror effect
    // would then write B's messages to B's key, and A's user bubble
    // M1 was never persisted to A's key. Stream finalize on A reads
    // a fresh A snapshot that's missing M1, and switching back to A
    // loads from per-thread storage — M1 is gone permanently.
    //
    // The empty-over-non-empty guard in `safelyPersistThreadMessages`
    // makes this safe to double-write: the mirror-effect's later
    // write of `[...M1, assistant]` is idempotent w.r.t. the
    // synchronous write of `[...M1]` we do here.
    if (
      hasStorageHooks
      && originatingTid
      && readThreadMessagesRef.current
      && writeThreadMessagesRef.current
    ) {
      const fresh = readThreadMessagesRef.current(originatingTid) ?? [];
      // R10 (2026-08-06): sanitize the just-added message at THIS callsite —
      // safelyPersistThreadMessages runs its dev-only assertNoLiveFilesInDev
      // BEFORE its own sanitize pass, so handing it a live File[] (the send
      // path's raw attachments) would throw mid-send in development builds,
      // before ADD_MESSAGE ever dispatches. The write itself deliberately
      // stays SYNCHRONOUS and light: deferring it to a macrotask would
      // re-open the PR #114 sub-fix A thread-switch race this write exists
      // to close, and the send path now adds the bubble with NO
      // attachmentsSerialized (no dataUrl payload) — the background
      // serialization/upgrade step carries the heavy payload into storage.
      writeThreadMessagesRef.current(originatingTid, [...fresh, sanitizeMessageForPersist(normalized)]);
      if (typeof window !== 'undefined') {
        try {
          const dbg = typeof localStorage !== 'undefined'
            && localStorage.getItem('DEBUG_THREAD_ISOLATION');
          if (dbg && dbg !== '0' && dbg !== 'false') {
            // eslint-disable-next-line no-console
            console.debug(
              `${THREAD_ISO_DEBUG_TAG} action=add-persisted-sync originatingThreadId=${originatingTid} activeThreadId=${activeTid} crossThread=${isCrossThread} id=${normalized.id} sourceTag=${(normalized as any).sourceTag || ''}`
            );
          }
        } catch { /* ignore */ }
      }
    }

    if (isCrossThread) {
      // Cross-thread: do NOT dispatch ADD_MESSAGE — the bubble belongs
      // to the originating thread, not the active one. The synchronous
      // write above is the only persistence path for this bubble.
      dispatchChatEvent(
        normalized.isUser ? CHAT_EVENTS.USER_MESSAGE : CHAT_EVENTS.ADD_SYSTEM_MESSAGE,
        normalized
      );
      return normalized;
    }

    // Same-thread (or single-thread mode): render in the active thread's
    // React state so the bubble shows up immediately. The mirror effect
    // will idempotently re-persist this same content shortly.
    messageCountRef.current += 1; // Increment synchronously to avoid stale closure issues
    dispatch({ type: 'ADD_MESSAGE', payload: normalized });
    dispatchChatEvent(
      normalized.isUser ? CHAT_EVENTS.USER_MESSAGE : CHAT_EVENTS.ADD_SYSTEM_MESSAGE,
      normalized
    );

    return normalized;
  }, []);

  const addSystemMessage = useCallback((input: SystemMessageInput, explicitOriginatingThreadId?: string | null): ChatMessage => {
    const systemMessage: ChatMessage = {
      // 2026-05-10: input.text is now optional (payload-rendered tools may
      // omit it). Default to '' so ChatMessage.text remains string-typed.
      text: input.text ?? '',
      html: input.html,
      isUser: false,
      timestamp: input.timestamp || new Date(),
      attachments: input.attachments,
      attachmentsSerialized: input.attachmentsSerialized,
      sourceTag: input.sourceTag,
      tokenUsage: input.tokenUsage,
      tokenUsageSnapshot: input.tokenUsageSnapshot,
      tokenUsages: input.tokenUsages,
      tokenUsageSnapshots: input.tokenUsageSnapshots,
      // SpecialMessageTile properties
      messageType: input.messageType,
      title: input.title,
      actionButtons: input.actionButtons,
      priority: input.priority,
      // 2026-08-12 (connect-card incident): SystemMessageInput has carried
      // workflowActionButtonsHtml since the workflow-buttons feature, but this
      // sink silently dropped it — any addSystemMessage-posted card (e.g. the
      // connect-app offer's one-click connect buttons) rendered buttonless.
      // The streaming-finalize sink already preserved it; copy it here too.
      workflowActionButtonsHtml: input.workflowActionButtonsHtml,
      // 2026-08-20 (data-only connect cards): renderPolicy:'never' posts land
      // here directly — keep the structured payload on the message so the
      // standing-offer dedupe and future turn context can read it.
      payload: input.payload,
    };

    return addMessage(systemMessage, explicitOriginatingThreadId);
  }, [addMessage]);

  // Round J: visible, RETRYABLE failure bubble for a send that died with no
  // other outcome. Rendered through the existing interrupted/regeneratePrompt
  // affordance (GlobalChatSystem's one-click button); the send_failure_retry
  // sourceTag makes the button read "Retry" and resend through the safe
  // programmatic envelope WITHOUT duplicating the original user bubble.
  //
  // Finding 8d (2026-07-15): an attachment-only failed turn has no original
  // text — regeneratePrompt stays empty and no Retry button renders — so the
  // copy must not say "Tap retry" (sendFailureBubbleText picks the variant).
  const appendSendFailureBubble = useCallback((threadId: string | null, originalText: string) => {
    addMessage({
      text: sendFailureBubbleText(originalText),
      isUser: false,
      timestamp: new Date(),
      sourceTag: SEND_FAILURE_RETRY_SOURCE_TAG,
      status: 'interrupted',
      interrupted: true,
      regeneratePrompt: (originalText || '').trim() || undefined,
    } as ChatMessage, threadId);
  }, [addMessage]);

  // Finding 8b (2026-07-15): N rapid pre-hydration sends settling signed-out
  // must produce ONE sign-in prompt per thread, not N. Dedupe against the
  // thread's fresh storage snapshot (synchronous — rapid re-dispatches in
  // the same tick see each other's writes) with an in-memory per-thread
  // guard as the fallback for surfaces without storage hooks. The guard
  // resets when authorization flips true so a future signed-out state can
  // prompt again.
  const signInPromptShownThreadsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (globalTools.isAuthorized) signInPromptShownThreadsRef.current.clear();
  }, [globalTools.isAuthorized]);
  // L3 (2026-07-15): warm sign-in invitation. Copy branches on DEVICE memory
  // (hasSignedInBeforeMarker — "welcome back / sign back in" vs the member-
  // service framing that NEVER suggests creating an account) and resolves
  // EN/ES from the triggering text (detectInputLanguage — the guest-surface
  // mechanism) with the canonical UI language as the no-text fallback. The
  // /auth mention renders as a real same-origin anchor via the bubble
  // pipeline's internal-route autolink (utils.ts, INTERNAL_AUTOLINK_ROUTES).
  const appendSignInPromptOnce = useCallback((threadId: string | null, promptContextText?: string) => {
    const guardKey = threadId ?? '__single_thread__';
    const history: ChatMessage[] | null =
      (threadId && readThreadMessagesRef.current)
        ? (readThreadMessagesRef.current(threadId) ?? [])
        : (messagesSyncRef.current || null);
    const alreadyInThread = Array.isArray(history)
      && history.some((m) => m && m.sourceTag === 'auth_required_notice');
    if (alreadyInThread || signInPromptShownThreadsRef.current.has(guardKey)) {
      return;
    }
    signInPromptShownThreadsRef.current.add(guardKey);
    const language = (promptContextText || '').trim()
      ? detectInputLanguage(promptContextText as string)
      : getUserUiLanguage();
    addSystemMessage({
      text: signInPromptText({
        hasSignedInBefore: hasSignedInBeforeMarker(),
        language,
      }),
      sourceTag: 'auth_required_notice',
    }, threadId);
  }, [addSystemMessage]);

  // ============================================================================
  // Processing State Control (prevents sending messages during active operations)
  // ============================================================================

  // Hard safety-timeout floor for state.isProcessing.
  //
  // This is now the ONLY path that auto-clears an "explicit-true" processing
  // flag. The previous HTML-animation heuristic caused false-clears the moment
  // a tool finalized its own pending widget mid-chain (see isSystemProcessing).
  //
  // 10 minutes comfortably covers the longest legitimate client-side workflow
  // (Perplexity sonar-deep-research Stage 3, which the deep-research tool
  // bounds to ~8 min) plus the chain handoff. Tools with very long legitimate
  // wall-clock times MUST call `ctx.refreshProcessingHeartbeat()` (or its
  // host equivalent `startProcessing()`) every few minutes to keep the clock
  // fresh; otherwise the floor will kick in and release the send button.
  const PROCESSING_SAFETY_TIMEOUT_MS = 10 * 60 * 1000;

  // Streaming lease v3 (2026-09-02): this tab's share of the cross-tab
  // "an answer is writing" signal is DERIVED from the per-thread processing
  // flags plus any queued sends — the two things the send path itself keeps
  // honest (watchdog-released on dead turns, cleared by every terminal
  // branch) — instead of balanced bump calls on every placeholder. The old
  // refcount leaked on every thinking widget / tool card that was removed
  // rather than finalized, and the leak kept the update banner's CTA greyed
  // out for the life of the tab (owner report, 2026-09-01 screenshots).
  // Queued sends hold the lease too: they live only in reducer state, so a
  // reload while one waits would silently drop it — "mid-task" by definition.
  useEffect(() => {
    const proc = state.processingByThread || {};
    const pend = state.pendingByThread || {};
    const active =
      // Aggregate flag covers single-thread mode (null thread id → no per-thread key).
      Boolean(state.isProcessing) ||
      Object.values(proc).some(Boolean) ||
      Object.values(pend).some((q) => Array.isArray(q) && q.length > 0);
    setLocalStreamingActive(active);
  }, [state.isProcessing, state.processingByThread, state.pendingByThread]);

  // Start processing - call when beginning any async operation. Safe to call
  // repeatedly as a heartbeat to keep the safety-timeout fresh during very
  // long workflows (deep research stage 3, workflow chains, etc.).
  //
  // Thread-isolation: optional `threadId` keys the per-thread processing
  // map. When omitted, falls back to currentSendThreadIdRef (set by
  // handleSend) so the heartbeat helpers exposed to globalTools can
  // resolve the originating thread without each tool having to thread
  // it through.
  const startProcessing = useCallback((threadId?: string | null, opts?: { turnEpochMs?: number }) => {
    const tid = threadId || currentSendThreadIdRef.current || null;
    dispatch({
      type: 'SET_PROCESSING',
      payload: {
        isProcessing: true,
        timestamp: Date.now(),
        threadId: tid,
        // Rising-edge clock override (server-task discovery): show the task's
        // true age, never a reset-to-0:00 clock. Heartbeat stays Date.now().
        ...(opts?.turnEpochMs ? { turnEpochMs: opts.turnEpochMs } : {}),
      },
    });
  }, []);

  // Stop processing - call when operation completes (success or failure).
  // Thread-isolation: optional `threadId` clears only that thread's flag.
  const stopProcessing = useCallback((threadId?: string | null) => {
    const tid = threadId || currentSendThreadIdRef.current || null;
    // Round J: a terminal releasing this thread's processing flag is a
    // turn-scoped acknowledgment (something owned the turn to completion or
    // failure — either way a visible outcome path already ran).
    sendAckWatchdogRef.current?.ack(tid);
    dispatch({
      type: 'SET_PROCESSING',
      payload: { isProcessing: false, threadId: tid },
    });
  }, []);

  // Round J.1 (Finding 1a): when a LATE ack proves the watchdog-fired turn
  // was actually alive, strip that thread's YOUNGEST send_failure_retry
  // bubble so its Retry can't double-dispatch the turn. Mirrors the two
  // write surfaces every bubble lives on: the originating thread's
  // per-thread storage (synchronous write in appendSendFailureBubble →
  // addMessage) and — when the thread is the active view (or a single-thread
  // surface) — the rendered React state.
  const stripStaleSendFailureBubble = useCallback((threadId: string | null) => {
    // Per-thread storage strip.
    if (threadId && readThreadMessagesRef.current && writeThreadMessagesRef.current) {
      const fresh = readThreadMessagesRef.current(threadId) ?? [];
      const stripped = stripYoungestSendFailureBubble(fresh);
      if (stripped.changed) {
        writeThreadMessagesRef.current(threadId, stripped.messages);
      }
    }
    // Rendered-state strip (active thread or single-thread surfaces).
    const activeTid = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    if (!threadId || activeTid === threadId || !activeTid) {
      const current = messagesSyncRef.current || [];
      for (let i = current.length - 1; i >= 0; i -= 1) {
        const m = current[i];
        if (m && !m.isUser && m.sourceTag === SEND_FAILURE_RETRY_SOURCE_TAG) {
          if (m.id) dispatch({ type: 'REMOVE_MESSAGE_BY_ID', payload: { id: m.id } });
          break;
        }
      }
    }
  }, []);

  // Round J: wire the send-ack watchdog's timeout to the real release +
  // visible-outcome pair now that both exist. Latest-closure via effect.
  useEffect(() => {
    sendAckTimeoutHandlerRef.current = (threadId, entry) => {
      console.error('[GlobalChat] send-ack watchdog fired — dispatched turn produced no acknowledgment', { threadId });
      stopProcessing(threadId);
      appendSendFailureBubble(threadId, entry.text);
    };
    // Round J.1 (Finding 1): reconcile a fire with a late-succeeding turn.
    sendAckLateAckHandlerRef.current = (threadId) => {
      console.warn('[GlobalChat] send-ack watchdog LATE ACK — the turn the watchdog gave up on is alive; stripping the stale retry bubble', { threadId });
      stripStaleSendFailureBubble(threadId);
      // The thread's queue-drain hold just cleared — re-evaluate promptly
      // (a cross-thread late ack may not re-render on its own).
      bumpDrainHoldTick();
    };
  }, [stopProcessing, appendSendFailureBubble, stripStaleSendFailureBubble, bumpDrainHoldTick]);

  // ──────────────────────────────────────────────────────────────────────
  // Stuck-flag sweep (owner report 2026-08-19: "still working" 30+ minutes
  // over two threads whose turns had both completed and delivered replies).
  //
  // TIME-DRIVEN, unlike the render-driven safety floor below: a leaked flag
  // with no further state churn never re-evaluates that computed, so its
  // 10-minute floor never actually ran — the flags (and the synthetic
  // "still working" pane widget + rail clocks) ran unbounded. This interval
  // guarantees both invariants hold on a clock:
  //   • a thread whose session-ledger turn is TERMINAL past a short grace,
  //     with a settled pane (no pending widget, newest message settled),
  //     clears within ~1 sweep — the user never watches a phantom spinner
  //     over a delivered reply for long;
  //   • any flag whose heartbeat stamp is 10+ minutes stale clears, no
  //     matter what leaked it or whether React happened to re-render.
  // Decision logic is pure (planStuckProcessingClears, unit-tested); this
  // effect only feeds it state and dispatches the same thread-keyed
  // stopProcessing every legitimate finalize path uses.
  // ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const flaggedThreadIds = Object.keys(state.processingByThread || {});
    if (flaggedThreadIds.length === 0) return undefined;
    const sweep = () => {
      try {
        const activeTid = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
        const sweepThreads = flaggedThreadIds.map((tid) => {
          const turn =
            typeof prest0nSessionHook.getTurnForThread === 'function'
              ? prest0nSessionHook.getTurnForThread(tid)
              : null;
          const messages: ChatMessage[] | undefined =
            tid === activeTid
              ? (messagesSyncRef.current || [])
              : (readThreadMessagesRef.current ? readThreadMessagesRef.current(tid) ?? undefined : undefined);
          return {
            threadId: tid,
            lastHeartbeatAtMs: (state.processingStartedAtByThread || {})[tid],
            turnPhase: (turn?.phase as 'streaming' | 'polling' | 'terminal' | undefined) ?? null,
            turnEndedAtMs: turn?.endedAtMs,
            messages,
            // 2026-08-23 audit-thread fix: active-task discovery is the
            // authoritative "server says work is still running" signal — while
            // a thread carries a FRESH confirmation, the terminal-turn clear
            // must not fire (the client turn ended, but delegated/background
            // work owns the flag).
            // REVIEW-20260824: freshness, not membership. discoveryLitThreadsRef
            // never prunes the ACTIVE thread (reconcile skips it by design), so
            // membership latched the veto true forever and rule 1 went dead
            // exactly where the phantom spinner is visible. A confirmation
            // counts only within 2x the 45s poll (one dropped poll tolerated);
            // hidden tabs never poll, so a stamp stays fresh while hidden.
            serverConfirmedActive: (() => {
              const confirmedAt = discoveryConfirmedAtRef.current.get(tid);
              if (!confirmedAt) return false;
              if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return true;
              return Date.now() - confirmedAt < DISCOVERY_CONFIRMATION_TTL_MS;
            })(),
          };
        });
        const clears = planStuckProcessingClears(sweepThreads, Date.now());
        for (const clear of clears) {
          console.warn(
            `[GlobalChat] stuck-flag sweep clearing thread ${clear.threadId} (${clear.reason})`,
          );
          stopProcessing(clear.threadId);
        }
      } catch (err) {
        console.warn('[GlobalChat] stuck-flag sweep failed:', err);
      }
    };
    // Immediate pass (ledger mutations should reconcile promptly), then poll.
    sweep();
    const intervalId = setInterval(sweep, 20_000);
    return () => clearInterval(intervalId);
  }, [
    state.processingByThread,
    state.processingStartedAtByThread,
    prest0nSessionHook.liveTurnsVersion,
    prest0nSessionHook.getTurnForThread,
    stopProcessing,
  ]);

  // Computed fallback: detect if any message has pending animation.
  //
  // NOTE (2026-05-07 fix): this value is now INFORMATIONAL ONLY and is exposed
  // so <GlobalChatSystem> can render a neutral "working…" fallback bubble when
  // state.isProcessing is true but no individual tool has posted its own pending
  // widget yet (e.g., between chain steps in executeWorkflowChain, or while
  // legal-doc-creator is pre-filling intake fields from sharedContext).
  //
  // It is NOT used to auto-clear state.isProcessing anymore — doing so was the
  // primary source of Miles' "nothing is spinning, is it paused?" bug: the
  // moment a tool called stream.finalize() the HTML heuristic flipped false,
  // isSystemProcessing() auto-cleared the explicit flag, and the send button
  // re-enabled even though the orchestrator was still mid-chain-handoff.
  const hasComputedPendingState = useCallback((): boolean => {
    return state.messages.some((msg) => {
      const html = msg.html || '';
      return html.includes('pendingShimmer') || html.includes('pendingSpin');
    });
  }, [state.messages]);

  // Authoritative processing gate.
  //
  // The explicit `state.isProcessing` flag is now the *single source of truth*.
  // Every tool / chain / workflow path that wants the send button locked calls
  // startProcessing(); every path that wants it released calls stopProcessing()
  // (finalize*, error handlers, cancelRunningTool).
  //
  // The ONLY exceptions below this point are:
  //   1. A tool is explicitly awaiting user input (clarifying question) — we
  //      WANT the send button enabled so the user can answer.
  //   2. A hard 60s safety-timeout floor — if processingStartedAt is older than
  //      the timeout we assume stuck state and auto-clear. Workflows that take
  //      longer than 60s (deep research stage 3, chain execution) MUST call
  //      startProcessing() again as they progress to keep the clock fresh.
  const isSystemProcessing = useCallback((): boolean => {
    // Exception: if any tool is awaiting user input, allow sending.
    if (globalTools.isAnyToolAwaitingInput()) {
      return false;
    }

    // Per-thread send-gating (2026-05-09 demo fix #82) extended for
    // same-thread queueing (PR feat/same-thread-queue-while-streaming):
    //
    // Before #82, the send button was gated on the GLOBAL
    // `state.isProcessing` flag — a long stream on thread A blocked
    // sending on thread B. PR #82 fixed that so the active thread's
    // processing state controlled the gate. This PR goes further:
    //
    //   The active thread being processing NO LONGER blocks the send
    //   button. Instead, the user is allowed to type a follow-up
    //   message on the SAME thread while the first turn is still
    //   streaming — it gets queued and fires after the in-flight
    //   turn completes. (Miles directive 2026-05-09 23:44 UTC: "yes
    //   it should be allowed".)
    //
    //   The new gate is: thread queue is FULL (>= MAX_QUEUE_DEPTH).
    //   Beyond the cap the input is gated and the composer renders a
    //   small "queued, please wait" hint.
    //
    // The safety-timeout floor below STILL runs when the active thread
    // is processing — it's the only auto-clear path for genuinely-stuck
    // streams. We just don't BLOCK the user on the in-flight turn alone.
    const perThreadMap = state.processingByThread || {};
    const hasPerThreadInfo = Object.keys(perThreadMap).length > 0;
    const pendingMap = state.pendingByThread || {};
    const activeThreadId =
      (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;

    // FIX (2026-07-10, brand-new empty-thread never gated by a dead thread's
    // leftover): state.messages IS the ACTIVE thread's message list (the
    // per-thread switch swaps it on New Thread). An active thread with zero
    // rendered messages and no queued pending entry cannot itself own a live
    // in-flight turn that has rendered nothing — every real turn renders at
    // least an immediate "thinking…" widget or keys pendingByThread. So such
    // a surface must NEVER be blocked by a leaked GLOBAL processing flag or
    // another (dead) thread's residue. This is READ-ONLY — it clears no flag,
    // preserving the "never auto-clear mid-chain" invariant; the source-side
    // release above stops the leak, and this is the belt-and-suspenders gate
    // guard so New Thread always sends clean (Malaika screenshot 3).
    {
      const activePendingDepth = activeThreadId
        ? (pendingMap[activeThreadId] || []).length
        : 0;
      if (state.messages.length === 0 && activePendingDepth === 0) {
        return false;
      }
    }

    if (hasPerThreadInfo || Object.keys(pendingMap).length > 0) {
      const activeProcessing = activeThreadId ? !!perThreadMap[activeThreadId] : false;
      const activeQueueDepth = activeThreadId
        ? (pendingMap[activeThreadId] || []).length
        : 0;
      const queueFull = activeQueueDepth >= MAX_QUEUE_DEPTH;
      if (!activeProcessing && !queueFull) {
        // Active thread is idle and queue isn't full — free to send.
        return false;
      }
      if (activeProcessing && !queueFull) {
        // Active thread IS processing but queue has capacity. New send
        // would queue, not fire — do NOT block the input.
        return false;
      }
      // queueFull — fall through and return `true` from the explicit
      // flag check below to disable the send button. We still want to
      // honour the safety-timeout floor for stuck streams.
      if (queueFull) {
        return true;
      }
    }

    // Trust the explicit flag. No HTML-heuristic auto-clear — see comment
    // above hasComputedPendingState.
    if (!state.isProcessing) {
      return false;
    }

    // Orphaned-global-flag guard (Symptom 5, Miles 2026-07-10 screenshot:
    // brand-new "New thread" with a greyed composer and a stuck
    // "please wait while processing…" popup).
    //
    // We reach here with state.isProcessing === true but NO per-thread
    // attribution (both perThreadMap and pendingMap empty — otherwise the
    // block above would have returned). That combination can only be a
    // leaked/orphaned global flag: every real multi-thread turn keys
    // processingByThread. The one legacy-single-thread ambiguity is
    // disambiguated by hasComputedPendingState() — a real legacy turn
    // still renders a pendingShimmer/pendingSpin widget, so it stays
    // blocked; a leaked flag renders nothing and is safe to unblock.
    //
    // This does NOT clear the flag (preserving the "never auto-clear
    // mid-chain" invariant behind Miles's earlier "nothing is spinning,
    // is it paused?" fix); the safety-timeout floor below still owns the
    // only mutating auto-clear path.
    if (
      isOrphanedGlobalProcessingFlag({
        isProcessing: state.isProcessing,
        hasPerThreadProcessing: hasPerThreadInfo,
        hasPerThreadPending: Object.keys(pendingMap).length > 0,
        hasRenderedPendingWidget: hasComputedPendingState(),
      })
    ) {
      return false;
    }

    // Hard safety-timeout floor. This is the ONLY auto-clear path.
    //
    // Thread-isolation (2026-05-09 demo fix): only auto-clear the
    // SPECIFIC thread(s) that timed out — NEVER blow away every
    // thread's processing flag with a global SET_PROCESSING:false.
    // Miles's regression repro showed thread A's spinner clearing the
    // moment thread B fired any global cleanup; this dispatch was one
    // of the candidate culprits. We iterate the per-thread map and
    // clear only entries whose start timestamp exceeded the floor.
    if (state.processingStartedAt) {
      const now = Date.now();
      const stuckThreadIds: string[] = [];
      for (const [tid, startedAt] of Object.entries(state.processingStartedAtByThread || {})) {
        if (typeof startedAt === 'number' && now - startedAt > PROCESSING_SAFETY_TIMEOUT_MS) {
          stuckThreadIds.push(tid);
        }
      }
      if (stuckThreadIds.length > 0) {
        console.warn(
          `[GlobalChat] Processing timeout - auto-clearing stuck thread(s): ${stuckThreadIds.join(', ')}`
        );
        for (const tid of stuckThreadIds) {
          dispatch({
            type: 'SET_PROCESSING',
            payload: { isProcessing: false, threadId: tid },
          });
        }
        // After per-thread cleanup, re-evaluate whether any thread is
        // still processing. If the per-thread map was empty (e.g.,
        // legacy global processingStartedAt with no thread context),
        // fall back to the global clear so we never leave a stuck UI.
        if (Object.keys(state.processingByThread || {}).length === 0) {
          const elapsed = now - state.processingStartedAt;
          if (elapsed > PROCESSING_SAFETY_TIMEOUT_MS) {
            dispatch({
              type: 'SET_PROCESSING',
              payload: { isProcessing: false },
            });
          }
        }
        return false;
      }
      // Legacy fallback: no per-thread entries but processingStartedAt
      // is set (single-thread mode). Original global clear behaviour.
      if (
        Object.keys(state.processingByThread || {}).length === 0
        && now - state.processingStartedAt > PROCESSING_SAFETY_TIMEOUT_MS
      ) {
        console.warn('[GlobalChat] Processing timeout (legacy single-thread) - auto-clearing stuck state');
        dispatch({
          type: 'SET_PROCESSING',
          payload: { isProcessing: false },
        });
        return false;
      }
    }

    // THREAD-MODE GUARANTEE (Symptom 5 hardening, 2026-07-10): reaching here
    // means the global flag is set with NO per-thread attribution. In
    // thread-switcher mode that is a leak by definition — every real
    // multi-thread turn keys processingByThread (asserted above the orphan
    // guard). The orphan guard alone still blocked when a STRANDED pending
    // widget sat in the list (hasRenderedPendingWidget true), wedging the
    // composer for up to the 10-minute floor; in thread mode a stranded
    // widget is a rendering artifact, not evidence of a live turn — and even
    // if a turn WERE live, sends queue rather than fire, so not blocking is
    // safe. Legacy single-thread instances (activeThreadId === null) keep
    // the original blocking behaviour and the widget-based disambiguation.
    return !activeThreadId;
  }, [state.isProcessing, state.processingStartedAt, state.processingByThread, state.processingStartedAtByThread, state.pendingByThread, state.messages.length, globalTools, hasComputedPendingState]);

  // Generate unique message ID (immune to React dispatch race conditions)
  const generateMessageId = useCallback((): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for environments without crypto.randomUUID
    return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }, []);

  // Streaming message helpers for real-time updates
  const startStreamingMessage = useCallback((initial: SystemMessageInput, explicitOriginatingThreadId?: string | null): { index: number; id: string } => {
    // Phase 5: resolve the originating thread FIRST (explicit over the shared
    // ref) so the processing flag is keyed to the correct thread even under
    // concurrent multi-thread sends.
    const streamOriginatingTid = resolveOriginatingThreadId(explicitOriginatingThreadId, currentSendThreadIdRef.current);
    // Set processing state when streaming begins (prevents new message sends)
    startProcessing(streamOriginatingTid);

    // Round J: a streaming message starting on the originating thread is a
    // turn-scoped acknowledgment (both send envelopes arm AFTER creating
    // their own immediate thinking widget, so this can't self-ack).
    sendAckWatchdogRef.current?.ack(streamOriginatingTid);

    // Signal that streaming is active so syncFromStorage() defers.
    // Without this, the cross-tab sync poll can fire SET_MESSAGES from localStorage
    // (which lacks this in-memory-only streaming message) and blow it away before
    // finalize() is called — leaving spinners like "assessing" stuck forever.
    // Synchronous on purpose (the derived lease effect confirms after commit).
    noteStreamStarted();

    // Generate unique ID for this message (immune to index race conditions)
    const messageId = initial.id || generateMessageId();

    // Tag streaming messages when runtime or compile-time testing is active
    const baseTags = initial.tags || [];
    const normalizedTags = shouldTagAsTest()
      ? [...baseTags, 'test example'].filter((v, i, a) => a.indexOf(v) === i)
      : baseTags.length > 0 ? baseTags : undefined;

    const streamingMessage: ChatMessage = {
      id: messageId,
      text: initial.text || '',
      html: initial.html,
      isUser: false,
      timestamp: initial.timestamp || new Date(),
      attachments: initial.attachments,
      attachmentsSerialized: initial.attachmentsSerialized,
      sourceTag: initial.sourceTag,
      tokenUsage: initial.tokenUsage,
      tokenUsageSnapshot: initial.tokenUsageSnapshot,
      tokenUsages: initial.tokenUsages,
      tokenUsageSnapshots: initial.tokenUsageSnapshots,
      // SpecialMessageTile properties
      messageType: initial.messageType,
      title: initial.title,
      actionButtons: initial.actionButtons,
      priority: initial.priority,
      tags: normalizedTags,
    };

    // ────────────────────────────────────────────────────────────────────
    // THREAD ISOLATION — route NEW streaming bubbles into the
    // originating thread's per-thread storage when the active thread
    // has switched away. Mirrors the same routing rule in addMessage.
    //
    // Without this guard, every new pending widget (Stage 1/2/3,
    // attachment-read, document-analysis) created mid-stream while
    // the user is on a different thread lands in the OTHER thread's
    // view as an empty/animated bubble — the ghost bubble repro from
    // Miles's 5/10/2026 live test.
    // ────────────────────────────────────────────────────────────────────
    const originatingTid = streamOriginatingTid;
    const activeTid = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    const hasStorageHooks = !!(readThreadMessagesRef.current && writeThreadMessagesRef.current);
    const isCrossThread = shouldRouteNewMessageToStorage(originatingTid, activeTid, hasStorageHooks);

    if (originatingTid && activeTid && originatingTid !== activeTid && !hasStorageHooks) {
      console.warn(
        `${THREAD_ISO_DEBUG_TAG} cross-thread startStreamingMessage blocked: missing storage hooks`,
        { originatingTid, activeTid, messageId },
      );
      dispatchChatEvent(CHAT_EVENTS.ADD_SYSTEM_MESSAGE, streamingMessage);
      return { index: -1, id: messageId };
    }

    // PR #114 (Miles 2026-05-10) — Sub-fix A: ALWAYS synchronously
    // persist new streaming bubbles to the originating thread's
    // per-thread storage when storage hooks + originating thread id
    // are available. Mirrors the same fix in `addMessage` above and
    // closes the same residual race for assistant streaming bubbles.
    let routedIndex: number | null = null;
    if (
      hasStorageHooks
      && originatingTid
      && readThreadMessagesRef.current
      && writeThreadMessagesRef.current
    ) {
      const fresh = readThreadMessagesRef.current(originatingTid) ?? [];
      writeThreadMessagesRef.current(originatingTid, [...fresh, streamingMessage]);
      routedIndex = fresh.length;
      if (typeof window !== 'undefined') {
        try {
          const dbg = typeof localStorage !== 'undefined'
            && localStorage.getItem('DEBUG_THREAD_ISOLATION');
          if (dbg && dbg !== '0' && dbg !== 'false') {
            // eslint-disable-next-line no-console
            console.debug(
              `${THREAD_ISO_DEBUG_TAG} action=startStream-persisted-sync originatingThreadId=${originatingTid} activeThreadId=${activeTid} crossThread=${isCrossThread} id=${messageId} sourceTag=${initial.sourceTag || ''}`
            );
          }
        } catch { /* ignore */ }
      }
    }

    if (isCrossThread) {
      dispatchChatEvent(CHAT_EVENTS.ADD_SYSTEM_MESSAGE, streamingMessage);
      console.log('[GlobalChat] Started streaming message (routed):', { index: routedIndex ?? 0, id: messageId, sourceTag: initial.sourceTag, originatingTid, activeTid });
      return { index: routedIndex ?? 0, id: messageId };
    }

    // Use ref for index to avoid stale closure issues - the user message may have been
    // dispatched but React hasn't re-rendered yet, so state.messages.length is stale.
    // The ref is updated synchronously when we detect message count changes.
    const newMessageIndex = messageCountRef.current;
    messageCountRef.current += 1; // Increment synchronously before dispatch

    dispatch({ type: 'ADD_MESSAGE', payload: streamingMessage });
    dispatchChatEvent(CHAT_EVENTS.ADD_SYSTEM_MESSAGE, streamingMessage);

    console.log('[GlobalChat] Started streaming message:', { index: newMessageIndex, id: messageId, sourceTag: initial.sourceTag });

    return { index: newMessageIndex, id: messageId };
  }, [startProcessing, generateMessageId]);

  const updateStreamingMessage = useCallback((index: number, update: Partial<ChatMessage>) => {
    // Signal to other tabs that streaming is active - allows them to defer sync
    refreshStreamingFlag();
    dispatch({ type: 'UPDATE_MESSAGE', payload: { index, message: update } });
  }, []);

  // ID-based update (immune to React dispatch race conditions)
  const updateStreamingMessageById = useCallback((
    id: string,
    update: Partial<ChatMessage>,
    opts?: {
      /**
       * INCIDENT-20260818: the update's OWN thread — when (and only when) it
       * is the active thread, an id-miss appends instead of no-opping, so a
       * resumed turn's pending widget is always visible. See the payload doc.
       */
      appendIfMissingForThread?: string | null;
    },
  ) => {
    // Signal to other tabs that streaming is active - allows them to defer sync
    refreshStreamingFlag();
    const activeTid = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    const requestedAppendTid = opts?.appendIfMissingForThread || null;
    let appendIfMissing = Boolean(
      requestedAppendTid
      && activeTid
      && requestedAppendTid === activeTid,
    );
    // T3 VERIFICATION BELT (2026-08-22): an append may only materialize a
    // message its claimed thread actually OWNS. When the per-thread store is
    // readable and carries no trace of this id, refuse the append — the
    // plain by-id update below no-ops safely — instead of fabricating a
    // bubble in the viewed pane (the cross-thread ghost class). In the real
    // INCIDENT-20260818 scenario (pane wiped by a raced SET_MESSAGES) the
    // durable store still holds the placeholder, so the resume widget stays
    // visible; an unreadable store (single-thread surfaces) keeps the
    // fail-open append.
    if (appendIfMissing && requestedAppendTid && readThreadMessagesRef.current) {
      try {
        const persisted = readThreadMessagesRef.current(requestedAppendTid) || [];
        if (!persisted.some((m) => m?.id === id)) {
          console.warn('[GlobalChat] append-on-miss refused: claimed thread has no trace of the message', {
            id,
            claimedThread: requestedAppendTid,
          });
          appendIfMissing = false;
        }
      } catch {
        // Store read failed — keep the fail-open append (visibility wins).
      }
    }
    dispatch({ type: 'UPDATE_MESSAGE_BY_ID', payload: { id, message: update, appendIfMissing } });
  }, []);

  const finalizeStreamingMessage = useCallback((index: number, final: Partial<ChatMessage>, explicitOriginatingThreadId?: string | null) => {
    // Phase 5: explicit thread over the shared ref, so a finalize fired for
    // thread A never clears thread B's flag / persists to B's store.
    const finalizeOriginatingTid = resolveOriginatingThreadId(explicitOriginatingThreadId, currentSendThreadIdRef.current);
    // CHANGE 10 Fix 3: Cancel any pending persist that captured stale (empty) state
    // This prevents the persistence race condition where stale data overwrites good data
    if (pendingPersistRef.current) {
      clearTimeout(pendingPersistRef.current);
      pendingPersistRef.current = null;
      console.log('[GlobalChat] Cancelled pending persist to prevent stale data');
    }

    // CHANGE 10 Fix 2: Force fallback content for empty orchestrator messages
    // This ensures orchestrator responses NEVER result in empty bubbles
    const FALLBACK_CONTENT = "I encountered an issue processing your request. Please try again.";
    let text = final.text;
    let html = final.html;

    // 2026-07-14 incident hardening: an empty finalize on these families
    // means a turn DIED mid-flight. The silent-removal branch below then
    // deleted the widget AND released processing — pane blank, chips dead,
    // nothing ever arrives ("nothing came back, nothing spinning"). Inject
    // the honest fallback instead so the turn ends VISIBLY. Plain chat
    // messages (no sourceTag) keep the removal path — empty finalize there
    // is benign cleanup.
    const finalTag = String((final as any)?.sourceTag || '');
    const isOrchestratorMessage = finalTag.includes('orchestrator')
      || finalTag.startsWith('deep_research')
      || finalTag.startsWith('prest0n_session')
      || finalTag.startsWith('research_result')
      || finalTag.startsWith('stage1_assessment');
    if (isOrchestratorMessage && (!text || !text.trim()) && (!html || !html.trim())) {
      console.warn('[GlobalChat] CHANGE 10 Fix 2: Injecting fallback content for empty orchestrator message', {
        index,
        sourceTag: (final as any)?.sourceTag,
      });
      text = FALLBACK_CONTENT;
      html = markdownToHtml(FALLBACK_CONTENT);
    }

    // Validate content - prevent blank messages (with potentially-injected fallback)
    const hasContent = (text && text.trim().length > 0) || (html && html.trim().length > 0);

    if (!hasContent) {
      console.warn('[GlobalChat] Attempted to finalize streaming message with empty content - removing instead', {
        index,
        sourceTag: (final as any)?.sourceTag,
        trace: new Error().stack?.split('\n').slice(0, 5).join('\n'),
      });

      // Remove the message entirely instead of showing "Message completed."
      // This prevents redundant/orphaned messages from appearing.
      // T3 (2026-08-22): index-addressed dispatches bind to the VIEWED pane —
      // after a mid-turn thread switch, `index` names an unrelated message in
      // the thread the user is looking at. Only touch the pane when it is the
      // originating thread's pane (null thread identity = single-thread mode,
      // no cross-thread risk). Flags always clear either way.
      const emptyFinalizeActiveTid = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
      if (!finalizeOriginatingTid || !emptyFinalizeActiveTid || finalizeOriginatingTid === emptyFinalizeActiveTid) {
        dispatch({ type: 'REMOVE_MESSAGE', payload: { index } });
      } else {
        console.warn('[GlobalChat] cross-thread empty finalize: pane removal skipped — index addresses the viewed thread, not the originating one', {
          index,
          originatingTid: finalizeOriginatingTid,
          activeTid: emptyFinalizeActiveTid,
          sourceTag: (final as any)?.sourceTag || null,
        });
      }
      // Clear processing state AFTER removing the message (the streaming
      // lease follows the processing flag via the derived effect).
      stopProcessing(finalizeOriginatingTid);
      return; // Exit early - don't update with empty content
    }

    // CRITICAL: Dispatch the message update FIRST, before clearing the streaming flag
    // or stopping processing. If we clear the flag first, the cross-tab sync poll can
    // fire a SET_MESSAGES from localStorage (which lacks in-memory streaming messages),
    // blowing away the message before UPDATE_MESSAGE reaches the reducer.
    // T3 (2026-08-22): but ONLY when the viewed pane IS the originating
    // thread's pane — `index` is a position in whatever transcript is on
    // screen, so after a mid-turn thread switch this dispatch would overwrite
    // an unrelated message in the thread the user is looking at (the
    // "apps answer rendered inside the research thread" write). The persist
    // below is origin-keyed and always runs; the origin thread shows the
    // finalized answer from its store on next visit. Null thread identity =
    // single-thread mode, no cross-thread risk — dispatch as before.
    const finalizeActiveTid = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    if (!finalizeOriginatingTid || !finalizeActiveTid || finalizeOriginatingTid === finalizeActiveTid) {
      dispatch({ type: 'UPDATE_MESSAGE', payload: { index, message: { ...final, text, html } } });
    } else {
      console.warn('[GlobalChat] cross-thread finalize (index): pane dispatch skipped — index addresses the viewed thread, not the originating one', {
        index,
        originatingTid: finalizeOriginatingTid,
        activeTid: finalizeActiveTid,
        sourceTag: (final as any)?.sourceTag || null,
      });
    }

    persistFinalizedStreamingMessageToThreadStorage({
      originatingThreadId: finalizeOriginatingTid,
      readThreadMessages: readThreadMessagesRef.current,
      writeThreadMessages: writeThreadMessagesRef.current,
      target: { index },
      final,
      text,
      html,
    });

    // NOW clear processing state — the message is safely in the reducer queue
    // (the streaming lease releases from the derived effect AFTER the commit
    // that carries both this update and the cleared flag).
    stopProcessing(finalizeOriginatingTid);

    // CHANGE 10 Fix 3: Reset throttle so next persist happens immediately with correct content
    // This ensures the finalized message is persisted ASAP, not with a stale version
    lastPersistTimeRef.current = 0;

    // NOTE: Transaction recording is NOT done here - custom handlers (like handleDeepResearch)
    // are responsible for recording their own transactions. This prevents duplicates when
    // custom handlers call both finalizeStreamingMessage and recordTransaction.
  }, [stopProcessing]);

  // ID-based finalize (immune to React dispatch race conditions)
  const finalizeStreamingMessageById = useCallback((id: string, final: Partial<ChatMessage>, explicitOriginatingThreadId?: string | null) => {
    // Phase 5: explicit thread over the shared ref (see finalizeStreamingMessage).
    const finalizeOriginatingTid = resolveOriginatingThreadId(explicitOriginatingThreadId, currentSendThreadIdRef.current);
    // CHANGE 10 Fix 3: Cancel any pending persist that captured stale (empty) state
    if (pendingPersistRef.current) {
      clearTimeout(pendingPersistRef.current);
      pendingPersistRef.current = null;
      console.log('[GlobalChat] Cancelled pending persist to prevent stale data (ID-based)');
    }

    // Force fallback content for empty orchestrator messages
    const FALLBACK_CONTENT = "I encountered an issue processing your request. Please try again.";
    let text = final.text;
    let html = final.html;

    // 2026-07-14 incident hardening: an empty finalize on these families
    // means a turn DIED mid-flight. The silent-removal branch below then
    // deleted the widget AND released processing — pane blank, chips dead,
    // nothing ever arrives ("nothing came back, nothing spinning"). Inject
    // the honest fallback instead so the turn ends VISIBLY. Plain chat
    // messages (no sourceTag) keep the removal path — empty finalize there
    // is benign cleanup.
    const finalTag = String((final as any)?.sourceTag || '');
    const isOrchestratorMessage = finalTag.includes('orchestrator')
      || finalTag.startsWith('deep_research')
      || finalTag.startsWith('prest0n_session')
      || finalTag.startsWith('research_result')
      || finalTag.startsWith('stage1_assessment');
    if (isOrchestratorMessage && (!text || !text.trim()) && (!html || !html.trim())) {
      console.warn('[GlobalChat] Injecting fallback content for empty orchestrator message (ID-based)', {
        id,
        sourceTag: (final as any)?.sourceTag,
      });
      text = FALLBACK_CONTENT;
      html = markdownToHtml(FALLBACK_CONTENT);
    }

    // Validate content - prevent blank messages
    const hasContent = (text && text.trim().length > 0) || (html && html.trim().length > 0);

    if (!hasContent) {
      console.warn('[GlobalChat] Attempted to finalize streaming message with empty content (ID-based) - removing instead', {
        id,
        sourceTag: (final as any)?.sourceTag,
      });
      dispatch({ type: 'REMOVE_MESSAGE_BY_ID', payload: { id } });
      // Clear processing state AFTER removing the message (the streaming
      // lease follows the processing flag via the derived effect).
      stopProcessing(finalizeOriginatingTid);
      return;
    }

    // CRITICAL: Dispatch the message update FIRST, before clearing the streaming flag
    // or stopping processing. If we clear the flag first, the cross-tab sync poll can
    // fire a SET_MESSAGES from localStorage (which lacks in-memory streaming messages),
    // blowing away the message before UPDATE_MESSAGE_BY_ID reaches the reducer.
    console.log('[GlobalChat] Finalizing message by ID:', { id, sourceTag: (final as any)?.sourceTag });
    dispatch({ type: 'UPDATE_MESSAGE_BY_ID', payload: { id, message: { ...final, text, html } } });

    persistFinalizedStreamingMessageToThreadStorage({
      originatingThreadId: finalizeOriginatingTid,
      readThreadMessages: readThreadMessagesRef.current,
      writeThreadMessages: writeThreadMessagesRef.current,
      target: { id },
      final,
      text,
      html,
    });

    // NOW clear processing state — the message is safely in the reducer queue
    // (the streaming lease releases from the derived effect after commit).
    stopProcessing(finalizeOriginatingTid);

    // Reset throttle so next persist happens immediately with correct content
    lastPersistTimeRef.current = 0;

    // Force immediate Firestore sync when message has attachments.
    // This ensures PDF/DOCX attachments reach Firestore before the user navigates away,
    // enabling cross-device download via storageUrl persisted in attachments_metadata.
    if (final.attachmentsSerialized?.length || final.attachments?.length) {
      setTimeout(() => {
        console.log('[GlobalChat] Dispatching force-sync for attachment persistence');
        window.dispatchEvent(new Event('global-chat-force-sync'));
      }, 150); // Small delay to let React state settle and localStorage persist complete
    }
  }, [stopProcessing]);

  const removeMessage = useCallback((index: number): void => {
    console.log('[GlobalChat] Removing message at index:', index);
    dispatch({
      type: 'REMOVE_MESSAGE',
      payload: { index },
    });
  }, []);

  // ID-based remove (immune to React dispatch race conditions)
  const removeMessageById = useCallback((id: string): void => {
    console.log('[GlobalChat] Removing message by ID:', id);
    dispatch({
      type: 'REMOVE_MESSAGE_BY_ID',
      payload: { id },
    });
  }, []);

  // ============================================================================
  // G1 (2026-07-15) — Guest Prest0n chat turn (settled signed-out dispatch)
  // ============================================================================
  // Replaces the Round-J sign-in prompt at BOTH settled-signed-out sites
  // (handleSend fall-through + the programmatic gate) with a real, cloud-only
  // guest turn. The caller's envelope already appended the user bubble; this
  // owns the rest of the turn's visible lifecycle: per-thread processing flag,
  // thinking widget, send-ack watchdog, and exactly one visible outcome —
  // the assistant reply, the server's friendly quota/error notice, or the
  // retryable failure bubble. Signed-in flows never reach this (both call
  // sites guard strictly on settled signed-out).
  //
  // Finding 6 (adversarial review 2026-07-15) — abort across the auth
  // transition: an in-flight guest turn is aborted when auth resolves to
  // SIGNED-IN (and on unmount), and the reply is re-checked against the
  // current auth state before it is appended — a guest-charter reply must
  // never land in a now-member thread.
  const guestTurnAbortControllersRef = useRef<Set<AbortController>>(new Set());
  const guestSignedInRef = useRef<boolean>(Boolean(chatAccountId));
  useEffect(() => {
    guestSignedInRef.current = Boolean(chatAccountId);
    if (chatAccountId) {
      for (const controller of guestTurnAbortControllersRef.current) {
        try { controller.abort(); } catch { /* already settled */ }
      }
      guestTurnAbortControllersRef.current.clear();
    }
  }, [chatAccountId]);
  useEffect(() => () => {
    // Unmount: no continuation should keep a billed guest request alive.
    for (const controller of guestTurnAbortControllersRef.current) {
      try { controller.abort(); } catch { /* already settled */ }
    }
    guestTurnAbortControllersRef.current.clear();
  }, []);

  const runGuestChatTurn = useCallback((threadId: string | null, text: string): void => {
    // History from the ORIGINATING thread's fresh snapshot (the user bubble
    // for this turn was already persisted synchronously by the envelope).
    const snapshot: ChatMessage[] =
      (threadId && readThreadMessagesRef.current)
        ? (readThreadMessagesRef.current(threadId) ?? [])
        : (messagesSyncRef.current || []);
    const history = buildGuestChatHistory(snapshot, text);

    if (threadId) startProcessing(threadId);
    const thinking = startStreamingMessage({
      text: 'thinking...',
      html: getPendingWidgetHtml('thinking'),
      sourceTag: 'immediate_thinking_pending',
    }, threadId);
    // Armed AFTER the widget so it can't self-ack (Round J discipline). The
    // reply's addMessage below is the turn-scoped ack. Finding 3: armed with
    // the GUEST window (75s) — the default 25s fired mid-turn (45s server
    // model budget) and offered a Retry while the billed call was in flight.
    armSendAckWatchdog(threadId, text, GUEST_SEND_ACK_TIMEOUT_MS);

    // Finding 6: abortable across the auth transition + unmount.
    const abortController = new AbortController();
    guestTurnAbortControllersRef.current.add(abortController);

    void (async () => {
      let response: GuestChatTurnResponse | null = null;
      let aborted = false;
      try {
        response = await callGuestChatTurn({
          message: text,
          history,
          route: (typeof window !== 'undefined' && window.location?.pathname) || '/',
          deviceId: getGuestDeviceId(),
          hasSignedInBefore: hasSignedInBeforeMarker(),
          signal: abortController.signal,
        });
      } catch (err) {
        aborted = abortController.signal.aborted;
        if (!aborted) console.warn('[GlobalChat] guest chat turn transport failure', err);
        response = null;
      } finally {
        guestTurnAbortControllersRef.current.delete(abortController);
      }
      if (thinking) {
        if (removeMessageById) removeMessageById(thinking.id);
        else removeMessage(thinking.index);
      }
      // Finding 6: re-check "still signed out" before surfacing anything —
      // if auth flipped to signed-in mid-turn (or the fetch was aborted for
      // that reason / unmount), drop the guest reply silently. stopProcessing
      // releases the thread flag AND acks the send watchdog (the existing
      // branch discipline), so no failure bubble ever fires for the drop.
      if (aborted || guestSignedInRef.current) {
        stopProcessing(threadId);
        return;
      }
      if (response && response.ok) {
        addMessage({
          text: response.reply,
          isUser: false,
          timestamp: new Date(),
          sourceTag: 'guest_chat_reply',
        } as ChatMessage, threadId);
        stopProcessing(threadId);
        return;
      }
      if (response && !response.ok && (response.message || '').trim()) {
        // Server-composed friendly copy (quota chain / kill-switch / model
        // hiccup) — a real visible outcome. A retry bubble here would just
        // re-fail against the same quota, so the copy itself is the surface.
        addMessage({
          text: String(response.message),
          isUser: false,
          timestamp: new Date(),
          sourceTag: 'guest_chat_notice',
        } as ChatMessage, threadId);
        stopProcessing(threadId);
        return;
      }
      // Transport failure / malformed body → the retryable failure bubble.
      stopProcessing(threadId);
      appendSendFailureBubble(threadId, text);
    })();
  }, [startProcessing, stopProcessing, startStreamingMessage, removeMessage, removeMessageById,
      armSendAckWatchdog, addMessage, appendSendFailureBubble]);

  // ============================================================================
  // Point 6 (Option B) — server-side durable GUEST deep research
  // ============================================================================
  // Resume polling a server-side guest research task by its persisted
  // serverTaskId and finalize the matching in-flight message. Used both for
  // hydrate re-attach (after a hard refresh) and could be reused elsewhere.
  const guestResumeInFlightRef = useRef<Set<string>>(new Set());
  const resumeGuestServerTask = useCallback(
    // Returns 'final' when the task reached a terminal render (report or
    // interrupted card) and 'retryable' when the server task may still be
    // running (poll wall-clock expired / transient error) — the caller drops
    // retryable ids from its attempted-set so a later pass re-attaches
    // instead of abandoning a live run (NON-DESTRUCTIVE EXPIRY, 2026-07-10).
    async (messageId: string, serverTaskId: string, promptPreview?: string, originThreadId?: string | null): Promise<'final' | 'retryable'> => {
      if (!serverTaskId || !messageId) return 'final';
      // De-dupe concurrent resumes for the same task across re-renders.
      if (guestResumeInFlightRef.current.has(serverTaskId)) return 'final';
      guestResumeInFlightRef.current.add(serverTaskId);
      // CHIP RE-LIGHT (Symptom 4, 2026-07-10): processingByThread is
      // in-memory only and zeroed by any unmount/refresh, so a resumed run
      // previously re-showed the message widget but left the thread chip
      // dark. Re-key the originating thread's flag for the re-attach.
      //
      // CROSS-THREAD DELIVERY (owner concurrent-turn repro 2026-08-16): the
      // originating thread is the thread that OWNS the scanned placeholder —
      // the caller effect captures it in the SAME pass that read the
      // transcript and passes it EXPLICITLY. Re-reading "active" here raced
      // thread switches: a stale capture bound the whole delivery (finalize +
      // start/stopProcessing) to the WRONG thread, and the storage-persist
      // fallback then APPENDED the finished answer into that thread's
      // transcript (streamFinalizePersist appends when the id isn't found) —
      // the calendar answer rendered into the email thread.
      // T3 HARDENING (2026-08-22, apps-answer-in-research-thread): the
      // ACTIVE-thread bridge fallback is RETIRED — the only in-repo caller
      // passes the scanned origin, and re-reading "active" is exactly the
      // wrong-thread landing mechanism. An origin-less call is a caller
      // bug: warn loudly and hold the delivery store-side (null never
      // appends into a pane).
      if (originThreadId === undefined) {
        console.warn('[GlobalChat] resumeGuestServerTask called with NO origin thread — delivery held store-side', { serverTaskId });
      }
      const resumeThreadId = originThreadId ?? null;
      if (resumeThreadId) startProcessing(resumeThreadId);
      try {
        // Show a live spinner again while we re-attach.
        updateStreamingMessageById(messageId, {
          html: getPendingWidgetHtml('resuming deep research'),
          sourceTag: 'prest0n_session_streaming',
          status: 'streaming',
        }, { appendIfMissingForThread: resumeThreadId });

        const pollResult = await pollGuestTaskUntilDone(serverTaskId, {
          onStage: (s: GuestServerResearchStage) => {
            if (s?.label) updateStreamingMessageById(messageId, { html: getPendingWidgetHtml(s.label) }, { appendIfMissingForThread: resumeThreadId });
          },
        });

        if (pollResult.status === 'timeout') {
          // The SERVER task is still running — the poll just outlasted its
          // wall clock. Keep the widget pending with serverTaskId intact so
          // this pass's caller (or the next hydrate) re-attaches; never
          // downgrade a live run to an interrupted card.
          updateStreamingMessageById(messageId, {
            html: getPendingWidgetHtml('research is still running — this will update automatically'),
            sourceTag: 'prest0n_session_streaming',
            status: 'streaming',
            serverTaskId,
          }, { appendIfMissingForThread: resumeThreadId });
          return 'retryable';
        }

        if (pollResult.status === 'complete' && pollResult.result) {
          const reportContent = ensureReferencesSection(
            stripThinkingBlocks(pollResult.result.report || ''),
          );
          const serverAttachments = Array.isArray(pollResult.result.attachments)
            ? pollResult.result.attachments
            : [];
          finalizeStreamingMessageById(messageId, {
            text: reportContent,
            html: formatResearchToHtml(reportContent),
            sourceTag: 'research_result',
            status: 'complete',
            interrupted: false,
            regeneratePrompt: undefined,
            serverTaskId: undefined,
            attachmentsSerialized: serverAttachments.length > 0 ? serverAttachments : undefined,
            // Phase 5 (adversarial-review Finding 1): this resume finalize runs
            // as a long-lived async continuation NOT wrapped by
            // withOriginatingThread; without the explicit thread it would
            // resolve via the shared ref (clobbered by any concurrent send) and
            // misroute A's report / clear B's spinner. resumeThreadId is the
            // resumed message's thread (active-at-resume-start).
          }, resumeThreadId);
          return 'final';
        }

        // Genuinely gone / errored — NOW fall back to the interrupted card so the
        // user can regenerate (the only case the interrupted UI is correct for a
        // server task). Use UPDATE so we keep the message id/history continuity.
        // 2026-06 reliability overhaul: pollResult.error is a server-provided
        // string with no structured errorInfo on the guest endpoint — pass it
        // through the raw-dump filter so JSON/stack payloads can never render
        // into the card (human notices like the client-built timeout message
        // still pass).
        const safeGuestError = sanitizeServerErrorNotice(pollResult.error);
        const interruptedNotice =
          pollResult.status === 'not_found'
            ? 'This research session expired or could not be found. Tap Regenerate to run it again.'
            : (safeGuestError || 'This research could not be completed. Tap Regenerate to run it again.');
        finalizeStreamingMessageById(messageId, {
          text: interruptedNotice,
          html: `<div class="interrupted-turn-notice" role="status" data-interrupted="true"${
            promptPreview ? ` data-regenerate-prompt="${(promptPreview || '').replace(/"/g, '&quot;')}"` : ''
          } style="display:flex;flex-direction:column;gap:4px;padding:12px 16px;border-radius:12px;background:#FBF7EE;border:1px solid rgba(198,167,94,0.25);font-size:13px;color:#8a6d2f;"><span style="font-weight:600;">Interrupted</span><span>${interruptedNotice}</span></div>`,
            sourceTag: 'interrupted_turn',
            status: 'interrupted',
            interrupted: true,
            regeneratePrompt: promptPreview,
            serverTaskId: undefined,
          }, resumeThreadId);
        return 'final';
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return 'final';
        console.warn('[GlobalChat] guest research resume failed:', err);
        // Transient (network) failure — the server task may well be alive;
        // let the caller retry on a later pass.
        return 'retryable';
      } finally {
        guestResumeInFlightRef.current.delete(serverTaskId);
        if (resumeThreadId) stopProcessing(resumeThreadId);
      }
    },
    [updateStreamingMessageById, finalizeStreamingMessageById, startProcessing, stopProcessing],
  );

  // Wire the guest-server-research config into globalTools' sharedContext once
  // the migration gate resolves. enabled === guest surface (no migrated
  // session) — the migrated/paid path never uses the legacy deepResearchTool.
  useEffect(() => {
    if (!globalTools.setGuestServerResearch) return;
    if (isMigrationGateChecking) return; // wait until guest/migrated is known
    // Point 6 auth-init race fix: don't enable guest config during the auth
    // gap. selectResumeMode returns 'wait' until auth has resolved AND the
    // gate has settled; only then is guest-vs-authed real.
    const mode = selectResumeMode({ authHasResolved, isMigrationGateChecking, prest0nSessionAccountId });
    if (mode === 'wait') return;
    const isGuest = mode === 'guest';
    globalTools.setGuestServerResearch(
      isGuest
        ? {
            enabled: true,
            persistServerTaskId: (messageId: string, taskId: string) => {
              // Stamp the taskId onto the in-flight message so it persists
              // (localStorage thread store / Firestore) and survives a refresh.
              updateStreamingMessageById(messageId, { serverTaskId: taskId });
            },
          }
        : { enabled: false },
    );
  }, [
    globalTools.setGuestServerResearch,
    authHasResolved,
    isMigrationGateChecking,
    prest0nSessionAccountId,
    updateStreamingMessageById,
  ]);

  // ──────────────────────────────────────────────────────────────────────────
  // PER-TURN resume guard (concurrent-turn state stomp fix, 2026-08-16).
  // The skip decision keys on the TASK'S OWN turn in the session hook's
  // per-turn ledger — never the shared "latest send" fields, which a
  // concurrent thread's poll used to stomp (post-#855 Fernando repro: thread
  // B's terminal tick cleared thread A's live-poll indication mid-run, so
  // these effects could re-attach a SECOND poller to a task whose live turn
  // was still driving it). A tracked turn in ANY phase pins the id: live
  // phases skip via the flags; a terminal ledger entry skips via id-equality
  // (the old "last terminal activeTask" guard, now per-task instead of
  // last-writer-wins). Legacy/test-double hook shapes without the accessor
  // fall back to the shared fields (same discipline as the sync
  // active-thread bridge's state fallback).
  const liveTurnResumeGuard = useCallback(
    (serverTaskId: string | null | undefined): Pick<
      Parameters<typeof shouldSkipLiveTaskResume>[0],
      'activeTaskId' | 'isPollingTask' | 'isStreaming'
    > => {
      if (typeof prest0nSessionHook.getTurnForTask !== 'function') {
        return {
          activeTaskId: prest0nSessionHook.activeTask?.taskId,
          isPollingTask: !!prest0nSessionHook.isPollingTask,
          isStreaming: !!prest0nSessionHook.isStreaming,
        };
      }
      const turn = prest0nSessionHook.getTurnForTask(serverTaskId);
      return {
        activeTaskId: turn ? (serverTaskId || '').trim() : null,
        isPollingTask: !!turn && turn.phase === 'polling',
        isStreaming: !!turn && turn.phase === 'streaming',
      };
    },
    [
      prest0nSessionHook.getTurnForTask,
      prest0nSessionHook.liveTurnsVersion,
      prest0nSessionHook.activeTask?.taskId,
      prest0nSessionHook.isPollingTask,
      prest0nSessionHook.isStreaming,
    ],
  );

  // Hydrate re-attach: after messages hydrate, find any in-flight assistant
  // message that carries a serverTaskId (a guest research run that was live
  // when the tab was hard-refreshed) and RESUME polling it. The server task
  // kept running, so this delivers the memo instead of a dead spinner.
  const guestResumeAttemptedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // Point 6 auth-init race fix: only run the GUEST resume path once auth has
    // resolved AND the gate settled on the guest surface. During the auth gap
    // selectResumeMode returns 'wait' so the in-flight message keeps its
    // spinner instead of being finalized as a (wrong) guest "session expired".
    if (selectResumeMode({ authHasResolved, isMigrationGateChecking, prest0nSessionAccountId }) !== 'guest') return;
    const resumable = findResumableServerTasks(state.messages);
    // CROSS-THREAD DELIVERY (2026-08-16): the placeholders above came out of
    // state.messages — the ACTIVE thread's transcript — so the thread that
    // owns them is the active thread AT SCAN TIME. Capture it once, here, and
    // pass it into every resume so the delivery target is pinned to the
    // placeholder's thread (never re-read mid-poll, minutes later).
    const scanThreadId =
      (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    for (const r of resumable) {
      if (!r.messageId || !r.serverTaskId) continue;
      // INCIDENT-20260818 ("the response popped up in another thread"): the
      // scan PAIRING itself races a thread switch — state.messages can still
      // hold the previous thread's transcript while the active-thread ref
      // already points at the next one (any message mutation in the old slot
      // re-fires this effect inside that window). Delivering on a mispaired
      // scan pins the resume — and streamFinalizePersist's append-on-miss —
      // to a thread that never owned the placeholder. Verify ownership
      // against the named thread's OWN storage (every bubble is sync-written
      // there at add time, PR #114 sub-fix A); a mismatch skips this pass
      // only — nothing is marked attempted — and a later pass or the
      // all-threads discovery re-attaches with a truthful pairing.
      if (scanThreadId && readThreadMessagesRef.current) {
        const owned = (readThreadMessagesRef.current(scanThreadId) ?? [])
          .some((m) => m?.id === r.messageId);
        if (!owned) continue;
      }
      if (
        shouldSkipLiveTaskResume({
          serverTaskId: r.serverTaskId,
          // PER-TURN (2026-08-16): guard against the task's OWN turn — a
          // concurrent thread's send/poll can no longer open this gate.
          ...liveTurnResumeGuard(r.serverTaskId),
          isRegisteredLiveSend: isLiveSendInFlight(r.serverTaskId),
        })
      ) {
        continue;
      }
      if (guestResumeAttemptedRef.current.has(r.serverTaskId)) continue;
      guestResumeAttemptedRef.current.add(r.serverTaskId);
      const taskId = r.serverTaskId;
      void resumeGuestServerTask(r.messageId, taskId, r.promptPreview, scanThreadId).then((disposition) => {
        // NON-DESTRUCTIVE EXPIRY: a retryable outcome (poll wall-clock
        // expired / transient error) means the server task may still be
        // running — allow a later pass to re-attach instead of abandoning it.
        if (disposition === 'retryable') guestResumeAttemptedRef.current.delete(taskId);
      });
    }
  }, [
    state.messages,
    authHasResolved,
    isMigrationGateChecking,
    prest0nSessionAccountId,
    liveTurnResumeGuard,
    resumeGuestServerTask,
  ]);

  // ============================================================================
  // Point 6 (Option B) — AUTHENTICATED-path durable server-task re-attach
  // ============================================================================
  // Mirror of resumeGuestServerTask for the migrated / account-VM surface. The
  // authed turn runs as a durable server-side background task; on a hard
  // refresh the in-flight message carries the serverTaskId stamped by
  // useGlobalTools.onTaskQueued. Here we re-poll the AUTHENTICATED task-status
  // endpoint (getPrest0nTaskStatus -> cloud fn prest0nGetTaskStatus) and
  // finalize the message with task.result.finalResponse (+ attachments),
  // mirroring the success branch in usePrest0nSession.onTaskQueued's
  // completed-handler. Only when the task is genuinely gone/failed do we fall
  // back to the Interrupted+Regenerate card.
  const authedResumeInFlightRef = useRef<Set<string>>(new Set());
  const resumeAuthedServerTask = useCallback(
    // Returns 'final' when the task reached a terminal render and 'retryable'
    // when the server task may still be running (poll wall-clock expired /
    // transient error) — the caller drops retryable ids from its attempted-set
    // so a later pass re-attaches (NON-DESTRUCTIVE EXPIRY, 2026-07-10).
    async (messageId: string, serverTaskId: string, promptPreview?: string, originThreadId?: string | null): Promise<'final' | 'retryable'> => {
      if (!serverTaskId || !messageId) return 'final';
      if (authedResumeInFlightRef.current.has(serverTaskId)) return 'final';
      authedResumeInFlightRef.current.add(serverTaskId);
      // CHIP RE-LIGHT (Symptom 4, 2026-07-10): see resumeGuestServerTask —
      // re-key the originating thread's processing flag for the re-attach.
      //
      // CROSS-THREAD DELIVERY (owner concurrent-turn repro 2026-08-16, the
      // calendar answer rendered into the email thread): the delivery thread
      // is the thread that OWNS the scanned placeholder, captured by the
      // caller effect in the same pass that read the transcript and passed
      // EXPLICITLY — never re-read from "active" here, where a raced thread
      // switch bound this turn's finalize (and the streamFinalizePersist
      // append-when-id-missing fallback) to whatever thread the user was
      // viewing instead.
      // T3 (2026-08-22): the active-thread bridge fallback is RETIRED — an
      // origin-less resume must never adopt whatever thread the user happens
      // to be viewing (that adoption is the cross-thread delivery bug). The
      // sole caller passes scanThreadId explicitly; a null origin holds
      // delivery store-side (by-id dispatches are id-scoped and safe).
      if (originThreadId === undefined) {
        console.warn('[GlobalChat] resumeAuthedServerTask called with NO origin thread — delivery held store-side', { serverTaskId });
      }
      const resumeThreadId = originThreadId ?? null;
      if (resumeThreadId) startProcessing(resumeThreadId);
      try {
        // R13 (2026-08-06, owner repro): the resume pill sat frozen on
        // "resuming your reply" for the whole turn because VM turns carry no
        // stage labels — nothing ever upgraded it. The pill now always shows
        // a live elapsed clock (from the task id's mint timestamp), so a
        // returning user sees real progress motion even with no stage feed.
        // Parallel branches: a resumed pill carries the ledger's rows too.
        const resumeWidgetHtml = (task: { branches?: unknown; branchOrder?: unknown } | null | undefined, label: string): string => {
          const entries = orderBranchEntries(task?.branches, task?.branchOrder);
          return entries.length > 0 ? getPendingWidgetWithBranchesHtml(label, entries, Date.now()) : getPendingWidgetHtml(label);
        };
        const resumeElapsedLabel = (prefix: string): string => {
          const baseMs = taskMintTimestampMs(serverTaskId) || Date.now();
          const totalSec = Math.max(0, Math.floor((Date.now() - baseMs) / 1000));
          const m = Math.floor(totalSec / 60);
          const s = totalSec % 60;
          const elapsed = m > 0 ? `${m}m ${s}s` : `${s}s`;
          return `${prefix} — ${elapsed} elapsed · updates automatically`;
        };
        // Show a live spinner again while we re-attach.
        updateStreamingMessageById(messageId, {
          html: getPendingWidgetHtml(resumeElapsedLabel('resuming your reply')),
          sourceTag: 'prest0n_session_streaming',
          status: 'streaming',
        }, { appendIfMissingForThread: resumeThreadId });

        // Re-poll the authenticated task status until terminal. Wall-clock
        // bounded (30 min) with 2s→10s backoff — long deep-research turns
        // legitimately outlive the old 12-min fixed-interval ceiling, and the
        // server's stale-task sweep is the authority that eventually flips a
        // genuinely dead task to 'failed' (which lands here as a terminal
        // decision). Expiry without a terminal status is NOT treated as gone.
        const RESUME_WALL_CLOCK_MS = 30 * 60 * 1000;
        const startedAt = Date.now();
        let pollIntervalMs = 2000;
        let decision: ReturnType<typeof decideAuthedResumeFinalize> = null;
        // Session-hardening S1: a missing record is NOT terminal on first
        // sight. The pre-flight stamp makes the widget's task id exist before
        // the server task doc does, and a single 404/network blip used to
        // paint the interrupted card over a healthy in-flight turn. Require a
        // miss streak AND minimum task age (mint ts is encoded in the id)
        // before finalizing as not-found; transient misses keep polling.
        const mintTsMs = taskMintTimestampMs(serverTaskId);
        let consecutiveMisses = 0;
        // EMPTY-DELIVERY RETRY HANDOFF (2026-08-05): the polled id can be
        // re-pointed ONCE at the server's silent salvage retry when the
        // original doc reports failed + dispatchMetadata.emptyDeliveryRetriedBy
        // (a retry doc never carries that marker itself, so this cannot loop).
        let pollingTaskId = serverTaskId;
        // INTERSTITIAL-MESSAGE ELIMINATION (2026-08-13): keep the last
        // successfully fetched record around — the 'clarifying' terminal
        // decision rebuilds the Stage-1 card from its currentStage.payload
        // (persisted server-side exactly for recovery like this).
        let lastPolledStagePayload: Record<string, unknown> | null = null;
        let lastPolledStageLabel = '';
        // Stale-label honesty (durability audit rank #9, 2026-07-13): a
        // SIGKILLed task's doc keeps its hours-old stage label; echoing it
        // verbatim forever ("Step 2 of 3…") is the "no error ever shown" face
        // of the zombie-task incident. Track label freshness client-side and
        // switch to an honest holding message once it stops changing.
        const STALE_LABEL_AFTER_MS = 5 * 60 * 1000;
        let lastStageLabel = '';
        let lastLabelChangeAt = Date.now();
        while (Date.now() - startedAt < RESUME_WALL_CLOCK_MS) {
          // Durability audit rank #8: only a served not-found counts toward
          // the miss streak. Network trouble (laptop-wake Wi-Fi
          // re-association, offline, 5xx) keeps polling without ever
          // finalizing a live task as "gone".
          // eslint-disable-next-line no-await-in-loop
          const statusResult = await getPrest0nTaskStatusDetailed(pollingTaskId);
          const latest = statusResult.kind === 'ok' ? statusResult.task : null;
          if (statusResult.kind === 'network_error') {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
            pollIntervalMs = Math.min(10000, Math.round(pollIntervalMs * 1.3));
            continue;
          }
          if (statusResult.kind === 'not_found') {
            consecutiveMisses += 1;
            if (!shouldTreatMissingTaskAsTerminal({ consecutiveMisses, mintTsMs, nowMs: Date.now() })) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
              pollIntervalMs = Math.min(10000, Math.round(pollIntervalMs * 1.3));
              continue;
            }
          } else {
            consecutiveMisses = 0;
          }
          // INTERSTITIAL-MESSAGE ELIMINATION (2026-08-13): remember the
          // freshest clarifying-card payload the task doc carries. The
          // generic-pulse writer guarantees a payload-carrying stage is never
          // clobbered (taskQueue buildTaskStageStreamCallback), so on a
          // clarifying turn this holds the card's structured content.
          const polledStagePayload =
            latest?.currentStage?.stage === 'clarifying-questions'
              ? latest?.currentStage?.payload
              : null;
          if (polledStagePayload && typeof polledStagePayload === 'object') {
            lastPolledStagePayload = polledStagePayload as Record<string, unknown>;
            lastPolledStageLabel = (latest?.currentStage?.label || '').trim();
          }
          // Language for a failed task's errorInfo notice mirrors the user's
          // original prompt (2026-06 reliability overhaul).
          decision = decideAuthedResumeFinalize(latest, {
            language: detectInputLanguage(promptPreview || ''),
          });
          if (decision && decision.kind === 'retrying') {
            // Follow the salvage retry: keep the in-progress widget and poll
            // the retry doc; ITS terminal state decides what the user sees.
            pollingTaskId = decision.retryTaskId;
            // Read before the reset below nulls it — the compiler caught this.
            const chainReason = decision.reason;
            decision = null;
            consecutiveMisses = 0;
            // The waiting line depends on WHICH chain this is, and the two
            // are nothing alike (2026-09-08). A salvage retry really is one
            // more pass, seconds away. A long document is still being
            // written, and since the hop ceiling went 2 -> 12 it can run for
            // tens of minutes — telling that user "almost there" is not vague,
            // it is false, and it is the only thing they are told for the
            // whole wait. House voice: say what is happening and that it will
            // land here, promise no clock we cannot keep.
            updateStreamingMessageById(messageId, {
              html: getPendingWidgetHtml(chainReason === 'doc-continue'
                ? 'still writing — this one runs long. it\'ll land here when it\'s done, even if you close the tab'
                : 'almost there — taking one more pass'),
            }, { appendIfMissingForThread: resumeThreadId });
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
            continue;
          }
          if (decision) break; // terminal (completed/failed/confirmed-not-found)
          // Surface the freshest stage label while we wait — but never echo a
          // label that has stopped changing for 5+ minutes as if it were live
          // (rank #9). The server sweeper terminalizes genuinely dead tasks
          // within ~30 minutes; until then, be honest about the wait.
          const stageLabel = (latest?.currentStage?.label || '').trim();
          if (stageLabel && stageLabel !== lastStageLabel) {
            lastStageLabel = stageLabel;
            // Stale-on-arrival honesty (2026-08-14, Harbor Ledger repro): a
            // re-attach used to reset freshness to "now", which re-lit a dead
            // task's minutes-old label as "live" for a fresh 5 minutes. Seed
            // freshness from the SERVER's stage timestamp when it parses;
            // "now" stays the fallback for transports that mangle the shape.
            const serverStampMs = firestoreishTimestampMs(latest?.currentStage?.timestamp);
            lastLabelChangeAt = serverStampMs ?? Date.now();
          }
          const labelIsStale = Date.now() - lastLabelChangeAt > STALE_LABEL_AFTER_MS;
          if (stageLabel && !labelIsStale) {
            // R13: stage labels also carry the elapsed clock so the pill
            // visibly ticks between stage transitions.
            updateStreamingMessageById(messageId, { html: resumeWidgetHtml(latest, resumeElapsedLabel(stageLabel)) }, { appendIfMissingForThread: resumeThreadId });
          } else if (labelIsStale) {
            updateStreamingMessageById(messageId, {
              html: resumeWidgetHtml(latest, resumeElapsedLabel('still working — taking longer than expected')),
            }, { appendIfMissingForThread: resumeThreadId });
          } else {
            // R13: no stage feed at all (ordinary VM turns) — the ticking
            // clock IS the progress display.
            updateStreamingMessageById(messageId, {
              html: resumeWidgetHtml(latest, resumeElapsedLabel('working on your reply')),
            }, { appendIfMissingForThread: resumeThreadId });
          }
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
          pollIntervalMs = Math.min(10000, Math.round(pollIntervalMs * 1.3));
        }

        if (!decision) {
          // Wall clock expired while the task is still pending/running on the
          // server. Keep the widget pending with serverTaskId intact — never
          // downgrade a live run to an interrupted card (the exact "deep
          // research died while I was away" symptom this fixes).
          updateStreamingMessageById(messageId, {
            html: getPendingWidgetHtml('still working — this will update automatically'),
            sourceTag: 'prest0n_session_streaming',
            status: 'streaming',
            serverTaskId,
          }, { appendIfMissingForThread: resumeThreadId });
          return 'retryable';
        }

        const finalDecision: NonNullable<typeof decision> = decision;

        if (finalDecision.kind === 'completed') {
          // Re-attach the turn's usage so the recovered message keeps its ⓘ
          // popup (model codename + tokens + 5X cost) — previously every
          // refresh-recovery finalize dropped tokenUsage entirely.
          const resumedUsage = finalDecision.usage
            ? {
                inputTokens: finalDecision.usage.inputTokens,
                outputTokens: finalDecision.usage.outputTokens,
                reasoningTokens: finalDecision.usage.reasoningTokens,
                model: finalDecision.usage.model,
                provider: finalDecision.usage.provider as any,
                upstreamCostUsd: finalDecision.usage.upstreamCostUsd ?? undefined,
                timestamp: new Date(),
              }
            : undefined;
          const resumedUsages =
            finalDecision.tokenUsages && finalDecision.tokenUsages.length > 0
              ? finalDecision.tokenUsages.map((u) => ({
                  inputTokens: u.inputTokens,
                  outputTokens: u.outputTokens,
                  reasoningTokens: u.reasoningTokens,
                  model: u.model,
                  provider: u.provider as any,
                  upstreamCostUsd: u.upstreamCostUsd ?? undefined,
                  timestamp: new Date(),
                }))
              : undefined;
          finalizeStreamingMessageById(messageId, {
            text: finalDecision.finalResponse,
            // Authed answers are finalized via markdownToHtml elsewhere
            // (see useGlobalTools onDone finalize path) — match that here so
            // the re-attached answer renders identically to a live one.
            html: markdownToHtml(finalDecision.finalResponse),
            sourceTag: 'prest0n_session_complete',
            status: 'complete',
            interrupted: false,
            regeneratePrompt: undefined,
            serverTaskId: undefined,
            // Spread-conditional: an explicitly-present undefined key would
            // OVERWRITE usage already restored from thread storage in the
            // reducer's {...existing, ...updates} merge (older task docs
            // legitimately lack result.usage).
            ...(resumedUsage ? { tokenUsage: resumedUsage } : {}),
            ...(resumedUsages ? { tokenUsages: resumedUsages } : {}),
            attachmentsSerialized:
              finalDecision.attachmentsSerialized && finalDecision.attachmentsSerialized.length > 0
                ? finalDecision.attachmentsSerialized
                : undefined,
            // Phase 5 (Finding 1): explicit resume thread — see guest resume.
          }, resumeThreadId);
          return 'final';
        }

        // INTERSTITIAL-MESSAGE ELIMINATION (owner directive 2026-08-13): a
        // completed interactive clarifying turn must NEVER finalize into a
        // text bubble. The old path painted the one-line skip breadcrumb
        // (surfaceOwnedText) as a standalone assistant message and attached
        // the turn's token usage to that phantom bubble while the real card
        // showed "no extensive compute was necessary". Rebuild the structured
        // card from the task doc's persisted stage payload
        // (currentStage.payload — written server-side exactly for recovery)
        // and attach the usage to IT, the most recent real message; when no
        // payload survived, clear the placeholder silently — never a
        // breadcrumb, never an Interrupted card.
        if (finalDecision.kind === 'clarifying') {
          const clarifyingUsage = finalDecision.usage
            ? {
                inputTokens: finalDecision.usage.inputTokens,
                outputTokens: finalDecision.usage.outputTokens,
                reasoningTokens: finalDecision.usage.reasoningTokens,
                model: finalDecision.usage.model,
                provider: finalDecision.usage.provider as any,
                upstreamCostUsd: finalDecision.usage.upstreamCostUsd ?? undefined,
                timestamp: new Date(),
              }
            : undefined;
          const clarifyingUsages =
            finalDecision.tokenUsages && finalDecision.tokenUsages.length > 0
              ? finalDecision.tokenUsages.map((u) => ({
                  inputTokens: u.inputTokens,
                  outputTokens: u.outputTokens,
                  reasoningTokens: u.reasoningTokens,
                  model: u.model,
                  provider: u.provider as any,
                  upstreamCostUsd: u.upstreamCostUsd ?? undefined,
                  timestamp: new Date(),
                }))
              : undefined;
          const payload = lastPolledStagePayload as {
            initialFindings?: unknown;
            clarifyingQuestions?: unknown;
            language?: unknown;
            originalQuery?: unknown;
            clarifyingRound?: unknown;
            clarifyingMaxRounds?: unknown;
          } | null;
          const rebuiltQuestions = Array.isArray(payload?.clarifyingQuestions)
            ? (payload!.clarifyingQuestions as Array<{ question?: unknown } | null>)
                .map((q) => (q && typeof q.question === 'string' ? q.question.trim() : ''))
                .filter((q): q is string => Boolean(q))
            : [];
          // ANSWER-FIRST (2026-08-20): zero-question cards are now a normal,
          // common shape (the answer + strategic offer). Rebuild whenever the
          // payload carries EITHER questions or findings — the old
          // questions-only gate dropped answer cards to the "could not be
          // restored / Regenerate" notice on refresh.
          const rebuiltFindings =
            typeof payload?.initialFindings === 'string' ? payload.initialFindings.trim() : '';
          let cardRebuilt = false;
          if (payload && (rebuiltQuestions.length > 0 || rebuiltFindings)) {
            try {
              const lang: 'en' | 'es' = payload.language === 'es' ? 'es' : 'en';
              // Mirror useGlobalTools' onTaskStatus card construction exactly
              // (same button id + hidden secondary, findings-first shape), so
              // the resume-rebuilt card behaves identically: Skip routes
              // through the task-queue skip handler; answers go below in chat.
              const { mainHtml, buttonsHtml } = formatDeepResearchQualificationHtml(
                typeof payload.initialFindings === 'string' ? payload.initialFindings : '',
                null,
                rebuiltQuestions,
                'task-queue-dr-clarifying-skip-btn',
                GLOBAL_VIDEO_SCRIPT_BUTTON_IDS.videoScript,
                undefined,
                lang,
                // FINDINGS-FIRST REDESIGN (2026-08-19): one button; the
                // card's helper line owns the answer-below affordance.
                { hidden: true },
                false,
                typeof payload.originalQuery === 'string' ? payload.originalQuery : undefined,
                typeof payload.clarifyingRound === 'number' && typeof payload.clarifyingMaxRounds === 'number'
                  ? { round: payload.clarifyingRound as number, maxRounds: payload.clarifyingMaxRounds as number }
                  : undefined,
                // ANSWER-FIRST (2026-08-20): strategic offer parity with the
                // live onTaskStatus construction. The rebuild gate accepts
                // questions OR findings, so zero-question answer cards
                // (offer declined) resume with the same shape they rendered.
                (payload as { offerDeepResearch?: unknown }).offerDeepResearch !== false,
              );
              // Pure answer cards must not carry the qualification tag —
              // midClarifyingFreeTextPreRoute keys on it (see the live
              // finalize path in useGlobalTools for the full rationale).
              const resumeIsPureAnswerCard =
                (payload as { offerDeepResearch?: unknown }).offerDeepResearch === false &&
                rebuiltQuestions.length === 0;
              finalizeStreamingMessageById(messageId, {
                text: lastPolledStageLabel || (payload.language === 'es' ? 'Preguntas aclaratorias' : 'Clarifying questions'),
                html: mainHtml,
                workflowActionButtonsHtml: buttonsHtml,
                sourceTag: resumeIsPureAnswerCard ? 'deep_research_answer' : 'deep_research_qualification',
                status: 'complete',
                interrupted: false,
                regeneratePrompt: undefined,
                serverTaskId: undefined,
                ...(clarifyingUsage ? { tokenUsage: clarifyingUsage } : {}),
                ...(clarifyingUsages ? { tokenUsages: clarifyingUsages } : {}),
              }, resumeThreadId);
              cardRebuilt = true;
            } catch (cardErr) {
              console.warn('[useGlobalChat] clarifying card rebuild on resume failed (falling back to silent clear):', cardErr);
            }
          }
          if (!cardRebuilt) {
            // No structured payload survived — the card cannot be rebuilt
            // faithfully, and this branch is only reachable when the card was
            // NEVER rendered in this browser (a rendered card finalizes the
            // placeholder, so there is nothing left to resume). The honest
            // terminal is the standard retryable card — finalized through the
            // THREAD-ROUTED path like every other terminal branch here, so a
            // background-thread placeholder can never be left spinning
            // (adversarial-review finding: removeMessageById only acts on the
            // ACTIVE thread's in-memory list and persists nothing).
            const clarifyingGoneNotice =
              detectInputLanguage(promptPreview || '') === 'es'
                ? 'Las preguntas aclaratorias ya no están disponibles. Toca Regenerar para ejecutar la solicitud de nuevo.'
                : 'The clarifying questions could not be restored. Tap Regenerate to run this request again.';
            finalizeStreamingMessageById(messageId, {
              text: clarifyingGoneNotice,
              html: buildAuthedInterruptedHtml(clarifyingGoneNotice, promptPreview),
              sourceTag: 'interrupted_turn',
              status: 'interrupted',
              interrupted: true,
              regeneratePrompt: promptPreview,
              serverTaskId: undefined,
              ...(clarifyingUsage ? { tokenUsage: clarifyingUsage } : {}),
              ...(clarifyingUsages ? { tokenUsages: clarifyingUsages } : {}),
            }, resumeThreadId);
          }
          return 'final';
        }

        // F1 (2026-08-03): the user STOPPED this task (cancelled_by_user).
        // Their Stop already produced its own terminal surface — clear the
        // re-attached widget silently instead of painting an Interrupted
        // card over the user's own action after a refresh.
        if (finalDecision.kind === 'cancelled') {
          finalizeStreamingMessageById(messageId, {
            text: 'stopped',
            html: getStoppedWidgetHtml(),
            sourceTag: 'cancelled',
            status: 'complete',
            interrupted: false,
            regeneratePrompt: undefined,
            serverTaskId: undefined,
          }, resumeThreadId);
          return 'final';
        }

        // 'retrying' never escapes the poll loop above (the handoff resets
        // `decision` to null and keeps polling the retry doc) — this branch
        // exists to narrow the union for the compiler. Phrased as a negative
        // check because CI's tsc proves the loop invariant and flags a direct
        // `=== 'retrying'` literal comparison as TS2367 (no overlap). If it
        // ever fired, keeping the widget pending is the safe behavior,
        // mirroring the wall-clock-expired path.
        if (finalDecision.kind !== 'interrupted') {
          updateStreamingMessageById(messageId, {
            html: getPendingWidgetHtml('almost there — taking one more pass'),
            sourceTag: 'prest0n_session_streaming',
            status: 'streaming',
            serverTaskId,
          }, { appendIfMissingForThread: resumeThreadId });
          return 'retryable';
        }

        // Genuinely gone / failed — fall back to the interrupted+regenerate card
        // (UPDATE in place so we keep the message id / history continuity).
        finalizeStreamingMessageById(messageId, {
          text: finalDecision.notice,
          html: buildAuthedInterruptedHtml(finalDecision.notice, promptPreview),
          sourceTag: 'interrupted_turn',
          status: 'interrupted',
          interrupted: true,
          regeneratePrompt: promptPreview,
          serverTaskId: undefined,
        }, resumeThreadId);
        return 'final';
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return 'final';
        console.warn('[GlobalChat] authed server-task resume failed:', err);
        // Transient (network) failure — the server task may well be alive;
        // let the caller retry on a later pass.
        return 'retryable';
      } finally {
        authedResumeInFlightRef.current.delete(serverTaskId);
        if (resumeThreadId) stopProcessing(resumeThreadId);
      }
    },
    [updateStreamingMessageById, finalizeStreamingMessageById, startProcessing, stopProcessing],
  );

  // Hydrate re-attach for the AUTHENTICATED surface. Mirrors the guest effect
  // but runs when prest0nSessionAccountId IS set. Uses a SEPARATE attempted-set
  // ref so it never collides with the guest path. The guest effect early-returns
  // on this surface, so exactly one of the two effects ever fires per message.
  const authedResumeAttemptedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // Point 6 auth-init race fix: only run the AUTHED resume path once auth has
    // resolved AND the gate settled on the authed surface. selectResumeMode
    // returns 'wait' during the auth gap, so this path now reliably wins for
    // authenticated refreshes instead of losing the race to the guest path.
    if (selectResumeMode({ authHasResolved, isMigrationGateChecking, prest0nSessionAccountId }) !== 'authed') return;
    const resumable = findResumableServerTasks(state.messages);
    // CROSS-THREAD DELIVERY (2026-08-16): pin every resume's delivery target
    // to the thread that owns the scanned transcript — see the guest effect.
    const scanThreadId =
      (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    for (const r of resumable) {
      if (!r.messageId || !r.serverTaskId) continue;
      // INCIDENT-20260818: scan-pairing race guard — see the guest effect.
      // The placeholder must exist in the named thread's own storage before
      // this resume may deliver there; a mispaired pass skips silently and a
      // later pass (or all-threads discovery) re-attaches truthfully.
      if (scanThreadId && readThreadMessagesRef.current) {
        const owned = (readThreadMessagesRef.current(scanThreadId) ?? [])
          .some((m) => m?.id === r.messageId);
        if (!owned) continue;
      }
      if (
        shouldSkipLiveTaskResume({
          serverTaskId: r.serverTaskId,
          // PER-TURN (2026-08-16): guard against the task's OWN turn — a
          // concurrent thread's send/poll can no longer open this gate.
          ...liveTurnResumeGuard(r.serverTaskId),
          isRegisteredLiveSend: isLiveSendInFlight(r.serverTaskId),
        })
      ) {
        continue;
      }
      if (authedResumeAttemptedRef.current.has(r.serverTaskId)) continue;
      authedResumeAttemptedRef.current.add(r.serverTaskId);
      const taskId = r.serverTaskId;
      void resumeAuthedServerTask(r.messageId, taskId, r.promptPreview, scanThreadId).then((disposition) => {
        // NON-DESTRUCTIVE EXPIRY: a retryable outcome (poll wall-clock
        // expired / transient error) means the server task may still be
        // running — allow a later pass to re-attach instead of abandoning it.
        if (disposition === 'retryable') authedResumeAttemptedRef.current.delete(taskId);
      });
    }
  }, [
    state.messages,
    authHasResolved,
    isMigrationGateChecking,
    prest0nSessionAccountId,
    liveTurnResumeGuard,
    resumeAuthedServerTask,
  ]);

  // ============================================================================
  // Phase 1.5 — resume DISCOVERY (all-threads re-attach, RC1.4)
  // ============================================================================
  // The two resume effects above only scan the ACTIVE thread's state.messages,
  // so a BACKGROUND thread with a still-running server task never re-lights its
  // chip on hydrate / second-device sign-in until the user opens it (its
  // completed answer is never lost — the server persists it — but the "still
  // working" signal is invisible). Ask the server which tasks are ACTUALLY
  // non-terminal and light exactly those chips, plus stamp any locally-cached
  // in-flight placeholder that lost its serverTaskId so the per-message resume
  // path re-attaches it. Runs once per authed hydrate.
  // Threads whose chip WE lit from discovery (server-confirmed running).
  // Tracked so a reconcile can CLEAR them once the server reports the task
  // terminal — a locally-driven chip (live send) is never in this set, so
  // the reconcile can't clear an actively-streaming thread (Phase 2 honesty).
  const discoveryLitThreadsRef = useRef<Set<string>>(new Set());
  // REVIEW-20260824: when each thread was LAST server-confirmed (epoch ms).
  // The stuck-flag sweep's serverConfirmedActive veto reads THIS map, not the
  // Set above: the Set is cumulative for the active thread (reconcile skips it
  // by design, safe for chip-clearing, unsafe as a veto), so the veto keys on
  // confirmation freshness instead and self-expires when polls stop
  // confirming.
  const discoveryConfirmedAtRef = useRef<Map<string, number>>(new Map());
  // 45s: one light HTTPS listing per interval, foreground tabs only — honest
  // "still working" chips for work the viewer didn't start, without a
  // listener (firestore.rules exposes no client read on session task docs).
  const ACTIVE_TASK_DISCOVERY_POLL_MS = 45_000;
  // 2x the poll: one dropped/failed listing (it returns [] on error) must not
  // flicker the sweep veto off while work is genuinely running.
  const DISCOVERY_CONFIRMATION_TTL_MS = 90_000;
  const runActiveTaskDiscovery = useCallback(async () => {
    if (selectResumeMode({ authHasResolved, isMigrationGateChecking, prest0nSessionAccountId }) !== 'authed') return;
    if (!readThreadMessagesRef.current || !writeThreadMessagesRef.current) return;

    const activeTasks = await listActivePrest0nTasks();
    const activeTid = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    const plan = planActiveTaskReattach({
      activeTasks,
      activeThreadId: activeTid,
      getThreadMessages: (threadId) =>
        (threadId === activeTid
          ? messagesSyncRef.current
          : (readThreadMessagesRef.current ? readThreadMessagesRef.current(threadId) : [])) || [],
    });

    // Clear chips we previously lit whose task the server no longer reports as
    // running (never the active thread — its finalize owns that).
    const { toClear } = reconcileDiscoveryChips({
      previouslyLit: discoveryLitThreadsRef.current,
      confirmed: plan.chipThreadIds,
      activeThreadId: activeTid,
    });
    for (const threadId of toClear) {
      discoveryLitThreadsRef.current.delete(threadId);
      discoveryConfirmedAtRef.current.delete(threadId);
      stopProcessing(threadId);
    }
    // REVIEW-20260824: the ACTIVE thread is deliberately absent from toClear
    // (its finalize owns the chip) — its stale CONFIRMATION is not deleted
    // here either: stamps age out via DISCOVERY_CONFIRMATION_TTL_MS, which
    // both un-latches the veto for the active thread and tolerates one
    // failed/empty listing (an eager prune on absence would not).

    // Light chips for server-confirmed running tasks, clocked from when the
    // SERVER started the work (not from when this poll noticed it) so a long
    // audit or routine run shows its true age instead of resetting to 0:00
    // every 45s.
    for (const threadId of plan.chipThreadIds) {
      discoveryLitThreadsRef.current.add(threadId);
      discoveryConfirmedAtRef.current.set(threadId, Date.now());
      const turnEpochMs = plan.chipEpochByThread[threadId];
      startProcessing(threadId, turnEpochMs ? { turnEpochMs } : undefined);
    }

    // Stamp placeholders that lost their serverTaskId so resume re-attaches.
    for (const stamp of plan.stamps) {
      if (stamp.isActive) {
        updateStreamingMessageById(stamp.messageId, { serverTaskId: stamp.taskId });
      } else if (readThreadMessagesRef.current && writeThreadMessagesRef.current) {
        const msgs: ChatMessage[] = readThreadMessagesRef.current(stamp.threadId) || [];
        const idx = msgs.findIndex((m: ChatMessage) => m.id === stamp.messageId);
        if (idx >= 0 && !msgs[idx].serverTaskId) {
          const next = [...msgs];
          next[idx] = { ...next[idx], serverTaskId: stamp.taskId };
          writeThreadMessagesRef.current(stamp.threadId, next);
        }
      }
    }
  }, [
    authHasResolved,
    isMigrationGateChecking,
    prest0nSessionAccountId,
    startProcessing,
    stopProcessing,
    updateStreamingMessageById,
  ]);

  // Run discovery once per authed hydrate, and re-run when the tab regains
  // visibility so background-thread chips stay honest (a task that finished
  // while the tab was hidden clears its chip on return) without a poll loop.
  const activeTaskDiscoveryDoneRef = useRef(false);
  useEffect(() => {
    if (selectResumeMode({ authHasResolved, isMigrationGateChecking, prest0nSessionAccountId }) !== 'authed') return;
    if (!activeTaskDiscoveryDoneRef.current) {
      activeTaskDiscoveryDoneRef.current = true;
      void runActiveTaskDiscovery();
    }
    if (typeof document === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void runActiveTaskDiscovery();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Owner directive 2026-08-22 ("anytime a triage thread is working it
    // should say 'still working'"): server-started work — triage analyses,
    // routine runs — arms no local flag, and discovery used to run only at
    // hydrate + tab re-focus, so a viewer sitting in the open thread saw
    // NOTHING until the answer landed. A light visible-only poll keeps the
    // chips honest in the foreground (the earlier "without a poll loop"
    // stance predates server-started thread work); thread-open pokes from
    // the pane cover the switch-latency gap. Hidden tabs never poll.
    const pollId = window.setInterval(() => {
      if (document.visibilityState === 'visible') void runActiveTaskDiscovery();
    }, ACTIVE_TASK_DISCOVERY_POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(pollId);
    };
  }, [authHasResolved, isMigrationGateChecking, prest0nSessionAccountId, runActiveTaskDiscovery]);

  // H1 (owner report 2026-07-14, spinner-outlives-turn): completion path for
  // RECOVERED tasks. The hydrate-recovery poll (usePrest0nSession.initSession,
  // empty callbacks + persistCompletionToThread) finalizes the resumed turn's
  // content via the durable thread write with no onDone/onError — and it holds
  // `isPollingTask`, which makes shouldSkipLiveTaskResume() block the
  // per-message resume path whose `finally` would have released the flag. The
  // chip lit at hydrate (runActiveTaskDiscovery's startProcessing, or the
  // resume re-light) therefore outlived the turn. When the recovered task goes
  // terminal, clear the SAME per-thread flags the send path set — for the
  // ORIGINATING thread from the queued-task record, active or background —
  // and drop it from the discovery-lit set so a later reconcile can't act on
  // stale membership.
  useEffect(() => {
    recoveredTaskTerminalRef.current = (threadId: string | null) => {
      if (!threadId) return;
      discoveryLitThreadsRef.current.delete(threadId);
      stopProcessing(threadId);
    };
    return () => {
      recoveredTaskTerminalRef.current = () => undefined;
    };
  }, [stopProcessing]);

  // Wire up context message appender for document analysis persistence
  // This allows the document reader to add hidden context messages to the conversation
  useEffect(() => {
    globalTools.setAppendContextMessage(addMessage);
  }, [globalTools.setAppendContextMessage, addMessage]);

  // Wire up processing-state heartbeat (2026-05-07 fix).
  //
  // Exposes startProcessing / stopProcessing to globalTools' sharedContext so
  // the chain executor and any long-running tool can keep state.isProcessing
  // fresh across handoffs — preventing the "nothing is spinning" gap between
  // tool finalize() and the next tool's first showPending(). The fallback
  // "working…" widget in GlobalChatSystem renders whenever isProcessing is
  // true but no tool-owned pending widget is currently visible.
  useEffect(() => {
    if (globalTools.setProcessingHeartbeat) {
      globalTools.setProcessingHeartbeat(startProcessing, stopProcessing);
    }
    return () => {
      if (globalTools.setProcessingHeartbeat) {
        globalTools.setProcessingHeartbeat(null, null);
      }
    };
  }, [globalTools.setProcessingHeartbeat, startProcessing, stopProcessing]);

  // Wire chat instance id into globalTools so the cross-thread search
  // tool (PR fix/thread-isolation-regression-and-cross-thread-retrieval)
  // can scope its localStorage reads to the right chat surface.
  useEffect(() => {
    if (globalTools.setChatInstanceId) {
      globalTools.setChatInstanceId(config.instanceId || null);
    }
    return () => {
      if (globalTools.setChatInstanceId) {
        globalTools.setChatInstanceId(null);
      }
    };
  }, [globalTools.setChatInstanceId, config.instanceId]);

  // Mount-time messaging for the outward-decision click branches
  // (2026-08-27, batch-tile refresh self-healing): chatHelpersRef inside
  // useGlobalTools is only populated by the first TURN of a page session,
  // so after a refresh a "send it" click on a held-send card fell through
  // unhandled until the user happened to send a message. These primitives
  // exist from mount — wiring them lets those branches decide, post the
  // receipt, and re-render the batch tile before any turn has run.
  useEffect(() => {
    if (globalTools.setBaseOutwardMessaging) {
      globalTools.setBaseOutwardMessaging({
        addSystemMessage: addSystemMessage as (input: unknown) => unknown,
        updateMessageById: (id: string, update: Record<string, unknown>) =>
          updateStreamingMessageById(id, update as Partial<ChatMessage>),
      });
    }
    return () => {
      if (globalTools.setBaseOutwardMessaging) {
        globalTools.setBaseOutwardMessaging(null);
      }
    };
  }, [globalTools.setBaseOutwardMessaging, addSystemMessage, updateStreamingMessageById]);

  // Tile refresh self-healing at hydrate. Batch tile (2026-08-27, plan D2's
  // belt): a persisted batch send card re-reads every row's true state from
  // the server when its thread hydrates, re-renders in place, and
  // re-registers for live row stamping — so a card that went stale (rows
  // decided before the refresh or on another device) is honest again
  // without a click. Connect tile (2026-09-02, P0 thread-durability): every
  // connect card re-projects from its payload and peeks the custom-
  // connection roster, so a re-opened thread shows the tile's CURRENT state
  // ("signed in as …", "session expired", "opened … — sign-in not
  // finished") — never a new message, never a re-offer (that decision is the
  // primary's). Batch tiles run once per message per page session; a failed
  // rebuild (offline) un-marks itself so a later pass — or the click-time DOM
  // fallback — retries. Connect tiles are admitted by PROJECTION rather than
  // by id (see connectTileSeenRef below). Batch tiles from before the
  // self-description attributes shipped skip cheaply and keep the documented
  // v1 fallback.
  const rehydratedTilesRef = useRef<Set<string>>(new Set());
  // CONNECT TILES ARE KEYED BY THEIR LAST PROJECTION, not by id alone
  // (review 2026-09-02). The roster-derived row lives only in the html, and
  // the next server snapshot replaces the html wholesale — with a
  // once-per-id marker the row was never re-derived and "signed in as …"
  // showed once, then vanished for the session. Re-admitting a card whose
  // html no longer matches what this lane last saw restores it;
  // rehydrateConnectTile no-ops on an identical projection, so re-admission
  // is cheap. `retryAt` keeps an UNREADABLE roster (offline, auth not yet
  // resolved) from re-projecting on every streamed chunk: it backs off for
  // one roster-cache window.
  const connectTileSeenRef = useRef<Map<string, { html: string; retryAt: number }>>(new Map());
  const CONNECT_TILE_RETRY_MS = 5_000;
  useEffect(() => {
    const batchCandidates: Array<{ id: string; html: string }> = [];
    const connectCandidates: Array<{
      id: string;
      payload: ChatMessage['payload'];
      html: string;
      isContextOnly: boolean;
    }> = [];
    const nowMs = Date.now();
    for (const m of state.messages) {
      const id = typeof m.id === 'string' ? m.id : '';
      const html = typeof m.workflowActionButtonsHtml === 'string' ? m.workflowActionButtonsHtml : '';
      if (m.isUser || !id) continue;
      if (m.sourceTag === 'prest0n_batch_send' && html.includes('data-batch-root')) {
        if (rehydratedTilesRef.current.has(id)) continue;
        batchCandidates.push({ id, html });
      } else if (isRebuildableConnectCard(m)) {
        const seen = connectTileSeenRef.current.get(id);
        if (seen && (seen.html === html || nowMs < seen.retryAt)) continue;
        connectCandidates.push({ id, payload: m.payload, html, isContextOnly: m.isContextOnly === true });
      }
    }
    if (batchCandidates.length === 0 && connectCandidates.length === 0) return;
    for (const m of batchCandidates) rehydratedTilesRef.current.add(m.id);
    for (const m of connectCandidates) {
      connectTileSeenRef.current.set(m.id, { html: m.html, retryAt: 0 });
    }
    const threadId =
      (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    if (batchCandidates.length > 0) {
      void import('../../services/globalTools/tools/batchSendTile')
        .then(({ rehydrateBatchTile }) => {
          for (const m of batchCandidates) {
            void rehydrateBatchTile({
              messageId: m.id,
              threadId,
              html: m.html,
              messaging: {
                addSystemMessage:
                  addSystemMessage as unknown as Parameters<typeof rehydrateBatchTile>[0]['messaging']['addSystemMessage'],
                updateMessageById: (id: string, update: { workflowActionButtonsHtml?: string }) =>
                  updateStreamingMessageById(id, update as Partial<ChatMessage>),
              },
            }).then((live) => {
              if (!live) rehydratedTilesRef.current.delete(m.id);
            }).catch(() => {
              rehydratedTilesRef.current.delete(m.id);
            });
          }
        })
        .catch(() => {
          for (const m of batchCandidates) rehydratedTilesRef.current.delete(m.id);
        });
    }
    for (const m of connectCandidates) {
      void rehydrateConnectTile({
        messageId: m.id,
        threadId,
        payload: m.payload,
        current: { workflowActionButtonsHtml: m.html, isContextOnly: m.isContextOnly },
        messaging: {
          updateMessageById: (id, update) =>
            updateStreamingMessageById(id, update as Partial<ChatMessage>),
        },
      }).then((live) => {
        // Unreadable roster: keep the observed projection so an unchanged
        // card is not re-projected on every messages change, and retry
        // after one roster-cache window.
        if (!live) {
          connectTileSeenRef.current.set(m.id, { html: m.html, retryAt: Date.now() + CONNECT_TILE_RETRY_MS });
        }
      }).catch(() => {
        connectTileSeenRef.current.set(m.id, { html: m.html, retryAt: Date.now() + CONNECT_TILE_RETRY_MS });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.messages, addSystemMessage, updateStreamingMessageById]);

  // Push page context into globalTools so the orchestrator is page-aware.
  // The source field from GlobalChatConfig is mapped to a lightweight context object.
  useEffect(() => {
    if (config.source && globalTools.setPageContext) {
      // Dynamic import avoided — use inline construction.
      // The full summary comes from pageContextRegistry at the App level;
      // here we provide a minimal fallback so the orchestrator always has *something*.
      globalTools.setPageContext({
        pathname: typeof window !== 'undefined' ? window.location.pathname : '/',
        pageName: config.source,
        summary: `User is on the ${config.source} page of prest0.ai.`,
      });
    }
    return () => {
      // Clean up on unmount
      if (globalTools.setPageContext) {
        globalTools.setPageContext(undefined);
      }
    };
  }, [config.source, globalTools.setPageContext]);

  // Eagerly set chatHelpersRef so button clicks work after page reload.
  // Previously this was only set inside handleSend (when a message is sent),
  // meaning after page reload the helpers ref was null until the user sent a
  // message — causing "skip to deep research" and other button clicks to silently fail.
  useEffect(() => {
    const helperBundle = {
      addSystemMessage,
      addMessage,
      // Thread transcript reader — BUNDLE PARITY (review 2026-09-02). It
      // shipped only on the two PER-SEND bundles while this eager one, which
      // overwrites them on the very next render, omitted it: a button clicked
      // after a page load met a bundle without the reader, and the feature
      // that depended on it (the connect card's `opened` stamp, since moved
      // to the transcript surface) silently did nothing. The asymmetry is the
      // bug class; the same fail-open expression the per-send bundles use
      // closes it for every reader of this bundle.
      readThreadMessages: (threadId: string) =>
        (readThreadMessagesRef.current ? readThreadMessagesRef.current(threadId) ?? [] : []),
      streaming: {
        startStreamingMessage,
        updateStreamingMessage,
        updateStreamingMessageById,
        finalizeStreamingMessage,
        finalizeStreamingMessageById,
        removeMessage,
        removeMessageById,
      },
    };
    if (globalTools.chatHelpersRef) {
      globalTools.chatHelpersRef.current = helperBundle;
    }
    if (agenticWorkflows.chatHelpersRef) {
      agenticWorkflows.chatHelpersRef.current = helperBundle;
    }
  }, [
    addSystemMessage, addMessage,
    startStreamingMessage, updateStreamingMessage, updateStreamingMessageById,
    finalizeStreamingMessage, finalizeStreamingMessageById,
    removeMessage, removeMessageById,
    globalTools, agenticWorkflows,
  ]);

  const getCurrentPlainText = useCallback((): string => {
    const plain = state.promptHtml ? htmlToPlainText(state.promptHtml) : '';
    return (plain || state.prompt || '').trim();
  }, [state.promptHtml, state.prompt]);

  // R10 (instant send feedback): background-resolution records for sends
  // that were QUEUED behind an in-flight turn, keyed by the pending/user
  // message id. handleSend stashes the record at enqueue time (serialization
  // and uploads may still be in flight); firePendingMessage consumes — and
  // deletes — it to perform the same bounded waits before its turn dispatch,
  // so queued sends keep gcsPath-upgraded attachments + ingestJobId.
  const pendingSendResolutionsRef = useRef<Map<string, SendAttachmentResolution>>(new Map());
  /** Threads already told "you're offline — will send when you're back" (PR-2); one line per thread. */
  const offlineNoticeShownRef = useRef<Set<string>>(new Set());

  const handleSend = useCallback(async () => {
    // Block send if Prest0n session is still booting for a migrated account.
    // For migrated accounts we NEVER fall back to the legacy orchestrator,
    // so we must wait for the session to be ready before allowing a send.
    //
    // 2026-05-13: previously this was a silent early-return, which produced
    // the worst-case UX during the Fernando IP-rollover incident — composer
    // text didn't clear, no spinner appeared, the title stayed at "New
    // Thread", and the user could not tell whether the send was queued,
    // failed, or being ignored. Now we surface a visible system message so
    // the user knows to retry, and we also tickle a session refresh so a
    // hung initial fetch (which now has a 25s timeout in client.ts) gets
    // a fresh chance.
    // ORG STICKINESS (INCIDENT-20260818): while workspace identity is
    // UNRESOLVED for a signed-in user, nothing may dispatch — a turn sent now
    // would run under a guessed identity and its thread would be born into
    // the wrong organization. The gate retries on backoff + 'online'.
    if (workspaceUnresolved) {
      addSystemMessage({
        text: 'Connecting to your workspace — your message will send as soon as the connection is confirmed. Please retry in a moment.',
        sourceTag: 'workspace_unresolved_block',
      });
      return;
    }
    if (prest0nSessionAccountId && prest0nSessionHook.isLoading) {
      fernandoTrace(
        'HANDLE_SEND_BRANCH',
        { branch: 'blocked_session_loading', prest0nSessionAccountId },
        isFernandoStrictMode(prest0nSessionAccountId)
      );
      addSystemMessage({
        text: 'Still connecting to Prest0n — please retry in a moment.',
        sourceTag: 'session_init_inflight_block',
      });
      return;
    }
    if (
      prest0nSessionAccountId
      && !prest0nSessionHook.session
      && prest0nSessionHook.error
    ) {
      // Session-init has failed (or timed out). Surface the error in chat
      // and kick off a refresh so the next send has a chance of going
      // through. Without this, the user sees nothing and the prompt
      // stays unsent in the composer with no spinner — exactly the
      // 2026-05-13 Fernando wedge.
      fernandoTrace(
        'HANDLE_SEND_BRANCH',
        {
          branch: 'blocked_session_error',
          prest0nSessionAccountId,
          error: prest0nSessionHook.error.message,
          code: FERN_FE_CODES.SESSION_INIT_FAILED,
        },
        isFernandoStrictMode(prest0nSessionAccountId)
      );
      addSystemMessage({
        text: `Couldn't reach Prest0n — retrying. (${prest0nSessionHook.error.message.slice(0, 160)})`,
        sourceTag: 'session_init_failed_block',
      });
      // Fire-and-forget; the next send attempt will pick up the new session
      // if the refresh succeeded.
      void prest0nSessionHook.refreshSession();
      return;
    }
    fernandoTrace(
      'HANDLE_SEND_BRANCH',
      {
        branch: prest0nSessionAccountId ? 'prest0n_session_active' : 'no_prest0n_session',
        prest0nSessionAccountId,
        sessionReady: Boolean(prest0nSessionHook.session),
      },
      isFernandoStrictMode(prest0nSessionAccountId)
    );

    // Expired trial: no sends — the injected trial-ended message in the
    // thread explains who to contact (owner directive 2026-07-31).
    if (trialExpiredBlock) {
      return;
    }

    // Block send if balance is too low
    if (lowBalanceState.shouldBlock) {
      return;
    }

    // Send-time trim (owner report 2026-07-13): normalizeChatHtml trims
    // edge text/<br> whitespace but deliberately keeps empty BLOCK elements
    // (assistant reports need them). For the user-composed prompt those edge
    // blocks are pure blank padding, so strip them here too — residual paste
    // space never ships even if it sneaks past the paste handler.
    // preserveBlankLines: the author's own <br><br> is a deliberate blank line
    // between items, never "excessive spacing" (owner report 2026-09-07).
    const safeHtml = trimEdgeEmptyBlocks(normalizeChatHtml(state.promptHtml || '', { preserveBlankLines: true }));
    const text = getCurrentPlainText();
    // Allow sending with just attachments (no text required)
    if (!text && !state.attachments.length) return;

    // ────────────────────────────────────────────────────────────────────
    // THREAD ISOLATION + ATTACHMENT-LOSS FIX
    // fix-marker: webchat-attachments-take2-2026-05-11
    //
    // Capture the originating thread id and pin the cross-thread routing
    // ref BEFORE any await. The previous shape awaited
    // `serializeAttachmentsWithStorage` (which uploads to Firebase
    // Storage over the network — seconds, not microseconds) BEFORE
    // capturing `originatingThreadId` and BEFORE calling `addMessage`.
    // If the user switched threads while that upload was in flight,
    // `getActiveThreadIdRef.current()` returned the *new* active
    // thread, so the user bubble landed on the wrong thread (or was
    // lost entirely from the originating thread's per-thread snapshot).
    // That is the live repro Miles reported in Slack thread
    // C0AM8U804KZ/1778281880.499349 ("send 'read these' + 4 images on
    // thread A, switch to B, switch back to A — bubble + thumbnails
    // gone").
    //
    // R10 (instant send feedback, 2026-08-06) — serialization is now FULLY
    // background:
    //   1. The bubble is added IMMEDIATELY with the raw File[] — no
    //      FileReader/base64 pass first. ChatAttachment's useImageSrc
    //      prefers URL.createObjectURL(file) over the serialized dataUrl,
    //      so raw File entries fully support first paint. (The old
    //      "handful of microtasks" claim was wrong for multi-MB files —
    //      hundreds of ms to seconds before the bubble could exist.)
    //   2. Background `serializeAttachments` (FileReader → dataUrl)
    //      patches the bubble's `attachmentsSerialized` in BOTH live state
    //      and per-thread storage shortly after (the persist boundary
    //      strips File[], so until this lands the stored snapshot has no
    //      attachment record — the patch closes the switch-back window).
    //   3. Background `serializeAttachmentsWithStorage` (Firebase/GCS
    //      upload) upgrades it again with storageUrl/gcsPath entries, so
    //      cross-device pickup still works.
    // The turn dispatch performs bounded waits on 2/3 AFTER all visible
    // feedback renders — see resolveSendAttachmentsForTurn.
    // ────────────────────────────────────────────────────────────────────
    const originatingThreadId: string | null =
      (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
    currentSendThreadIdRef.current = originatingThreadId;
    const attachmentRaceFixMarker = 'fix-marker: webchat-attachments-take2-2026-05-11';

    // Snapshot the files once — the hoisted CLEAR_ATTACHMENTS below must
    // never race the background serialization/upload closures.
    const filesForSend = state.attachments.slice();
    // Per-send background-resolution record (R10). The serialize/upload/
    // ingest-kickoff promises land here; the turn-build sites (the direct
    // dispatch below, or firePendingMessage for queued sends) perform the
    // same bounded waits on it.
    const sendResolution: SendAttachmentResolution = {
      serializedPromise: null,
      uploadPromise: null,
      ingestJobIdPromise: null,
    };

    // Delivery ladder (2026-09-08, chat-thread experience PR-2): mint the
    // task id HERE, before the bubble exists, so the bubble carries the exact
    // id the server writes the human turn under. Every later signal — the
    // own-doc ack, the 2xx, the group-store echo, a refused dispatch — joins
    // the ledger on this one key, and the dispatcher reuses the id instead of
    // minting its own. `orderTs` is the send instant encoded in the id (the
    // server stamps the same shape), `seq` 0 = the question's position.
    const sendClientTaskId = mintClientTaskId();
    const sendSharedMessageId = userTurnAnchorId(sendClientTaskId) as string;
    const sendOrderTs = orderTsFromClientTaskId(sendClientTaskId) ?? Date.now();
    const sendSenderUid = getCurrentUserUid() || undefined;

    // Use addMessage to ensure tags are applied when SHOULD_TAG_AS_TEST=true.
    // Phase 5 (adversarial-review): pass originatingThreadId EXPLICITLY so a
    // concurrent send on another thread can never clobber
    // currentSendThreadIdRef and misroute this user bubble.
    const userMessage = addMessage({
      text,
      html: safeHtml || undefined,
      isUser: true,
      timestamp: new Date(),
      sharedMessageId: sendSharedMessageId,
      orderTs: sendOrderTs,
      seq: 0,
      senderId: sendSenderUid,
      attachments: filesForSend.length ? [...filesForSend] : undefined,
      // METADATA SKELETON (2026-08-20 attachment-durability round): name/
      // type/size are synchronously available on File, so an attachment
      // record exists from the FIRST persist — the synchronous persist in
      // addMessage sanitizes File[] away, and before this skeleton a tab
      // close (or crash) ahead of the async FileReader patch left the stored
      // message with ZERO attachment record: the chip was simply gone on
      // reload. The FileReader/upload patches below UPGRADE this skeleton
      // (richness-guarded, never clobbered down).
      attachmentsSerialized: filesForSend.length
        ? filesForSend.map((f) => ({ name: f.name, type: f.type, size: f.size }))
        : undefined,
    }, originatingThreadId);

    // "sent" (PR-2): ONE additive per-doc write of the bubble under its own
    // id, awaited — Firestore resolves it only on server acknowledgement
    // (offline persistence queues it), so the check appears exactly when the
    // sender's copy is safe. The mirror effect's later batch merges the same
    // doc (idempotent). A rejection leaves the ○; the mirror retries.
    {
      const sendDeliveryKey = deliveryKey(originatingThreadId, sendSharedMessageId);
      const uidForOwnWrite = getCurrentUserUid();
      if (sendDeliveryKey && uidForOwnWrite && originatingThreadId) {
        deliveryLedger.markDispatched(sendDeliveryKey);
        void writeOwnUserTurnNow(uidForOwnWrite, config.instanceId, originatingThreadId, userMessage)
          .then(() => deliveryLedger.markOwnAcked(sendDeliveryKey))
          .catch(() => { /* the mirror effect retries; the glyph stays ○ */ });
      }
    }

    // Sending always re-pins the transcript: the user's own message (and the
    // reply that follows it) belongs in view, even if they were scrolled up
    // reading history. This is the ONE auto-scroll allowed to override an
    // unpinned reader.
    pinToBottom('smooth');

    // ────────────────────────────────────────────────────────────────────
    // BACKGROUND: local FileReader → dataUrl serialization (R10). The
    // bubble already renders from the live File[]; this patch makes the
    // attachment record durable. It must rewrite BOTH live state AND the
    // originating thread's per-thread storage — the synchronous persist in
    // addMessage sanitizes File[] away, so with no attachmentsSerialized
    // the stored snapshot has zero attachment record and a thread
    // switch-back before this patch would reproduce the 2026-05-11
    // "bubble thumbnails gone" regression. userMessage is also mutated in
    // place so the later turn/queue paths see the payload immediately.
    // ────────────────────────────────────────────────────────────────────
    if (filesForSend.length) {
      const serializeTargetId = userMessage.id;
      sendResolution.serializedPromise = serializeAttachments(filesForSend)
        .then((serialized) => {
          if (!serialized?.length) return undefined;
          // Never clobber the storage-upgraded entries if the (slower)
          // upload chain somehow landed first. The send-time metadata
          // skeleton counts as absent (2026-08-20) — it exists precisely to
          // be upgraded by this patch.
          if (isMetadataOnlyAttachments(userMessage.attachmentsSerialized)) {
            userMessage.attachmentsSerialized = serialized;
            const activeNow =
              (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
            if (serializeTargetId && activeNow === originatingThreadId) {
              dispatch({
                type: 'UPDATE_MESSAGE_BY_ID',
                payload: { id: serializeTargetId, message: { attachmentsSerialized: serialized } },
              });
            }
            if (
              originatingThreadId
              && readThreadMessagesRef.current
              && writeThreadMessagesRef.current
            ) {
              const stored = readThreadMessagesRef.current(originatingThreadId) ?? [];
              const idx = stored.findIndex(m => m.id === serializeTargetId);
              if (idx !== -1 && isMetadataOnlyAttachments(stored[idx].attachmentsSerialized)) {
                const next = stored.slice();
                next[idx] = { ...next[idx], attachmentsSerialized: serialized };
                writeThreadMessagesRef.current(originatingThreadId, next);
              }
            }
          }
          return serialized;
        })
        .catch(() => undefined);
    }

    // ────────────────────────────────────────────────────────────────────
    // BACKGROUND: upgrade attachmentsSerialized with Firebase Storage
    // URLs for cross-device pickup. Non-blocking — the bubble has
    // already been added (raw File[] entries; the serialize patch above
    // backfills dataUrls), so a mid-upload thread switch can no longer
    // race the bubble onto the wrong thread.
    // fix-marker: webchat-attachments-take2-2026-05-11
    // ────────────────────────────────────────────────────────────────────
    if (filesForSend.length) {
      // FH-QA-T2.1 (2026-05-21): the background Firebase Storage
      // upgrade is fire-and-forget, but the SETUP of it (calling
      // getCurrentUserUid + capturing refs) must NEVER throw
      // synchronously — if it does, the rest of handleSend (including
      // the prest0nSession.sendTurn call that produces the actual AI
      // reply) never runs, the user sees nothing, and the attachment
      // pill stays in the composer because CLEAR_ATTACHMENTS is
      // farther down. The 12:46 UTC Fernando demo repro had exactly
      // this shape: file pill stayed, no upload fired, no turn fired,
      // no error. Wrap the entire setup block in a try/catch so a
      // future regression here cannot wedge the whole send pipeline.
      try {
        const uidForUpload = getCurrentUserUid();
        const targetMessageId = userMessage.id;
        if (uidForUpload && targetMessageId) {
          const filesToUpload = filesForSend.slice();
          const targetThreadId = originatingThreadId;
          // Group send ⇒ the turn build awaits the FULL upload (2026-09-07
          // photo-vanish fix; see SendAttachmentResolution.awaitFullUpload).
          // Decided once, here, from the host's live membership predicate:
          // true = shared, null = not known yet (PR-6, 2026-09-09: wait — the
          // safe side), false = a resolved solo thread (the 8 s race + inline
          // fallback). No host predicate ⇒ solo.
          const sharedVerdict = targetThreadId && isThreadSharedRef.current
            ? isThreadSharedRef.current(targetThreadId)
            : false;
          sendResolution.awaitFullUpload = sharedVerdict !== false;
          sendResolution.uploadPromise = (async () => {
            try {
              const canUseCloudUpload =
                cloudExtractEnabled === true
                && !!prest0nSessionAccountId
                && !!(prest0nSessionHook as any)?.session?.sessionId;

              let upgraded: SerializedAttachment[];
              if (!canUseCloudUpload) {
                upgraded = await serializeAttachmentsWithStorage(
                  filesToUpload,
                  uidForUpload,
                  targetMessageId
                );
              } else {
                const sessionId = (prest0nSessionHook as any).session!.sessionId as string;
                const accountId = prest0nSessionAccountId!;

                // Bounded, not fifty at once (2026-09-10). Each upload
                // carries a five-minute abort whose clock starts at the
                // fetch call, and the browser only runs ~6 per host: fired
                // all together, the tail of a fifty-file batch burns its
                // whole budget queued and aborts having sent nothing. See
                // UPLOAD_CONCURRENCY.
                const uploadResults = await mapWithConcurrency(
                  filesToUpload,
                  UPLOAD_CONCURRENCY,
                  async (file) => {
                    try {
                      return { status: 'fulfilled' as const, value: await uploadAttachmentViaGcsSignedUrl(file, sessionId, accountId) };
                    } catch (uploadErr) {
                      return { status: 'rejected' as const, reason: uploadErr };
                    }
                  },
                );

                const base = await serializeAttachmentsWithStorage(
                  filesToUpload,
                  uidForUpload,
                  targetMessageId
                );
                upgraded = base.map((att, i) => {
                  const result = uploadResults[i];
                  if (result?.status === 'fulfilled' && result.value?.success && result.value.gcsPath) {
                    return { ...att, gcsPath: result.value.gcsPath };
                  }
                  if (result?.status === 'rejected' || (result?.status === 'fulfilled' && !result.value?.success)) {
                    console.warn('[cloud-ingest] GCS upload failed for file, falling back:', result);
                  }
                  return att;
                });

                // Ingest-on-upload: ALL signed-URL uploads have settled, so
                // kick off server-side extraction NOW for the files that made
                // it into GCS instead of waiting for the turn POST. Fired
                // here (not awaited) so a slow/cold ingest function can never
                // hold up the rest of the send pipeline; the turn dispatch
                // picks the jobId up later via a bounded wait. Files without
                // a gcsPath (failed uploads) are simply omitted — the backend
                // extracts those inline as before.
                // Photo-attachment fix (2026-08-05): photos are excluded
                // per-file — image/* through the DOCUMENT ingest contract
                // just runs a pointless tesseract pass that yields nothing
                // for photographic scenes (the server routes photos to
                // vision description at turn time instead). Mixed batches
                // still ingest their PDFs/docs.
                const ingestable = upgraded.filter(
                  (att): att is SerializedAttachment & { gcsPath: string } =>
                    !!att.gcsPath && !String(att.type || '').toLowerCase().startsWith('image/'),
                );
                if (ingestable.length > 0) {
                  sendResolution.ingestJobIdPromise = startCloudIngest({
                    sessionId,
                    threadId: targetThreadId ?? undefined,
                    attachments: ingestable.map((att) => ({
                      name: att.name,
                      type: att.type,
                      size: att.size,
                      gcsPath: att.gcsPath,
                    })),
                  })
                    .then((res) => (res?.jobId ? res.jobId : undefined))
                    .catch((err) => {
                      // Non-fatal by contract: log and proceed without a
                      // jobId — backend falls back to inline extraction.
                      console.warn('[cloud-ingest] startCloudIngest failed — proceeding without jobId:', err);
                      return undefined;
                    });
                }
              }

              // R10: mutate the live userMessage reference in place so the
              // turn/queue paths (which read it after their bounded waits)
              // and any consumer holding the same reference see the
              // upgraded entries immediately.
              userMessage.attachmentsSerialized = upgraded;
              // Patch live React state if the originating thread is
              // currently active. UPDATE_MESSAGE_BY_ID only touches
              // `state.messages` (the active thread's slot), so we
              // gate it on the active === originating check.
              const activeNow =
                (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
              if (activeNow === targetThreadId) {
                dispatch({
                  type: 'UPDATE_MESSAGE_BY_ID',
                  payload: { id: targetMessageId, message: { attachmentsSerialized: upgraded } },
                });
              }
              // Always patch per-thread storage so a later switch-back
              // (or another device loading from Firestore-synced
              // localStorage) picks up the storageUrl entries.
              if (
                targetThreadId
                && readThreadMessagesRef.current
                && writeThreadMessagesRef.current
              ) {
                const stored = readThreadMessagesRef.current(targetThreadId) ?? [];
                const idx = stored.findIndex(m => m.id === targetMessageId);
                if (idx !== -1) {
                  const next = stored.slice();
                  next[idx] = { ...next[idx], attachmentsSerialized: upgraded };
                  writeThreadMessagesRef.current(targetThreadId, next);
                }
              }
              return upgraded;
            } catch {
              // Storage upload failed; bubble retains dataUrl-only
              // attachmentsSerialized. That still works locally and
              // through the localStorage→Firestore mirror; only true
              // cross-device pickup degrades.
              return undefined;
            }
          })();

          // ── BACKGROUND UPLOAD HEAL (2026-08-20 attachment-durability
          // round) ── a dataUrl-only record is the FIRST to vanish: the
          // server transcript's size guard strips inline payloads, so with
          // no durable locator the attachment survives only in local
          // storage. When any upload failed at send, retry it in-session
          // (60s, then 5min) and merge the won locators in by name —
          // additively, never clobbering a richer entry.
          void sendResolution.uploadPromise.then((upgradedAtSend) => {
            const needsHeal = (list: SerializedAttachment[] | undefined): string[] =>
              (list ?? [])
                .filter((a) => a?.name && !a.storageUrl && !a.storagePath && !a.gcsPath)
                .map((a) => a.name);
            if (needsHeal(upgradedAtSend ?? userMessage.attachmentsSerialized).length === 0) return;

            const healOnce = async (): Promise<boolean> => {
              const missingNames = new Set(needsHeal(userMessage.attachmentsSerialized));
              if (missingNames.size === 0) return false;
              const retryFiles = filesToUpload.filter((f) => missingNames.has(f.name));
              if (retryFiles.length === 0) return false;
              const healed = await serializeAttachmentsWithStorage(
                retryFiles,
                uidForUpload,
                targetMessageId,
              );
              const byName = new Map(
                healed
                  .filter((h) => h?.name && (h.storageUrl || h.storagePath || h.gcsPath))
                  .map((h) => [h.name, h] as const),
              );
              if (byName.size === 0) return needsHeal(userMessage.attachmentsSerialized).length > 0;
              const merged = (userMessage.attachmentsSerialized ?? []).map((att) => {
                const won = att?.name ? byName.get(att.name) : undefined;
                return won && !att.storageUrl && !att.storagePath && !att.gcsPath
                  ? { ...att, ...won, dataUrl: att.dataUrl ?? won.dataUrl }
                  : att;
              });
              userMessage.attachmentsSerialized = merged;
              const activeNow =
                (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
              if (activeNow === targetThreadId) {
                dispatch({
                  type: 'UPDATE_MESSAGE_BY_ID',
                  payload: { id: targetMessageId, message: { attachmentsSerialized: merged } },
                });
              }
              if (targetThreadId && readThreadMessagesRef.current && writeThreadMessagesRef.current) {
                const stored = readThreadMessagesRef.current(targetThreadId) ?? [];
                const idx = stored.findIndex(m => m.id === targetMessageId);
                if (idx !== -1) {
                  const next = stored.slice();
                  next[idx] = { ...next[idx], attachmentsSerialized: merged };
                  writeThreadMessagesRef.current(targetThreadId, next);
                }
              }
              console.log('[GlobalChat] attachment upload heal landed durable locators', {
                messageId: targetMessageId,
                healedCount: byName.size,
              });
              return needsHeal(userMessage.attachmentsSerialized).length > 0;
            };

            setTimeout(() => {
              void healOnce()
                .then((stillMissing) => {
                  if (stillMissing) {
                    setTimeout(() => { void healOnce().catch(() => {}); }, 5 * 60_000);
                  }
                })
                .catch(() => {
                  setTimeout(() => { void healOnce().catch(() => {}); }, 5 * 60_000);
                });
            }, 60_000);
          }).catch(() => { /* upload chain never rejects by contract */ });
        }
      } catch (uploadSetupErr) {
        // Synchronous failure in upload SETUP — do not let it propagate
        // and abort the rest of handleSend (would wedge the turn POST).
        // Bubble keeps its dataUrl-only attachmentsSerialized; the turn
        // still fires and the document analyzer / VM still receives
        // attachment metadata via attachmentsSerialized.
        console.warn(
          '[GlobalChat] FH-QA-T2.1: background attachment upload setup threw — continuing with send anyway',
          uploadSetupErr
        );
      }
    }

    // R10: NO await here — the fire-and-forget contract the background-
    // upload comment above always promised is now real. Everything below
    // this line (guest
    // buffer, composer clear, chat-open, queue check, chip spinner,
    // thinking pill) runs synchronously in the click continuation; the
    // upload/ingest results are awaited — bounded — only where the turn
    // body is built (resolveSendAttachmentsForTurn below and in
    // firePendingMessage for queued sends).

    // SECURITY: Buffer message for post-auth merge if user is not authenticated
    // This allows messages typed before login to be preserved and merged with Firestore history
    if (!isAuthenticated) {
      sessionMessagesRef.current.push(userMessage);
    }

    config.onMessageSent?.(userMessage);

    // Open the just-sent suppression window BEFORE clearing prompt state so
    // any draft-rehydrate path that races against the synchronous clear (the
    // 1000 ms cross-tab poll or another component re-writing the global
    // CHAT_DRAFT key from its own stale state) sees the window and skips.
    lastSendAtRef.current = Date.now();
    // Also stamp the shared cross-component suppression singleton so the
    // LandingPage hero composer's poll + restore loops (which have no
    // useGlobalChat handle) bail on the same window.
    // See BUG-DR-COMPOSER-REPOP-LONG-TURN / draftSuppressionState.ts.
    draftMarkSentNow();
    // FH-QA composer-refill race on LONG NON-attachment prompts
    // (2026-05-21 16:25 UTC): stamp the just-sent text/html as a
    // CONTENT-domain sentinel as well so any debounced save closure
    // whose captured prompt body matches gets rejected by
    // `isSentTextMatch()` — even if its fire time lands outside the
    // time-domain suppression window (e.g. a 300 ms timer scheduled
    // during typing, fired after the 2 s safety-net + 1.5 s cooldown
    // window paradoxically slipped past on a slow machine).
    // See draftSuppressionState.ts / composerRefillNonAttachmentRace.test.ts.
    draftMarkSentText(text, safeHtml || '');

    // Clear prompt
    dispatch({ type: 'RESET_PROMPT' });
    // Cancel any pending draft save and clear persisted draft
    if (draftPersistTimeoutRef.current) {
      window.clearTimeout(draftPersistTimeoutRef.current);
      draftPersistTimeoutRef.current = null;
    }
    draftPendingFlushRef.current = null;
    draftLastSavedRef.current = null;
    if (config.persistDraft) {
      removeFromLocalStorage(storageKeys.CHAT_DRAFT);
    }
    if (promptInputRef.current) {
      promptInputRef.current.innerHTML = '';
    }
    dispatch({ type: 'CLEAR_ATTACHMENTS' });
    
    if (!state.isChatOpen) {
      userInitiatedOpenRef.current = true;
      dispatch({ type: 'SET_CHAT_OPEN', payload: true });
      persistPageOpen(true);
    }

    // Push to ref so the heartbeat helpers exposed to globalTools can
    // resolve the originating thread without each tool having to thread
    // it through.
    currentSendThreadIdRef.current = originatingThreadId;
    if (typeof window !== 'undefined') {
      try {
        const dbg = typeof localStorage !== 'undefined'
          && localStorage.getItem('DEBUG_THREAD_ISOLATION');
        if (dbg && dbg !== '0' && dbg !== 'false') {
          // eslint-disable-next-line no-console
          console.debug(
            `[threadIso] ${attachmentRaceFixMarker} action=send originatingThreadId=${originatingThreadId} activeThreadId=${originatingThreadId}`
          );
        }
      } catch { /* ignore */ }
    }

    // R10: the ingest-jobId patience race that used to sit here moved into
    // resolveSendAttachmentsForTurn — it now runs immediately before the
    // turn body is built, AFTER the thinking pill renders, on both the
    // direct path (below) and the queue-drain path (firePendingMessage).

    // ────────────────────────────────────────────────────────────────────
    // SAME-THREAD QUEUEING WHILE STREAMING
    //
    // Miles directive (2026-05-09 23:44 UTC): "yes it should be allowed".
    //
    // If the originating thread already has an in-flight turn,
    // we MUST NOT fire a second concurrent turn on the same thread
    // (that would race two AbortControllers, two streaming widgets,
    // and two finalize handlers against the SAME thread's history
    // — the demo-day repro Miles reported).  Instead:
    //
    //   1) The user bubble has ALREADY been added to history above.
    //   2) Mirror it onto the per-thread pending queue.
    //   3) Return.  The drain effect will fire it as a new turn the
    //      moment the in-flight turn finalizes (stopProcessing on this
    //      thread).
    //
    // We deliberately DO NOT call startProcessing() here — the thread
    // is already marked processing for the in-flight turn; the drain
    // effect will keep it processing across queue entries.
    // ────────────────────────────────────────────────────────────────────
    // OFFLINE (PR-2, 2026-09-08): with no network the dispatch would fail on
    // the spot and the bubble would look sent. Park the turn on the per-thread
    // queue instead — the same queue a send behind an in-flight turn uses —
    // with the SAME client task id, so the drain fires it on `online` and
    // the server converges on one row. The drain effect holds while offline.
    const offlineAtSend = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (offlineAtSend && originatingThreadId) {
      const pendingOffline = pendingFromChatMessage(userMessage, originatingThreadId, {
        clientTaskId: sendClientTaskId,
        sharedMessageId: sendSharedMessageId,
      });
      pendingOffline.attachmentsSerialized = userMessage.attachmentsSerialized;
      if (filesForSend.length) {
        pendingSendResolutionsRef.current.set(pendingOffline.id, sendResolution);
      }
      dispatch({ type: 'ENQUEUE_PENDING', payload: pendingOffline });
      deliveryLedger.markQueuedOffline(deliveryKey(originatingThreadId, sendSharedMessageId));
      if (!offlineNoticeShownRef.current.has(originatingThreadId)) {
        offlineNoticeShownRef.current.add(originatingThreadId);
        addSystemMessage({
          text: getUserUiLanguage() === 'es'
            ? 'Sin conexión — tu mensaje se enviará en cuanto vuelvas a estar en línea.'
            : "You're offline — your message will send as soon as you're back online.",
          sourceTag: 'offline_queued_notice',
        }, originatingThreadId);
      }
      return;
    }

    const alreadyProcessing = !!(
      originatingThreadId && state.processingByThread[originatingThreadId]
    );
    // In-thread parallelism slice 1 (2026-08-19): LEDGER BELT. During
    // parallel operation the first finisher clears the thread's processing
    // flag while its sibling still runs — a new send must not slip past the
    // gate on the stale-false flag. The per-turn ledger is liveness truth;
    // ≥1 live turn routes through the busy branch (whose wedge breaker
    // still releases genuine corpses). Zero live turns + flag false =
    // today's idle path, byte-identical.
    const ledgerLiveTurnCount =
      originatingThreadId && typeof prest0nSessionHook.countLiveTurnsForThread === 'function'
        ? prest0nSessionHook.countLiveTurnsForThread(originatingThreadId)
        : 0;
    // Set inside the busy branch when the server triages this exact message
    // as INDEPENDENT PARALLEL WORK and capacity allows a second turn.
    let parallelDispatchAuthorized = false;
    if ((alreadyProcessing || ledgerLiveTurnCount >= 1) && originatingThreadId) {
      const pending = pendingFromChatMessage(userMessage, originatingThreadId, {
        clientTaskId: sendClientTaskId,
        sharedMessageId: sendSharedMessageId,
      });
      // R10: serialization/upload may still be in flight at enqueue time —
      // pendingFromChatMessage copied whatever has landed on userMessage so
      // far (possibly nothing). Stash the resolution record keyed by the
      // pending id so firePendingMessage performs the SAME bounded waits
      // before its turn dispatch; without this, queued sends would ship
      // multi-MB inline dataUrls (or no payload at all) and lose the
      // upload-time extraction jobId.
      pending.attachmentsSerialized = userMessage.attachmentsSerialized;
      if (filesForSend.length) {
        pendingSendResolutionsRef.current.set(pending.id, sendResolution);
      }
      // 2026-08-14 wedge breaker (Harbor Ledger repro): a processing flag
      // with NO live turn behind it is a corpse, not a turn — the durable
      // Interrupted card landed but this tab's release was missed, and
      // queueing here would park the send until the 10-minute safety floor.
      // Verify liveness from the per-thread transcript (persisted store —
      // in-flight placeholders are persisted, which is why the hydrate-time
      // orphan reconciler can find them) plus the session hook's live
      // signals. Wedged ⇒ release the flag now; the drain effect fires the
      // queue head immediately, in order — still never two concurrent turns.
      const wedgeCheckMsgs = readThreadMessagesRef.current
        ? (readThreadMessagesRef.current(originatingThreadId) ?? state.messages)
        : state.messages;
      // PER-TURN (concurrent-turn state stomp fix, 2026-08-16): liveness is
      // judged for THIS thread's own turn via the session hook's per-turn
      // ledger. The shared flags both over-blocked (ANOTHER thread's live
      // poll parked this thread's queued sends behind a genuine corpse) and
      // under-blocked (a concurrent turn's terminal tick stomped a live
      // poll's indication, so a LIVE turn's flag could be released as a
      // corpse mid-run — the post-#855 Fernando DR repro). Legacy/
      // test-double hook shapes fall back to the shared flags.
      const hasPerTurnLedger = typeof prest0nSessionHook.getTurnForThread === 'function';
      const wedgeThreadTurn = hasPerTurnLedger
        ? prest0nSessionHook.getTurnForThread(originatingThreadId)
        : null;
      const wedgeThreadTurnLive = hasPerTurnLedger
        ? !!wedgeThreadTurn && wedgeThreadTurn.phase !== 'terminal'
        : (!!prest0nSessionHook.isStreaming || !!prest0nSessionHook.isPollingTask);
      if (threadHasNoLiveTurn(wedgeCheckMsgs) && !wedgeThreadTurnLive) {
        dispatch({ type: 'ENQUEUE_PENDING', payload: pending });
        console.warn(
          `[GlobalChat] processing flag held by a dead turn on thread ${originatingThreadId} — releasing; queued send fires now`
        );
        stopProcessing(originatingThreadId);
        return;
      }
      // Mid-turn interjection (owner directive 2026-08-16): a text-only send
      // during a LIVE task-backed turn attaches to the RUNNING task instead
      // of queueing — the server folds it into the in-flight reasoning at
      // the next step boundary (the Slack-lane experience), and anything the
      // turn doesn't consume is answered by a SERVER-OWNED follow-up turn.
      // No client re-send queue for this path means the whole
      // "Queued… → couldn't start this reply" failure class can't occur.
      // accepted:false (terminal task, SSE lane, network miss) falls through
      // to the ordinary queue, so nothing is ever dropped.
      if (!filesForSend.length) {
        const liveTaskId = liveServerTaskIdOf(wedgeCheckMsgs)
          // PER-TURN fallback (2026-08-16): the DR clarifying card FINALIZES
          // the pending widget, so a mid-clarifying thread can have no
          // in-flight placeholder to read the id from. Bind the interjection
          // to THIS thread's own confirmed-running turn from the per-turn
          // ledger — its taskId is the turn's ORIGIN binding, never "the
          // latest send's" task (deep-research continuation answers attach
          // to their own run even while another thread's turn is live).
          || (wedgeThreadTurn && wedgeThreadTurn.phase === 'polling'
            ? wedgeThreadTurn.taskId
            : null);
        if (liveTaskId) {
          const { accepted, lane, verdict, ack } = await postPrest0nTaskInterjection(liveTaskId, text);
          // In-thread parallelism slice 1 (2026-08-19): the server triaged
          // this message as INDEPENDENT NEW WORK. With capacity for a second
          // turn, escape the busy branch — the normal dispatch below runs it
          // CONCURRENTLY. maxParallel is 2; anything beyond queues as today.
          if (!accepted && verdict === 'parallel_task' && ledgerLiveTurnCount < 2) {
            parallelDispatchAuthorized = true;
            addSystemMessage({
              text: ack || 'On it — running this alongside the current task.',
              sourceTag: 'queued_notice',
            }, originatingThreadId);
          }
          if (accepted) {
            // DYNAMIC ACK (2026-08-21 owner directive): the server's triage
            // authors a response specific to this message and the live work —
            // for a mid-run status ask (lane 'status') the ack IS the
            // up-to-date progress answer. HONEST fallback copy (2026-08-19)
            // stands when no ack came back: a deep-research pipeline only
            // SWEEPS unconsumed notes at completion — never promise mid-run
            // folding there. Loop turns genuinely fold at the next boundary.
            addSystemMessage({
              text: ack || (lane === 'pipeline'
                ? 'Prest0n sees this — it will be addressed right after the current run.'
                : 'Prest0n sees this — folding it into the reply in progress.'),
              sourceTag: 'queued_notice',
            }, originatingThreadId);
            if (typeof window !== 'undefined') {
              try {
                const dbg = typeof localStorage !== 'undefined'
                  && localStorage.getItem('DEBUG_THREAD_ISOLATION');
                if (dbg && dbg !== '0' && dbg !== 'false') {
                  // eslint-disable-next-line no-console
                  console.debug(
                    `${THREAD_QUEUE_DEBUG_TAG} action=interject threadId=${originatingThreadId} taskId=${liveTaskId}`
                  );
                }
              } catch { /* ignore */ }
            }
            return;
          }
        }
      }
      if (!parallelDispatchAuthorized) {
        dispatch({ type: 'ENQUEUE_PENDING', payload: pending });
        // 2026-07-14 incident hardening: a queued send must NEVER be silent —
        // without an acknowledgment, a wedged processing flag makes messages
        // simply vanish (no widget, no request, no error, and every re-send
        // does the same). The tag is in OVERLAY_NON_HIDING_SOURCE_TAGS so this
        // notice can never mask a live turn's widget.
        addSystemMessage({
          text: 'Queued — will send when the current reply finishes.',
          sourceTag: 'queued_notice',
        }, originatingThreadId);
        if (typeof window !== 'undefined') {
          try {
            const dbg = typeof localStorage !== 'undefined'
              && localStorage.getItem('DEBUG_THREAD_ISOLATION');
            if (dbg && dbg !== '0' && dbg !== 'false') {
              // eslint-disable-next-line no-console
              console.debug(
                `${THREAD_QUEUE_DEBUG_TAG} action=queue threadId=${originatingThreadId} id=${pending.id}`
              );
            }
          } catch { /* ignore */ }
        }
        return;
      }
      // parallelDispatchAuthorized: fall through to the normal dispatch —
      // this message runs as a SECOND CONCURRENT TURN on the thread.
    }

    // Mark this thread as processing immediately so the chip-level
    // spinner renders on the originating thread (not on whichever
    // thread happens to be active when an asynchronous chunk lands).
    if (originatingThreadId) startProcessing(originatingThreadId);

    // Show an immediate, global thinking indicator as soon as the user sends.
    // This closes the UI gap before GlobalTools/orchestrator routing has enough time
    // to create its own stage1 indicator. Downstream handlers remove it once they
    // create real progress/output.
    //
    // MISSING-PANE-INDICATOR fix (owner report 2026-07-13 evening): created
    // UNCONDITIONALLY again. The 2026-06-11 thread-mode skip
    // (BUG-THREAD-UX-REDUNDANT-SPINNER) assumed the active chip lights its
    // own spinner — but S5 pane-owns-active removed the active-chip spinner
    // and X3 removed the rail-level "working…" pill, so on thread-enabled
    // surfaces a send whose turn never reaches the orchestrator block
    // (awaiting-input resume, queue drain, running-workflow skip) had NO
    // in-pane indicator at all — just a subtle chip glow. History since:
    // the 2026-07-14 uniform-indicator directive brought the active chip's
    // spinner back, and the 2026-08-01 owner override keeps it visible even
    // while this pane widget is on screen — pane widget + summary-row
    // indicator COEXIST by design now (the row is the at-a-glance status
    // surface; the pane owns the in-conversation detail). Every downstream
    // handler still removes this widget the moment it posts its own. The
    // originating thread id is passed through so the processing flag stays
    // keyed to the correct thread under concurrent multi-thread sends.
    let immediateThinkingRemoved = false;
    const immediateThinking = startStreamingMessage({
      text: 'thinking...',
      html: getPendingWidgetHtml('thinking'),
      sourceTag: 'immediate_thinking_pending',
    }, originatingThreadId);
    const removeImmediateThinking = () => {
      if (immediateThinkingRemoved || !immediateThinking) return;
      if (removeMessageById) {
        removeMessageById(immediateThinking.id);
      } else {
        removeMessage(immediateThinking.index);
      }
      immediateThinkingRemoved = true;
    };

    // ────────────────────────────────────────────────────────────────────
    // R10: resolve the turn-body attachment payload ONLY NOW — every piece
    // of visible feedback (bubble, composer clear, chat-open, chip
    // spinner, thinking pill) is already on screen. Bounded waits: the
    // storage upgrade races a patience cap (waiting stays unbounded only
    // above the inline-dataUrl hard cap, where the send guard makes it
    // mandatory), then the ingest kickoff races its ≤12s patience.
    // ────────────────────────────────────────────────────────────────────
    await resolveSendAttachmentsForTurn(sendResolution, userMessage);

    // Round J durability invariant: arm the send-ack watchdog AFTER creating
    // the immediate thinking widget (so it can't self-ack) and AFTER the
    // bounded attachment waits above — arming before them would put the
    // upload + ingest patience inside the watchdog's ack window and fire
    // false mid-turn Retry bubbles (the double-dispatch hazard the
    // watchdog's Finding-3 comment documents). If no downstream handler
    // ever streams/appends/releases on this thread, the watchdog releases
    // processing and appends the retryable error bubble. A parallel-
    // authorized second dispatch arms per-turn (slice 1, 2026-08-19) so it
    // cannot cancel the first turn's window.
    armSendAckWatchdog(
      originatingThreadId,
      text,
      undefined,
      parallelDispatchAuthorized ? userMessage.id : undefined,
    );

    // ────────────────────────────────────────────────────────────────────
    // Thread-routed streaming helpers
    //
    // When a chunk/finalize/remove arrives for `originatingThreadId` and
    // the user has switched away to a different thread, the React
    // dispatch path would either drop the update (id not found in
    // active thread's state.messages) or, worse, mutate the wrong
    // thread's history. Instead, route the update directly into the
    // originating thread's per-thread localStorage. When the user
    // switches back, useChatThreads.switchThread will load the latest
    // version naturally.
    // ────────────────────────────────────────────────────────────────────
    const isOriginatingThreadActive = (): boolean => {
      if (!originatingThreadId) return true; // no thread switcher → single-thread mode
      const active = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
      return active === originatingThreadId;
    };
    const debugIso = (action: string) => {
      if (typeof window === 'undefined') return;
      try {
        const dbg = typeof localStorage !== 'undefined'
          && localStorage.getItem('DEBUG_THREAD_ISOLATION');
        if (dbg && dbg !== '0' && dbg !== 'false') {
          const active = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
          // eslint-disable-next-line no-console
          console.debug(
            `[threadIso] action=${action} originatingThreadId=${originatingThreadId} activeThreadId=${active}`
          );
        }
      } catch { /* ignore */ }
    };
    const writeToOriginatingThread = (mutator: (msgs: ChatMessage[]) => ChatMessage[]) => {
      if (!originatingThreadId || !readThreadMessagesRef.current || !writeThreadMessagesRef.current) {
        return;
      }
      // PR #112 (Miles 2026-05-10): read fresh from storage at the
      // point of write so the mutator runs against the current
      // on-disk snapshot, not a value captured before any awaited
      // chunk/orchestrator response.
      const fresh = readThreadMessagesRef.current(originatingThreadId) ?? [];
      const next = mutator(fresh);
      writeThreadMessagesRef.current(originatingThreadId, next);
    };
    const routedUpdateById = (id: string, update: Partial<ChatMessage>): boolean => {
      if (isOriginatingThreadActive()) return false; // delegate to base
      writeToOriginatingThread(msgs => {
        const idx = msgs.findIndex(m => m.id === id);
        if (idx === -1) return msgs;
        const next = [...msgs];
        next[idx] = { ...next[idx], ...update };
        return next;
      });
      debugIso('chunk-routed-to-originating');
      return true;
    };
    const routedFinalizeById = (id: string, final: Partial<ChatMessage>): boolean => {
      if (isOriginatingThreadActive()) return false;
      // Owner report 2026-08-20 ("spinning widget disappeared"): an INTERIM
      // background finalize (a mid-turn tool result) used to release the
      // originating thread's processing flag, disarming the rail spinner and
      // the synthetic pane indicator while the turn was still running. Shape
      // truth from the thread's own store decides: release only when no live
      // placeholder remains. Leaked flags stay covered by the stuck-flag sweep.
      let stillWorking = false;
      writeToOriginatingThread(msgs => {
        const idx = msgs.findIndex(m => m.id === id);
        if (idx === -1) {
          stillWorking = msgs.some((m) => isInFlightPlaceholderMessage(m));
          return msgs;
        }
        // Empty-content guard mirrors the in-state finalize (don't leave
        // an empty bubble in the originating thread either).
        const next = [...msgs];
        const merged = { ...next[idx], ...final };
        const hasContent = (merged.text && merged.text.trim().length > 0)
          || (merged.html && merged.html.trim().length > 0);
        const result = hasContent ? (next[idx] = merged, next) : next.filter((_, i) => i !== idx);
        stillWorking = result.some((m) => m.id !== id && isInFlightPlaceholderMessage(m));
        return result;
      });
      // Clear processing for the originating thread when its stream finalizes.
      if (originatingThreadId && !stillWorking) stopProcessing(originatingThreadId);
      debugIso('finalize-routed-to-originating');
      return true;
    };
    const routedRemoveById = (id: string): boolean => {
      if (isOriginatingThreadActive()) return false;
      writeToOriginatingThread(msgs => msgs.filter(m => m.id !== id));
      debugIso('remove-routed-to-originating');
      return true;
    };

    // Custom user message handler (e.g., deep research)
    let handled = false;
    const streamingHelpers = {
      startStreamingMessage,
      updateStreamingMessage,
      // Forward the INCIDENT-20260818 append-on-miss opts (the resume lanes
      // rely on them; the tool lane's 1 Hz widget ticker gets the same net,
      // so a re-entered thread re-materializes its live widget on the next
      // tick instead of no-opping — owner report 2026-08-20).
      updateStreamingMessageById: (id: string, update: Partial<ChatMessage>, opts?: Parameters<typeof updateStreamingMessageById>[2]) => {
        if (routedUpdateById(id, update)) return;
        updateStreamingMessageById(id, update, opts);
      },
      finalizeStreamingMessage,
      // Phase 5: forward the explicit originating thread to the bare finalize
      // for the active-thread path (routedFinalizeById already closure-captures
      // it for the background path).
      finalizeStreamingMessageById: (id: string, final: Partial<ChatMessage>, explicitTid?: string | null) => {
        if (routedFinalizeById(id, final)) return;
        finalizeStreamingMessageById(id, final, explicitTid);
      },
      removeMessage,
      removeMessageById: (id: string) => {
        if (routedRemoveById(id)) return;
        removeMessageById(id);
      },
    };
    // Pin the originating thread id into currentSendThreadIdRef BEFORE
    // calling any helper. The helper internals call startProcessing() /
    // stopProcessing() with no args, which read this ref to resolve the
    // target thread. Without this guard, a second concurrent send (on a
    // different thread) would overwrite the ref and a finalize from the
    // first request would clear the WRONG thread's processing flag — the
    // exact "both spinners stopped" symptom Miles reported.
    const withOriginatingThread = <Args extends unknown[], R>(fn: (...args: Args) => R) =>
      (...args: Args): R => {
        const prev = currentSendThreadIdRef.current;
        currentSendThreadIdRef.current = originatingThreadId;
        try {
          return fn(...args);
        } finally {
          currentSendThreadIdRef.current = prev;
        }
      };

    const helperBundle: any = {
      // Phase 5: pass the send's captured originating thread EXPLICITLY so
      // addMessage/addSystemMessage no longer resolve thread from the shared
      // currentSendThreadIdRef (which a concurrent send can clobber). The
      // withOriginatingThread wrapper below remains as a fallback for any bare
      // internal call, but the exposed helpers no longer depend on it.
      addSystemMessage: (input: SystemMessageInput) => addSystemMessage(input, originatingThreadId),
      addMessage: (message: ChatMessage) => addMessage(message, originatingThreadId),
      // Thread-isolation: surface the originating thread id so downstream
      // handlers (globalTools.createMessageHandler, custom onUserMessage)
      // can key per-thread state (abort controllers, processing flags)
      // by it. Captured at send-time so it's stable across the entire
      // request lifecycle even if the user switches threads mid-flight.
      originatingThreadId,
      // Delivery ladder (PR-2): the dispatcher reuses this id instead of minting.
      clientTaskId: sendClientTaskId,
      // Standing-connect-offer guard (2026-08-20): downstream offer helpers
      // read the thread transcript to dedupe connect cards. Fail-open —
      // storage hooks absent means an empty read, never a throw.
      readThreadMessages: (threadId: string) =>
        (readThreadMessagesRef.current ? readThreadMessagesRef.current(threadId) ?? [] : []),
      streaming: {
        ...streamingHelpers,
        startStreamingMessage: withOriginatingThread((initial: SystemMessageInput) => {
          const result = streamingHelpers.startStreamingMessage(initial, originatingThreadId);
          removeImmediateThinking();
          return result;
        }),
        updateStreamingMessage: withOriginatingThread(streamingHelpers.updateStreamingMessage),
        updateStreamingMessageById: withOriginatingThread(streamingHelpers.updateStreamingMessageById),
        finalizeStreamingMessage: withOriginatingThread((index: number, finalMessage: Partial<ChatMessage>) => {
          streamingHelpers.finalizeStreamingMessage(index, finalMessage, originatingThreadId);
          removeImmediateThinking();
        }),
        finalizeStreamingMessageById: withOriginatingThread((id: string, finalMessage: Partial<ChatMessage>) => {
          streamingHelpers.finalizeStreamingMessageById(id, finalMessage, originatingThreadId);
          removeImmediateThinking();
        }),
        removeMessage: withOriginatingThread((index: number) => {
          streamingHelpers.removeMessage(index);
          if (immediateThinking && index === immediateThinking.index) immediateThinkingRemoved = true;
        }),
        removeMessageById: withOriginatingThread((id: string) => {
          streamingHelpers.removeMessageById(id);
          if (immediateThinking && id === immediateThinking.id) immediateThinkingRemoved = true;
        }),
      },
    };

    // Store helpers in agenticWorkflows ref for button click handlers (event delegation)
    if (agenticWorkflows.chatHelpersRef) {
      agenticWorkflows.chatHelpersRef.current = helperBundle;
    }

    // Store helpers in globalTools ref for button click handlers (event delegation)
    if (globalTools.chatHelpersRef) {
      globalTools.chatHelpersRef.current = helperBundle;
    }

    // Try page-specific custom handler FIRST (before global tools).
    // A page that supplies its own backend handler bypasses the orchestrator
    // entirely (historically the deleted Citadel page; no route uses this today).
    // If the handler returns true, the message is fully handled — skip everything else.
    // If it returns false, fall through to GlobalTools/agentic workflows.
    if (config.onUserMessage) {
      try {
        const result = await config.onUserMessage(userMessage, helperBundle);
        handled = result === true;
        if (handled) removeImmediateThinking();
      } catch (err) {
        console.warn('[GlobalChat] onUserMessage handler failed', err);
      }
    }

    // Try GlobalTools if enabled AND page-specific handler didn't handle it
    if (!handled && USE_GLOBAL_TOOLS && globalTools.isAuthorized) {
      try {
        // CRITICAL: Update shared context with the ORIGINATING thread's
        // history INCLUDING the new user message BEFORE calling
        // orchestrator. When the user has already switched threads,
        // `state.messages` is the now-active thread, not the originating
        // thread. PR #98 reads the originating thread's per-thread
        // storage in that case to prevent B's orchestrator turn from
        // receiving A's conversation history.
        const activeForHistory =
          (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
        const baseHistory = resolveSendHistoryFailClosed({
          originatingThreadId,
          activeThreadId: activeForHistory,
          activeHistory: state.messages,
          readThreadMessages: readThreadMessagesRef.current,
        }) as ChatMessage[];
        const alreadyIncluded = !!(userMessage.id && baseHistory.some(m => m.id === userMessage.id));
        const updatedHistory = alreadyIncluded ? baseHistory : [...baseHistory, userMessage];
        globalTools.sharedContext.setMessageHistory(updatedHistory);

        const globalToolsHandler = globalTools.createMessageHandler();
        const globalToolsResult = await globalToolsHandler(userMessage, helperBundle);
        // Handler returns { handled: true } (object) or true (boolean) — accept both
        handled = globalToolsResult === true
          || (typeof globalToolsResult === 'object' && globalToolsResult !== null && (globalToolsResult as any).handled === true);
        if (handled) removeImmediateThinking();
      } catch (err) {
        console.warn('[GlobalChat] GlobalTools handler failed', err);
      }
    }

    // Fall back to old agentic workflows if GlobalTools didn't handle it
    if (!handled && agenticWorkflows.isAuthorized) {
      try {
        const agenticHandler = createAgenticWorkflowHandler(agenticWorkflows);
        const agenticResult = await agenticHandler(userMessage, helperBundle);
        handled = agenticResult === true;
        if (handled) removeImmediateThinking();
      } catch (err) {
        console.warn('[GlobalChat] Agentic workflow handler failed', err);
      }
    }

    if (handled) {
      return;
    }

    // FIX (2026-07-10, cold-start turn-death processing-flag leak):
    // We reach here only when NO owner took the turn — the page-specific
    // handler, GlobalTools, and the agentic workflow handler each
    // returned/threw without claiming ownership (`handled === false`).
    // The `startProcessing(originatingThreadId)` fired at the top of this
    // send (and/or the GlobalTools heartbeat's global startProcessing in
    // single-thread mode) will therefore NEVER be released: no stream
    // exists to fire onDone/onError. That leaked flag wedges the composer
    // with a stuck "please wait while processing…" gate — and, because the
    // dead pending widget lingers in state.messages, hasComputedPendingState()
    // keeps the orphan-guard from unblocking, so the stuck state carries
    // into New Thread (Malaika 2026-07-10 screenshot 3).
    //
    // A not-handled turn provably has NO live durable background task
    // polling (ownership is signalled by `handled === true`, which returns
    // above and lets the live stream own its own onDone/onError release —
    // never touched here). So releasing on this exact fall-through is safe
    // and does not violate the "never release while a live heartbeat polls"
    // invariant. removeImmediateThinking() also strips the dead pending
    // widget so hasComputedPendingState() can no longer defeat the guard.
    removeImmediateThinking();
    stopProcessing(originatingThreadId);

    // ────────────────────────────────────────────────────────────────────
    // Round J (2026-07-14 addendum): the construction-notice event has NO
    // listener on public pages, so this fall-through used to be TOTAL
    // silence there (verified 5/5 in the anonymous-visitor repro). The
    // fall-through must itself produce a visible in-thread outcome unless a
    // configured auto-reply / platform relay below will do so:
    //  - unauthorized + auth still hydrating → defer + re-dispatch through
    //    the safe envelope once auth settles (signed-in users' pre-hydration
    //    sends must eventually SEND — owner incident account was signed in);
    //  - unauthorized + auth settled → visible sign-in prompt;
    //  - anything else → the retryable send-failure bubble.
    // ────────────────────────────────────────────────────────────────────
    const willAutoReply = !!(config.autoReply && String(config.autoReply.text || '').trim());
    if (!willAutoReply && !config.sendToPlatform) {
      const blockedByAuthGates = USE_GLOBAL_TOOLS
        && !globalTools.isAuthorized
        && !agenticWorkflows.isAuthorized
        && !config.onUserMessage;
      if (blockedByAuthGates && !authHasResolvedRef.current) {
        armSendAckWatchdog(originatingThreadId, text);
        // Finding 2: carry THIS turn's originating thread into the deferral
        // so the post-settle re-dispatch can't chase the active thread.
        void deferSendUntilAuthSettles(text, {}, originatingThreadId);
      } else if (blockedByAuthGates) {
        // G1: settled signed-out → real cloud-only guest turn (replaces the
        // Round-J sign-in prompt; the user bubble is already in the thread).
        runGuestChatTurn(originatingThreadId, text);
      } else {
        appendSendFailureBubble(originatingThreadId, text);
      }
    }

    // Open construction notice (additional signal where a listener exists)
    openConstructionNotice({ text, source: config.source });

    // Auto-reply (page-specific) if configured
    if (config.autoReply && config.autoReply.text) {
      const replyText = String(config.autoReply.text).trim();
      if (replyText) {
        const sendReply = () => {
          // Use addMessage to ensure tags are applied when SHOULD_TAG_AS_TEST=true.
          // Phase 5: explicit thread — this runs in a setTimeout continuation.
          const reply = addMessage({
            text: replyText,
            isUser: false,
            timestamp: new Date(),
            sourceTag: config.autoReply?.sourceTag || 'auto_reply',
          }, originatingThreadId);
          config.onAIResponse?.(reply);
        };
        const delay = typeof config.autoReply.delayMs === 'number' ? config.autoReply.delayMs : 0;
        if (delay > 0) {
          window.setTimeout(sendReply, delay);
        } else {
          sendReply();
        }
      }
    }

    // Send to platform if configured
    if (config.sendToPlatform) {
      const uid = (() => {
        try {
          return getAuth().currentUser?.uid || '';
        } catch {
          return '';
        }
      })();

      if (uid) {
        try {
          const sessionId = (() => {
            try {
              return localStorage.getItem('prest0_active_workflow_session_id') || undefined;
            } catch {
              return undefined;
            }
          })();
          const res = await PlatformMessageService.getInstance().sendIncomingPlatformMessage(text, { session_id: sessionId });
          if (res?.planning_stage?.planned_tools?.length) {
            const tool = res.planning_stage.planned_tools[0];
            const elements = Array.isArray(tool?.elements) ? tool.elements : [];
            const messageElement = elements.find((el: Record<string, unknown>) => el?.key === 'message_content');
            const aiText = String(messageElement?.value || '').trim();
            if (aiText) {
              // Use addMessage to ensure tags are applied when SHOULD_TAG_AS_TEST=true.
              // Phase 5: explicit thread — runs after an await (platform round-trip).
              const reply = addMessage({ text: aiText, isUser: false, timestamp: new Date() }, originatingThreadId);
              dispatchChatEvent(CHAT_EVENTS.ADD_PLATFORM_MESSAGES, [reply]);
              config.onAIResponse?.(reply);
            }
          }
        } catch (error: unknown) {
          console.warn('[PlatformMessage] Request failed:', (error as Record<string, unknown>)?.code || 'UNKNOWN', (error as Error)?.message || error);
        }
      }
    }
  }, [
    state,
    config,
    getCurrentPlainText,
    storageKeys.CHAT_DRAFT,
    addSystemMessage,
    addMessage,
    pinToBottom,
    lowBalanceState.shouldBlock,
    trialExpiredBlock,
    cloudExtractEnabled,
    prest0nSessionAccountId,
    prest0nSessionHook,
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // SAME-THREAD QUEUE DRAIN
  //
  // Miles directive (2026-05-09 23:44 UTC): "yes it should be allowed".
  //
  // Fire a message that was queued by handleSend while the thread
  // had an in-flight turn. The user bubble already lives in history
  // (it was added at queue time). This function runs the orchestrator
  // dispatch path for that bubble, mirroring handleSend's post-bubble
  // logic but skipping prompt/attachment ingestion (already done) and
  // the auto-reply / sendToPlatform branches (kept lean for queue-fired
  // turns; if a future product requirement needs them on queue dispatch
  // they can be added here without touching handleSend).
  // ─────────────────────────────────────────────────────────────────────────
  const firePendingMessage = useCallback(async (pending: PendingMessage) => {
    const originatingThreadId = pending.threadId;

    // Reconstruct a ChatMessage shape from the pending entry. The user
    // bubble was already added to history when handleSend enqueued the
    // message; the orchestrator only needs the message shape (id/text/
    // html/attachments) to fire its turn.
    const userMessage: ChatMessage = {
      id: pending.id,
      text: pending.text,
      isUser: true,
      timestamp: new Date(pending.enqueuedAt),
      html: pending.html,
      attachmentsSerialized: pending.attachmentsSerialized,
      // Ingest-on-upload: re-carry the upload-time extraction job id when it
      // had already resolved at queue time. R10: since handleSend no longer
      // awaits uploads before enqueueing, this is usually still undefined
      // here — the stashed resolution record below performs the same bounded
      // waits as the direct path and patches this message in place.
      ingestJobId: pending.ingestJobId,
    };

    currentSendThreadIdRef.current = originatingThreadId;
    startProcessing(originatingThreadId);

    // MISSING-PANE-INDICATOR fix (owner report 2026-07-13 evening): same
    // unconditional creation as the primary send path — the active-chip
    // spinner this skip relied on no longer exists (S5/X3), so queued turns
    // draining without an orchestrator widget had no in-pane indicator.
    let immediateThinkingRemoved = false;
    const immediateThinking = startStreamingMessage({
      text: 'thinking...',
      html: getPendingWidgetHtml('thinking'),
      sourceTag: 'immediate_thinking_pending',
    }, originatingThreadId);
    const removeImmediateThinking = () => {
      if (immediateThinkingRemoved || !immediateThinking) return;
      if (removeMessageById) {
        removeMessageById(immediateThinking.id);
      } else {
        removeMessage(immediateThinking.index);
      }
      immediateThinkingRemoved = true;
    };

    // ────────────────────────────────────────────────────────────────────
    // R10: a send queued while its background serialization/upload/ingest
    // was still in flight stashed its resolution record at enqueue time.
    // Perform the SAME bounded waits as the direct path — after the
    // thinking pill above, so queue drains keep instant feedback too —
    // and patch the reconstructed message in place. Queued sends thus keep
    // their gcsPath-upgraded attachments + upload-time ingestJobId instead
    // of shipping raw inline dataUrls (or nothing).
    // ────────────────────────────────────────────────────────────────────
    const stashedResolution = pendingSendResolutionsRef.current.get(pending.id);
    if (stashedResolution) {
      pendingSendResolutionsRef.current.delete(pending.id);
      await resolveSendAttachmentsForTurn(stashedResolution, userMessage);
    }

    // Round J: queued turns get the same send-ack watchdog as live sends
    // (armed after the widget so it can't self-ack, and after the bounded
    // attachment waits so they can't sit inside its ack window).
    armSendAckWatchdog(originatingThreadId, pending.text);

    // Queue-drain safety: pending messages may be fired while the user
    // is viewing the same thread, but the active React state can still
    // be one queued turn behind because the prior turn finalized in the
    // same render cycle that triggered this drain. Always route queued
    // turn streaming updates through the originating thread's
    // per-thread storage so each queued reply attaches to its own
    // placeholder and cannot finalize the previous/next queued bubble.
    const isOriginatingThreadActive = (): boolean => false;
    const syncQueuedThreadToActiveView = (messages: ChatMessage[]) => {
      const active = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
      if (active === originatingThreadId) {
        dispatch({ type: 'SET_MESSAGES', payload: messages });
      }
    };
    const writeToOriginatingThread = (mutator: (msgs: ChatMessage[]) => ChatMessage[]) => {
      if (!readThreadMessagesRef.current || !writeThreadMessagesRef.current) return;
      // PR #112 (Miles 2026-05-10): same fresh-read discipline as the
      // sibling helper above — the queued/parallel-stream path is the
      // most likely surface for stale `current` because each queued
      // reply traverses an awaited orchestrator response.
      const fresh = readThreadMessagesRef.current(originatingThreadId) ?? [];
      const next = mutator(fresh);
      writeThreadMessagesRef.current(originatingThreadId, next);
      // Keep the active React view in sync when the user is still
      // looking at this thread. This is a direct SET_MESSAGES from the
      // same per-thread storage snapshot, so it preserves isolation
      // while avoiding stale active-state finalization races.
      syncQueuedThreadToActiveView(next);
    };
    const routedUpdateById = (id: string, update: Partial<ChatMessage>): boolean => {
      if (isOriginatingThreadActive()) return false;
      writeToOriginatingThread(msgs => {
        const idx = msgs.findIndex(m => m.id === id);
        if (idx === -1) return msgs;
        const next = [...msgs];
        next[idx] = { ...next[idx], ...update };
        return next;
      });
      return true;
    };
    const routedFinalizeById = (id: string, final: Partial<ChatMessage>): boolean => {
      if (isOriginatingThreadActive()) return false;
      writeToOriginatingThread(msgs => {
        const idx = msgs.findIndex(m => m.id === id);
        if (idx === -1) return msgs;
        const next = [...msgs];
        const merged = { ...next[idx], ...final };
        const hasContent = (merged.text && merged.text.trim().length > 0)
          || (merged.html && merged.html.trim().length > 0);
        if (!hasContent) {
          return next.filter((_, i) => i !== idx);
        }
        next[idx] = merged;
        return next;
      });
      stopProcessing(originatingThreadId);
      return true;
    };
    const routedRemoveById = (id: string): boolean => {
      if (isOriginatingThreadActive()) return false;
      writeToOriginatingThread(msgs => msgs.filter(m => m.id !== id));
      return true;
    };

    const startQueuedStreamingMessage = (initial: SystemMessageInput): { index: number; id: string } => {
      const id = initial.id || generateMessageId();
      const streamingMessage: ChatMessage = {
        id,
        text: initial.text || '',
        html: initial.html,
        isUser: false,
        timestamp: initial.timestamp || new Date(),
        attachments: initial.attachments,
        attachmentsSerialized: initial.attachmentsSerialized,
        sourceTag: initial.sourceTag,
        tokenUsage: initial.tokenUsage,
        tokenUsageSnapshot: initial.tokenUsageSnapshot,
        tokenUsages: initial.tokenUsages,
        tokenUsageSnapshots: initial.tokenUsageSnapshots,
        messageType: initial.messageType,
        title: initial.title,
        actionButtons: initial.actionButtons,
        priority: initial.priority,
        tags: initial.tags,
      };
      if (!readThreadMessagesRef.current || !writeThreadMessagesRef.current) {
        // Defensive fallback; should not happen when the thread switcher is active.
        return startStreamingMessage(initial);
      }
      startProcessing(originatingThreadId);
      noteStreamStarted();
      // PR #112 (Miles 2026-05-10): read fresh immediately before the
      // write so we don't clobber other writers that may have landed
      // during startProcessing/localStorage.setItem.
      const fresh = readThreadMessagesRef.current(originatingThreadId) ?? [];
      const next = [...fresh, streamingMessage];
      writeThreadMessagesRef.current(originatingThreadId, next);
      syncQueuedThreadToActiveView(next);
      dispatchChatEvent(CHAT_EVENTS.ADD_SYSTEM_MESSAGE, streamingMessage);
      return { index: fresh.length, id };
    };

    const streamingHelpers = {
      startStreamingMessage: startQueuedStreamingMessage,
      updateStreamingMessage,
      updateStreamingMessageById: (id: string, update: Partial<ChatMessage>) => {
        if (routedUpdateById(id, update)) return;
        updateStreamingMessageById(id, update);
      },
      finalizeStreamingMessage,
      // Phase 5: forward explicit originating thread to the bare finalize (active path).
      finalizeStreamingMessageById: (id: string, final: Partial<ChatMessage>, explicitTid?: string | null) => {
        if (routedFinalizeById(id, final)) return;
        finalizeStreamingMessageById(id, final, explicitTid);
      },
      removeMessage,
      removeMessageById: (id: string) => {
        if (routedRemoveById(id)) return;
        removeMessageById(id);
      },
    };
    const withOriginatingThread = <Args extends unknown[], R>(fn: (...args: Args) => R) =>
      (...args: Args): R => {
        const prev = currentSendThreadIdRef.current;
        currentSendThreadIdRef.current = originatingThreadId;
        try { return fn(...args); }
        finally { currentSendThreadIdRef.current = prev; }
      };

    const helperBundle: any = {
      // Phase 5: explicit originating thread (see the primary bundle above).
      addSystemMessage: (input: SystemMessageInput) => addSystemMessage(input, originatingThreadId),
      addMessage: (message: ChatMessage) => addMessage(message, originatingThreadId),
      originatingThreadId,
      // Delivery ladder (PR-2): a queued / offline-held turn dispatches under
      // the id its bubble already carries.
      clientTaskId: pending.clientTaskId,
      // Standing-connect-offer guard (2026-08-20) — see the primary bundle.
      readThreadMessages: (threadId: string) =>
        (readThreadMessagesRef.current ? readThreadMessagesRef.current(threadId) ?? [] : []),
      streaming: {
        ...streamingHelpers,
        // startQueuedStreamingMessage already threads originatingThreadId from
        // its own closure, so no explicit arg is needed here.
        startStreamingMessage: withOriginatingThread((initial: SystemMessageInput) => {
          const result = streamingHelpers.startStreamingMessage(initial);
          removeImmediateThinking();
          return result;
        }),
        updateStreamingMessage: withOriginatingThread(streamingHelpers.updateStreamingMessage),
        updateStreamingMessageById: withOriginatingThread(streamingHelpers.updateStreamingMessageById),
        finalizeStreamingMessage: withOriginatingThread((index: number, finalMessage: Partial<ChatMessage>) => {
          streamingHelpers.finalizeStreamingMessage(index, finalMessage, originatingThreadId);
          removeImmediateThinking();
        }),
        finalizeStreamingMessageById: withOriginatingThread((id: string, finalMessage: Partial<ChatMessage>) => {
          streamingHelpers.finalizeStreamingMessageById(id, finalMessage, originatingThreadId);
          removeImmediateThinking();
        }),
        removeMessage: withOriginatingThread((index: number) => {
          streamingHelpers.removeMessage(index);
          if (immediateThinking && index === immediateThinking.index) immediateThinkingRemoved = true;
        }),
        removeMessageById: withOriginatingThread((id: string) => {
          streamingHelpers.removeMessageById(id);
          if (immediateThinking && id === immediateThinking.id) immediateThinkingRemoved = true;
        }),
      },
    };

    if (agenticWorkflows.chatHelpersRef) agenticWorkflows.chatHelpersRef.current = helperBundle;
    if (globalTools.chatHelpersRef) globalTools.chatHelpersRef.current = helperBundle;

    let handled = false;
    if (config.onUserMessage) {
      try {
        const result = await config.onUserMessage(userMessage, helperBundle);
        handled = result === true;
        if (handled) removeImmediateThinking();
      } catch (err) {
        console.warn('[GlobalChat] onUserMessage handler failed (queued)', err);
      }
    }
    if (!handled && USE_GLOBAL_TOOLS && globalTools.isAuthorized) {
      try {
        // PR #98: queued turns run after the user may have switched
        // away. Use the queued/originating thread's stored history, not
        // whichever thread is currently active in `state.messages`.
        const activeForHistory =
          (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
        const baseHistory = resolveSendHistoryFailClosed({
          originatingThreadId,
          activeThreadId: activeForHistory,
          activeHistory: state.messages,
          readThreadMessages: readThreadMessagesRef.current,
        }) as ChatMessage[];
        const alreadyIncluded = !!(userMessage.id && baseHistory.some(m => m.id === userMessage.id));
        const updatedHistory = alreadyIncluded ? baseHistory : [...baseHistory, userMessage];
        globalTools.sharedContext.setMessageHistory(updatedHistory);
        const globalToolsHandler = globalTools.createMessageHandler();
        const globalToolsResult = await globalToolsHandler(userMessage, helperBundle);
        handled = globalToolsResult === true
          || (typeof globalToolsResult === 'object' && globalToolsResult !== null && (globalToolsResult as any).handled === true);
        if (handled) removeImmediateThinking();
      } catch (err) {
        console.warn('[GlobalChat] GlobalTools handler failed (queued)', err);
      }
    }
    if (!handled && agenticWorkflows.isAuthorized) {
      try {
        const agenticHandler = createAgenticWorkflowHandler(agenticWorkflows);
        const agenticResult = await agenticHandler(userMessage, helperBundle);
        handled = agenticResult === true;
        if (handled) removeImmediateThinking();
      } catch (err) {
        console.warn('[GlobalChat] Agentic workflow handler failed (queued)', err);
      }
    }
    if (!handled) {
      removeImmediateThinking();
      // No orchestrator picked up the queued message: clear processing.
      stopProcessing(originatingThreadId);
      // Round J: a silently-dropped queued turn is the same bug class as a
      // silent live send — surface the retryable bubble.
      appendSendFailureBubble(originatingThreadId, pending.text);
    }
  }, [state.messages, config, addSystemMessage, addMessage, startStreamingMessage,
      updateStreamingMessage, updateStreamingMessageById, finalizeStreamingMessage,
      finalizeStreamingMessageById, removeMessage, removeMessageById,
      startProcessing, stopProcessing, agenticWorkflows, globalTools]);

  // ─────────────────────────────────────────────────────────────────────────
  // QUEUE DRAIN EFFECT
  //
  // When a thread transitions from processing → NOT processing AND
  // its pending queue has a head, dequeue + fire the head as a fresh
  // turn. We use a ref to avoid double-firing if React re-renders
  // mid-drain.
  // ─────────────────────────────────────────────────────────────────────────
  const drainingThreadsRef = useRef<Set<string>>(new Set());
  // Round J.1 (Finding 1b): held drains re-check timer — a hold expiry
  // produces no render of its own, so the effect schedules its own
  // re-evaluation via drainHoldTick (declared with the watchdog infra).
  const drainHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // OFFLINE HOLD (PR-2): a queue drained with no network fails on the spot,
  // so the drain waits for `online` — the listener re-runs the effect.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = (): void => setDrainHoldTick((t) => t + 1);
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const pending = state.pendingByThread || {};
    const proc = state.processingByThread || {};
    let minHoldRemainingMs = Infinity;
    for (const [tid, queue] of Object.entries(pending)) {
      if (!queue || queue.length === 0) continue;
      if (proc[tid]) continue; // still streaming on this thread
      // In-thread parallelism slice 1 (2026-08-19): LEDGER BELT — during
      // parallel operation the first finisher clears the flag while its
      // sibling still runs; queued sends must keep holding until the ledger
      // shows the whole thread idle (queue drain stays strictly serial).
      if (
        typeof prest0nSessionHook.countLiveTurnsForThread === 'function' &&
        prest0nSessionHook.countLiveTurnsForThread(tid) >= 1
      ) continue;
      // Round J.1 (Finding 1b): processing=false is NOT a terminal signal
      // when it was the WATCHDOG that released it — the original turn may
      // still be live (slow gate / degraded network), and draining now
      // would run two concurrent turns on this thread. Hold until a
      // genuine signal clears the fire record (late ack, terminal release,
      // error bubble) or the hold window lapses.
      const holdMs = sendAckWatchdogRef.current?.lateAckHoldRemainingMs(tid) ?? 0;
      if (holdMs > 0) {
        minHoldRemainingMs = Math.min(minHoldRemainingMs, holdMs);
        continue;
      }
      if (drainingThreadsRef.current.has(tid)) continue; // already firing
      const head = queue[0];
      if (!head || head.status !== 'queued') continue;
      drainingThreadsRef.current.add(tid);
      // Dequeue first to maintain a single source of truth, then fire.
      dispatch({ type: 'DEQUEUE_PENDING_HEAD', payload: { threadId: tid } });
      Promise.resolve().then(() => firePendingMessage(head))
        .catch(err => console.warn('[GlobalChat] firePendingMessage failed', err))
        .finally(() => { drainingThreadsRef.current.delete(tid); });
    }
    // Schedule a re-check for the soonest hold expiry (only while something
    // is actually held). Cleared on every effect re-run so at most one
    // timer is pending.
    if (drainHoldTimerRef.current) {
      clearTimeout(drainHoldTimerRef.current);
      drainHoldTimerRef.current = null;
    }
    if (Number.isFinite(minHoldRemainingMs)) {
      drainHoldTimerRef.current = setTimeout(
        () => setDrainHoldTick((t) => t + 1),
        Math.max(250, minHoldRemainingMs + 50),
      );
    }
    return () => {
      if (drainHoldTimerRef.current) {
        clearTimeout(drainHoldTimerRef.current);
        drainHoldTimerRef.current = null;
      }
    };
  }, [state.pendingByThread, state.processingByThread, firePendingMessage, drainHoldTick]);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    // Mark as actively typing - prevents sync from overwriting user input
    promptFocusedRef.current = true;
    
    const el = e.currentTarget;

    // Helper: compute caret offset (characters) from start of element
    const getCaretOffset = () => {
      try {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;
        const range = sel.getRangeAt(0);
        const preRange = range.cloneRange();
        preRange.selectNodeContents(el);
        preRange.setEnd(range.startContainer, range.startOffset);
        return preRange.toString().length;
      } catch {
        return null;
      }
    };

    // Helper: set caret to a character offset within element text content
    const setCaretOffset = (offset: number) => {
      try {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        let current = walker.nextNode();
        let remaining = offset;
        while (current) {
          const len = (current.textContent || '').length;
          if (remaining <= len) {
            const range = document.createRange();
            range.setStart(current, Math.max(0, remaining));
            range.collapse(true);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
            return;
          }
          remaining -= len;
          current = walker.nextNode();
        }
        // Fallback: collapse at end
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      } catch {
        /* noop */
      }
    };

    const rawHtml = el.innerHTML;
    const caretOffset = getCaretOffset();

    // Trim trailing blank lines (<br> or <div><br></div>) to avoid extra space at end
    const trimmed = rawHtml.replace(/(?:(?:<div><br><\/div>)|(?:<br>))+\s*$/gi, '');
    const sanitized = sanitizeHtmlInput(trimmed);

    const sanitizedChanged = sanitized !== rawHtml;

    if (sanitizedChanged) {
      el.innerHTML = sanitized;
      // Restore caret near previous logical position if possible
      const textLen = el.textContent?.length ?? 0;
      const targetOffset = caretOffset != null ? Math.min(caretOffset, textLen) : textLen;
      setCaretOffset(targetOffset);
    }

    dispatch({ type: 'SET_PROMPT_HTML', payload: sanitized });
    dispatch({ type: 'SET_PROMPT', payload: htmlToPlainText(sanitized) });

    // Debounce draft persistence
    if (config.persistDraft) {
      if (draftPersistTimeoutRef.current) {
        window.clearTimeout(draftPersistTimeoutRef.current);
      }
      // Stash the payload for the pagehide flush: if the page unloads before
      // the 500 ms debounce fires (update-banner refresh, F5, tab close),
      // the flush effect persists exactly this through the same guards.
      draftPendingFlushRef.current = { html: sanitized, text: htmlToPlainText(sanitized) };
      draftPersistTimeoutRef.current = window.setTimeout(() => {
        draftPendingFlushRef.current = null;
        // FH-QA composer-refill race (2026-05-21 13:50 UTC): the
        // GlobalChatSystem send wrapper stamps the shared suppression
        // singleton synchronously BEFORE invoking handleSend. handleSend
        // then awaits serializeAttachments before clearing the CHAT_DRAFT
        // key and cancelling THIS timeout. If the 500 ms debounce fires
        // during that await window, its closure carries the pre-send
        // `sanitized` text and would rewrite CHAT_DRAFT with the just-
        // typed text — which downstream pollers/storage-event listeners
        // would then rehydrate back into the composer. Bail at fire-time
        // when suppression is active so the closure can't win the race.
        if (shouldSuppressDraftRehydrate()) return;
        const draftPayload = { html: sanitized, text: htmlToPlainText(sanitized) };
        // FH-QA composer-refill race on LONG NON-attachment prompts
        // (2026-05-21 16:25 UTC): even if the time-domain suppression
        // window happens to be closed when this closure fires (e.g. a
        // backgrounded tab unparked the timer after a long delay),
        // refuse to persist a payload whose body matches the just-sent
        // sentinel. The sentinel is stamped synchronously inside
        // handleSend before RESET_PROMPT and expires after
        // SENT_TEXT_SENTINEL_MS (60 s).
        if (draftIsSentTextMatch(draftPayload.text, draftPayload.html)) return;
        draftLastSavedRef.current = JSON.stringify(draftPayload);
        setToLocalStorage(storageKeys.CHAT_DRAFT, draftPayload);
      }, 500);
    }
  }, [config.persistDraft, storageKeys.CHAT_DRAFT]);

  // ============================================================================
  // Draft flush on page exit (deploy-freshness doctrine, 2026-08-20).
  // The draft save above is debounced 500 ms, so a reload landing inside that
  // window — most visibly the update banner's "refresh now" click — silently
  // dropped the just-typed tail of the composer ("even your half-typed
  // message" is part of the owner directive; the banner promises the draft
  // survives). On pagehide, fire the pending save synchronously through the
  // SAME guards the debounced closure uses (suppression window + sent-text
  // sentinel), so this flush cannot resurrect just-sent text — see
  // draftSuppressionState.ts. Idempotent with a later bfcache-resumed timer:
  // both write the same payload. pagehide over beforeunload: it also covers
  // bfcache navigations and fires reliably on mobile Safari.
  // ============================================================================
  useEffect(() => {
    if (!config.persistDraft) return;
    const flushPendingDraft = () => {
      const pending = draftPendingFlushRef.current;
      if (!pending) return;
      draftPendingFlushRef.current = null;
      if (shouldSuppressDraftRehydrate()) return;
      if (draftIsSentTextMatch(pending.text, pending.html)) return;
      draftLastSavedRef.current = JSON.stringify(pending);
      setToLocalStorage(storageKeys.CHAT_DRAFT, pending);
    };
    window.addEventListener('pagehide', flushPendingDraft);
    return () => window.removeEventListener('pagehide', flushPendingDraft);
  }, [config.persistDraft, storageKeys.CHAT_DRAFT]);

  // ── ATTACHMENT HEAL ON HYDRATE (chat-thread PR-6, 2026-09-09) ──────────
  // A record that still has ONLY its base64 copy (the upload failed at send and
  // the in-session retries never ran — the tab closed, the phone slept) is the
  // FIRST to vanish: the server transcript strips inline payloads, so it lives
  // in this browser's local storage only. When the OWNER's browser next shows
  // such a message, re-upload the bytes from the base64 copy and merge the won
  // locators in — additively, a few per pass, one attempt per record per
  // session, never while offline. See attachmentHealOnHydrate.ts.
  const healOnHydrateAttemptedRef = useRef<Set<string>>(new Set());
  const healOnHydrateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const healOnHydrateInFlightRef = useRef(false);
  useEffect(() => {
    if (!attachmentHealOnHydrateEnabled()) return;
    const uid = getCurrentUserUid();
    if (!uid) return;
    const scheduledFrom = state.messages;
    const candidates = attachmentHealCandidates(scheduledFrom, uid, healOnHydrateAttemptedRef.current);
    if (candidates.length === 0) return;
    if (healOnHydrateTimerRef.current) clearTimeout(healOnHydrateTimerRef.current);
    healOnHydrateTimerRef.current = setTimeout(() => {
      healOnHydrateTimerRef.current = null;
      if (healOnHydrateInFlightRef.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      healOnHydrateInFlightRef.current = true;
      const threadId = getActiveThreadIdRef.current ? getActiveThreadIdRef.current() : null;
      void runAttachmentHealOnHydrate({
        candidates,
        attempted: healOnHydrateAttemptedRef.current,
        upload: (files, messageId) => serializeAttachmentsWithStorage(files, uid, messageId),
        readMessages: () => (
          (threadId && readThreadMessagesRef.current ? readThreadMessagesRef.current(threadId) : null)
          ?? scheduledFrom
        ),
        writeMessages: (next) => {
          if (threadId && writeThreadMessagesRef.current) writeThreadMessagesRef.current(threadId, next);
        },
        patchState: (messageId, attachmentsSerialized) => {
          const activeNow = getActiveThreadIdRef.current ? getActiveThreadIdRef.current() : null;
          if (activeNow !== threadId) return;
          dispatch({ type: 'UPDATE_MESSAGE_BY_ID', payload: { id: messageId, message: { attachmentsSerialized } } });
        },
      }).finally(() => { healOnHydrateInFlightRef.current = false; });
    }, ATTACHMENT_HEAL_ON_HYDRATE_DELAY_MS);
  }, [state.messages, dispatch]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    // PR-6 (2026-09-09): BOTH clipboard surfaces — `.files` and the file items
    // (a screenshot copied out of a viewer arrives as an item only).
    const files = filesFromClipboard(e.clipboardData);
    const hasFiles = files.length > 0;

    if (hasFiles && config.allowAttachments) {
      const { validFiles, oversizedFiles } = validateFiles(files, config.maxFileSize);
      let validFilesToKeep = validFiles;

      const maxAttachments = MAX_ATTACHMENTS_PER_MESSAGE;
      const allowedCount = maxAttachments - state.attachments.length;

      if (validFilesToKeep.length > allowedCount) {
        alert(`You can only attach up to ${maxAttachments} files per message. Additional files were not added.`);
        validFilesToKeep = validFilesToKeep.slice(0, Math.max(0, allowedCount));
      }

      if (validFilesToKeep.length > 0) {
        e.preventDefault();
        dispatch({ type: 'ADD_ATTACHMENTS', payload: validFilesToKeep });
        dispatch({ type: 'SET_ATTACHMENT_PANEL_VISIBLE', payload: true });
        dispatch({ type: 'SET_CHAT_OPEN', payload: true });
      }

      if (oversizedFiles.length > 0) {
        alert(`The following file(s) exceed the ${Math.round(config.maxFileSize / (1024 * 1024))}MB size limit and were not added:\n${oversizedFiles.join('\n')}`);
      }
      return;
    }

    const html = e.clipboardData?.getData('text/html') || '';
    const plain = e.clipboardData?.getData('text/plain') || '';
    if (html || plain) {
      e.preventDefault();
      // Paste hygiene (owner report 2026-07-13): rich clipboard HTML goes
      // through sanitizePastedHtml — contrast-fixes unreadable near-white
      // inline colors, strips pasted backgrounds, and trims the empty
      // wrapper blocks / <br> chains that rendered as un-removable blank
      // space. Plain-text-only pastes are edge-trimmed and inserted as
      // LITERAL text (escaped, newlines → <br>) so clipboard text can never
      // be parsed as markup. Pasted FILES never reach this branch — they
      // return above as attachments.
      const sanitized = html
        ? sanitizePastedHtml(html)
        : plainTextToComposerHtml(sanitizePastedText(plain));
      if (!sanitized) return; // whitespace-only paste: insert nothing

      // Whole-composer trim (owner follow-up 2026-07-14): the pasted FRAGMENT
      // is edge-trimmed by the sanitizers above, but the COMPOSED prompt
      // (previous content + <br> + fragment) can still carry edge empties
      // contributed by the pre-existing content — e.g. the stray <br> browsers
      // leave in an "emptied" contentEditable becomes a leading blank line
      // once real content follows it. Trim the composed whole so no edge blank
      // space ever survives composition (interior structure is untouched).
      const newHtml = trimEdgeEmptyBlocks(
        state.promptHtml
          ? `${state.promptHtml}${state.promptHtml.endsWith('<br>') ? '' : '<br>'}${sanitized}`
          : sanitized
      );

      dispatch({ type: 'SET_PROMPT_HTML', payload: newHtml });
      dispatch({ type: 'SET_PROMPT', payload: htmlToPlainText(newHtml) });

      if (promptInputRef.current) {
        if (promptInputRef.current.innerHTML !== newHtml) {
          promptInputRef.current.innerHTML = newHtml;
        }
        try {
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(promptInputRef.current);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        } catch {}
      }
    }
  }, [config.allowAttachments, config.maxFileSize, state.promptHtml]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>, source: 'main' | 'floating' = 'main') => {
    // Enter policy (owner report 2026-09-07): never send mid-IME-composition,
    // never on Shift+Enter, and on a coarse (touch) pointer Enter inserts a
    // newline — the send button sends. See composerEnterPolicy.ts.
    const native = e.nativeEvent as KeyboardEvent & { isComposing?: boolean; keyCode?: number };
    const shouldSend = enterShouldSend({
      key: e.key,
      shiftKey: e.shiftKey,
      isComposing: native?.isComposing,
      keyCode: native?.keyCode,
      coarsePointer: isCoarsePointer(undefined, state.isMobileViewport),
    });
    if (shouldSend) {
      e.preventDefault();
      // Expired trial: no sends (owner directive 2026-07-31)
      if (trialExpiredBlock) {
        return;
      }
      // Block send if balance is too low
      if (lowBalanceState.shouldBlock) {
        return;
      }
      // Block send if system is processing
      if (isSystemProcessing()) {
        onProcessingBlockedRef.current?.(source);
        return;
      }
      // Route through the host's send wrapper when one is installed, so Enter
      // and the send buttons take the SAME path (draft sentinels, @-mention
      // summon, the summon gate). Enter used to call handleSend directly,
      // which silently bypassed all of it (2026-08-07 incident: the summon
      // gate never suppressed a keyboard send).
      void (sendRouteOverrideRef.current ?? handleSend)();
    }
  }, [handleSend, isSystemProcessing, lowBalanceState.shouldBlock, trialExpiredBlock, state.isMobileViewport]);

  const handleToggleDock = useCallback(() => {
    const nextDock = !state.isChatDockedRight;
    userInitiatedOpenRef.current = true;
    dispatch({ type: 'SET_CHAT_DOCKED_RIGHT', payload: nextDock });
    dispatch({ type: 'SET_CHAT_OPEN', payload: true });
    persistPageDock(nextDock);
    persistPageOpen(true);
  }, [state.isChatDockedRight, persistPageDock, persistPageOpen]);

  const handleToggleFullScreen = useCallback(() => {
    const nextFullScreen = !state.isChatFullScreen;
    userInitiatedOpenRef.current = true;
    dispatch({ type: 'SET_CHAT_FULLSCREEN', payload: nextFullScreen });
    // When entering fullscreen, ensure chat is open and undock
    if (nextFullScreen) {
      dispatch({ type: 'SET_CHAT_OPEN', payload: true });
      dispatch({ type: 'SET_CHAT_DOCKED_RIGHT', payload: false });
      persistPageOpen(true);
      persistPageDock(false);
    }
  }, [state.isChatFullScreen, persistPageOpen, persistPageDock]);

  const handleCycleMode = useCallback(() => {
    userInitiatedOpenRef.current = true;

    // Mobile has NO mode to cycle (mobile UX round 2026-08-14): open IS the
    // fullscreen takeover — the reducer forces it on every open, and the
    // only non-fullscreen mobile state is closed. The mode-cycle button no
    // longer renders on mobile; this branch keeps any stray caller safe by
    // re-asserting the canonical open state instead of toggling into the
    // banned floating-open layout. (Supersedes the 2026-08-12 two-state
    // default ⇄ fullscreen cycle.)
    if (state.isMobileViewport) {
      dispatch({ type: 'SET_CHAT_FULLSCREEN', payload: true });
      dispatch({ type: 'SET_CHAT_OPEN', payload: true });
      persistPageOpen(true);
      return;
    }

    if (state.isChatFullScreen) {
      // Fullscreen → Default (centered, not docked)
      dispatch({ type: 'SET_CHAT_FULLSCREEN', payload: false });
      dispatch({ type: 'SET_CHAT_DOCKED_RIGHT', payload: false });
      persistPageDock(false);
    } else if (state.isChatDockedRight) {
      // Docked → Fullscreen
      dispatch({ type: 'SET_CHAT_FULLSCREEN', payload: true });
      dispatch({ type: 'SET_CHAT_DOCKED_RIGHT', payload: false });
      persistPageDock(false);
    } else {
      // Default → Docked right
      dispatch({ type: 'SET_CHAT_DOCKED_RIGHT', payload: true });
      dispatch({ type: 'SET_CHAT_OPEN', payload: true });
      persistPageDock(true);
      persistPageOpen(true);
    }
  }, [state.isChatFullScreen, state.isChatDockedRight, state.isMobileViewport, persistPageDock, persistPageOpen]);

  const handleFileSelect = useCallback(() => {
    userInitiatedOpenRef.current = true;
    dispatch({ type: 'SET_CHAT_OPEN', payload: true });
    persistPageOpen(true);
    fileInputRef.current?.click();
  }, [persistPageOpen]);

  // Handler for prompt input focus - prevents sync from overwriting while typing
  const handlePromptFocus = useCallback(() => {
    promptFocusedRef.current = true;
    // Only auto-open on focus if configured and page state has been restored
    if (config.autoOpenOnFocus && pageStateRestoredRef.current) {
      userInitiatedOpenRef.current = true;
      dispatch({ type: 'SET_CHAT_OPEN', payload: true });
      persistPageOpen(true);
    }
  }, [config.autoOpenOnFocus, persistPageOpen]);

  // Handler for prompt input blur - allows sync to resume
  const handlePromptBlur = useCallback(() => {
    promptFocusedRef.current = false;
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const { validFiles, oversizedFiles } = validateFiles(files, config.maxFileSize);
    let validFilesToKeep = validFiles;

    const maxAttachments = MAX_ATTACHMENTS_PER_MESSAGE;
    const allowedCount = maxAttachments - state.attachments.length;

    if (validFilesToKeep.length > allowedCount) {
      alert(`You can only attach up to ${maxAttachments} files per message. Additional files were not added.`);
      validFilesToKeep = validFilesToKeep.slice(0, Math.max(0, allowedCount));
    }

    if (validFilesToKeep.length > 0) {
      userInitiatedOpenRef.current = true;
      dispatch({ type: 'ADD_ATTACHMENTS', payload: validFilesToKeep });
      dispatch({ type: 'SET_ATTACHMENT_PANEL_VISIBLE', payload: true });
      dispatch({ type: 'SET_CHAT_OPEN', payload: true });
      persistPageOpen(true);
    }

    if (oversizedFiles.length > 0) {
      alert(`The following file(s) exceed the ${Math.round(config.maxFileSize / (1024 * 1024))}MB size limit and were not added:\n${oversizedFiles.join('\n')}`);
    }

    // Reset file input
    if (e.target) {
      e.target.value = '';
    }
  }, [config.maxFileSize, state.attachments.length]);

  const handleRemoveAttachment = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_ATTACHMENT', payload: index });
    if (state.attachments.length === 1) {
      setTimeout(() => dispatch({ type: 'SET_ATTACHMENT_PANEL_VISIBLE', payload: false }), 300);
    }
  }, [state.attachments.length]);

  const handleClearAttachments = useCallback(() => {
    dispatch({ type: 'CLEAR_ATTACHMENTS' });
  }, []);

  const toggleAttachmentPanel = useCallback(() => {
    if (state.attachments.length > 0) {
      const next = !state.isAttachmentPanelVisible;
      dispatch({ type: 'SET_ATTACHMENT_PANEL_VISIBLE', payload: next });
      dispatch({ type: 'SET_USER_COLLAPSED_ATTACHMENTS', payload: !next });
    }
  }, [state.attachments.length, state.isAttachmentPanelVisible]);

  const handleAttachmentButtonClick = useCallback((e?: React.MouseEvent) => {
    try { e?.stopPropagation(); } catch {}
    if (!state.isChatOpen) return;

    // Fix (2026-05-11): Previously, if attachments > 0, this button
    // toggled an Attachment Preview Panel. Since the preview is now
    // an inline horizontal strip, this button must always trigger the
    // file picker directly so users can attach more files.
    handleFileSelect();
  }, [state.isChatOpen, handleFileSelect]);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'SET_DRAGGING', payload: true });
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = promptBarRef.current?.getBoundingClientRect();
    if (rect) {
      const { clientX, clientY } = e;
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        dispatch({ type: 'SET_DRAGGING', payload: false });
      }
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'SET_DRAGGING', payload: false });

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const { validFiles, oversizedFiles } = validateFiles(files, config.maxFileSize);
    let validFilesToKeep = validFiles;

    const maxAttachments = MAX_ATTACHMENTS_PER_MESSAGE;
    const allowedCount = maxAttachments - state.attachments.length;

    if (validFilesToKeep.length > allowedCount) {
      alert(`You can only attach up to ${maxAttachments} files per message. Additional files were not added.`);
      validFilesToKeep = validFilesToKeep.slice(0, Math.max(0, allowedCount));
    }

    if (validFilesToKeep.length > 0) {
      dispatch({ type: 'ADD_ATTACHMENTS', payload: validFilesToKeep });
      dispatch({ type: 'SET_ATTACHMENT_PANEL_VISIBLE', payload: true });
      dispatch({ type: 'SET_CHAT_OPEN', payload: true });
      persistPageOpen(true);
    }

    if (oversizedFiles.length > 0) {
      alert(`The following file(s) exceed the ${Math.round(config.maxFileSize / (1024 * 1024))}MB size limit and were not added:\n${oversizedFiles.join('\n')}`);
    }
  }, [config.maxFileSize, state.attachments.length]);

  const handleHide = useCallback(() => {
    dispatch({
      type: 'SET_LAST_ATTACHMENT_VISIBLE_ON_HIDE',
      payload: state.isAttachmentPanelVisible && !state.userCollapsedAttachments && state.attachments.length > 0,
    });
    dispatch({ type: 'SET_CHAT_OPEN', payload: false });
    userInitiatedOpenRef.current = false;
    persistPageOpen(false); // Persist immediately to avoid losing state on fast navigation
  }, [state.isAttachmentPanelVisible, state.userCollapsedAttachments, state.attachments.length, persistPageOpen]);

  const handleOpen = useCallback(() => {
    // Only allow opening after page state has been restored to prevent auto-open on page load
    if (!pageStateRestoredRef.current) return;
    userInitiatedOpenRef.current = true;
    dispatch({ type: 'SET_CHAT_OPEN', payload: true });
    persistPageOpen(true);
  }, [persistPageOpen]);

  /**
   * Show the clear chat confirmation modal.
   */
  const handleClearChat = useCallback(() => {
    dispatch({ type: 'SET_CLEAR_CONFIRM_MODAL', payload: true });
  }, []);

  /**
   * Hide the clear chat confirmation modal.
   */
  const hideClearConfirmation = useCallback(() => {
    dispatch({ type: 'SET_CLEAR_CONFIRM_MODAL', payload: false });
  }, []);

  /**
   * Actually clear all chat history from React state, localStorage, and Firestore.
   * Called from confirmation modal. Does NOT affect wallet data (separate Firestore path).
   *
   * Coverage (Miles 2026-05-12, Slack thread C0AM8U804KZ/1778481193.723179):
   *   • React state.messages → []
   *   • Per-instance localStorage CHAT_HISTORY + CHAT_DRAFT
   *   • Legacy global keys GLOBAL_CHAT_HISTORY_KEY + GLOBAL_CHAT_DRAFT_KEY
   *     (these are the keys ConversationHistoryContext polls; if we leave
   *     them populated the next 2-second poll re-pushes the cleared
   *     content to Firestore, undoing our wipe).
   *   • Firestore conversation_history doc — namespace-aware (default
   *     `user_history`; tenant configs may override via
   *     `config.firestoreNamespace`).
   *   • Per-thread message storage and the thread store itself —
   *     handled by the `confirmClearChatWithReset` wrapper in
   *     GlobalChatSystem.tsx, which has access to the thread-store hook.
   */
  const confirmClearChat = useCallback(async () => {
    // 1. Hide the modal
    dispatch({ type: 'SET_CLEAR_CONFIRM_MODAL', payload: false });

    // 2. Clear React state immediately (optimistic UI update)
    dispatch({ type: 'CLEAR_CHAT' });

    // 3. Clear localStorage — both per-instance keys AND the legacy
    //    global keys. Without the global keys the
    //    ConversationHistoryContext.tsx 2-second poll re-pushes the
    //    pre-clear content right back to Firestore on the next tick.
    try {
      removeFromLocalStorage(storageKeys.CHAT_HISTORY);
      removeFromLocalStorage(storageKeys.CHAT_DRAFT);
      // Legacy global keys (cross-page chat history). Always remove
      // these on clear regardless of which key shape this surface uses
      // — they are the single doc the Firestore poller reads from.
      removeFromLocalStorage(GLOBAL_CHAT_HISTORY_KEY);
      removeFromLocalStorage(GLOBAL_CHAT_DRAFT_KEY);
    } catch (err) {
      console.warn('[ClearChat] Failed to clear localStorage:', err);
    }

    // 4. Clear Firestore (async, fire-and-forget with error logging).
    //    Pass-through `firestoreNamespace` from the chat config so
    //    tenant surfaces with custom namespaces wipe the right doc.
    //    Default namespace ('user_history') is the same as
    //    omitting it, so existing single-tenant behaviour is unchanged.
    try {
      const uid = getAuth().currentUser?.uid;
      if (uid) {
        // Empty-string sentinel from DEFAULT_CHAT_CONFIG → undefined so
        // the repo falls through to the canonical `user_history` path.
        const rawNamespace = (config as { firestoreNamespace?: string }).firestoreNamespace;
        const namespace = rawNamespace && rawNamespace.length > 0 ? rawNamespace : undefined;
        await clearConversationHistory(uid, namespace);
        console.log(
          '[ClearChat] Firestore conversation history cleared',
          namespace ? `(namespace=${namespace})` : '(default namespace)'
        );
      }
    } catch (err) {
      console.error('[ClearChat] Failed to clear Firestore history:', err);
      // Don't throw - localStorage and state are already cleared
      // User sees empty chat which is the expected behavior
    }

    // 5. Clear draft input in UI
    if (promptInputRef.current) {
      promptInputRef.current.innerHTML = '';
      promptInputRef.current.textContent = '';
    }
    dispatch({ type: 'SET_PROMPT', payload: '' });
    dispatch({ type: 'SET_PROMPT_HTML', payload: '' });

    // 6. Session-hardening S3 (Miles 2026-07-12): on non-thread-switcher
    //    surfaces the server conversation is keyed by the account's standalone
    //    conversation id — rotate it so the NEXT send derives a brand-new
    //    server session. Without this, the server's last-12-turn transcript
    //    for the old session would keep haunting a visually-cleared chat
    //    ("delete doesn't start fresh"). Threading surfaces get fresh ids per
    //    thread and never read the standalone id.
    if (!config.enableThreadSwitcher && prest0nSessionAccountId) {
      try {
        rotateStandaloneConversationId(prest0nSessionAccountId);
      } catch { /* best-effort — a failed rotation only preserves old continuity */ }
    }

    console.log('[ClearChat] Chat history cleared successfully');
  }, [storageKeys.CHAT_HISTORY, storageKeys.CHAT_DRAFT, promptInputRef, config, prest0nSessionAccountId]);

  // ============================================================================
  // Programmatic message send (for onboarding welcome, etc.)
  // ============================================================================

  const sendProgrammaticMessage = useCallback(async (text: string, options?: ProgrammaticSendOptions) => {
    if (!text.trim()) return;

    const opts = (options || {}) as InternalProgrammaticSendOptions;
    const hidden = opts.hidden === true;

    // Thread-isolation: programmatic messages target the current active thread
    // (onboarding welcome lands in whichever thread the user is viewing).
    // Captured FIRST so it threads into the user bubble AND the bundle helpers.
    //
    // Finding 2 (2026-07-15): an internal forceThreadId (auth-deferral
    // re-dispatch) pins the send to the thread captured at DEFER time — the
    // user may have switched threads while auth settled, and the original
    // thread owns the user bubble + the armed watchdog.
    const programmaticOriginatingThreadId =
      opts.forceThreadId !== undefined
        ? opts.forceThreadId
        : (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;

    // ────────────────────────────────────────────────────────────────────
    // Round J send-path parity: VISIBLE programmatic sends (starter chips,
    // regenerate, retry) get the same safety envelope as handleSend —
    // session gates, low-balance gate, queue-if-processing, per-thread
    // processing flag, immediate thinking widget, watchdog, and a visible
    // retryable error bubble on every failure path. HIDDEN sends
    // (clarifying-skip, onboarding) keep their minimal envelope — no gates,
    // no spinner — but still surface failures below.
    // ────────────────────────────────────────────────────────────────────
    if (!hidden) {
      // ORG STICKINESS (INCIDENT-20260818): same hold as handleSend — no
      // dispatch under an unresolved workspace identity.
      if (workspaceUnresolved) {
        addSystemMessage({
          text: 'Connecting to your workspace — your message will send as soon as the connection is confirmed. Please retry in a moment.',
          sourceTag: 'workspace_unresolved_block',
        }, programmaticOriginatingThreadId);
        return;
      }
      if (prest0nSessionAccountId && prest0nSessionHook.isLoading) {
        addSystemMessage({
          text: 'Still connecting to Prest0n — please retry in a moment.',
          sourceTag: 'session_init_inflight_block',
        }, programmaticOriginatingThreadId);
        return;
      }
      if (
        prest0nSessionAccountId
        && !prest0nSessionHook.session
        && prest0nSessionHook.error
      ) {
        addSystemMessage({
          text: `Couldn't reach Prest0n — retrying. (${prest0nSessionHook.error.message.slice(0, 160)})`,
          sourceTag: 'session_init_failed_block',
        }, programmaticOriginatingThreadId);
        void prest0nSessionHook.refreshSession();
        return;
      }
      if (trialExpiredBlock) {
        // Graceful visible response for expired trials — programmatic sends
        // have no composer to keep text in (owner directive 2026-07-31).
        addSystemMessage({
          text: TRIAL_ENDED_CHAT_MESSAGE,
          sourceTag: 'system:trial_ended',
        }, programmaticOriginatingThreadId);
        return;
      }
      if (lowBalanceState.shouldBlock) {
        // handleSend returns silently here (the composer keeps the text); a
        // programmatic send has no composer to fall back to, so the gate
        // must be visible. Same canonical copy/tag as the banner injector
        // (staleLowBalanceFilter + retraction semantics apply).
        addSystemMessage({
          text: "⚠️ you're out of PRO Work Units — that's why sending is unavailable. top up or redeem a code to resume instantly.",
          sourceTag: 'system:low_balance',
        }, programmaticOriginatingThreadId);
        return;
      }
    }

    // User bubble. Retry path (skipUserBubble) reuses the original bubble —
    // it already lives in the thread from the failed attempt — instead of
    // appending a duplicate.
    let userMessage: ChatMessage;
    if (opts.skipUserBubble) {
      const existingHistory: ChatMessage[] =
        (programmaticOriginatingThreadId && readThreadMessagesRef.current)
          ? (readThreadMessagesRef.current(programmaticOriginatingThreadId) ?? [])
          : state.messages;
      const existing = [...existingHistory].reverse().find(
        (m) => m.isUser && (m.text || '').trim() === text.trim(),
      );
      userMessage = existing || {
        id: generateMessageId(),
        text,
        isUser: true,
        timestamp: new Date(),
      };
    } else {
      // (Optionally hidden from display via sourceTag.)
      userMessage = addMessage({
        text,
        isUser: true,
        timestamp: new Date(),
        sourceTag: hidden ? 'system:onboarding_welcome' : undefined,
      }, programmaticOriginatingThreadId);
      config.onMessageSent?.(userMessage);
    }

    if (!hidden) {
      // Visible programmatic sends (starter chips, follow-up chips, retry,
      // regenerate) re-pin exactly like a typed send. HIDDEN sends
      // (onboarding bootstrap) must not move the user's viewport.
      pinToBottom('smooth');
    }

    // Queue-if-already-processing (same-thread), mirroring handleSend: never
    // fire a second concurrent turn on a thread — and never go silent about it.
    const alreadyProcessing = !!(
      programmaticOriginatingThreadId
      && state.processingByThread[programmaticOriginatingThreadId]
    );
    // In-thread parallelism slice 1 (2026-08-19): LEDGER BELT + parallel
    // escape — see handleSend's gate for the full rationale.
    const programmaticLedgerLiveCount =
      programmaticOriginatingThreadId && typeof prest0nSessionHook.countLiveTurnsForThread === 'function'
        ? prest0nSessionHook.countLiveTurnsForThread(programmaticOriginatingThreadId)
        : 0;
    let programmaticParallelAuthorized = false;
    if (!hidden && (alreadyProcessing || programmaticLedgerLiveCount >= 1) && programmaticOriginatingThreadId) {
      // 2026-08-14 wedge breaker — same liveness check as handleSend's gate.
      // This is the exact path the Harbor Ledger repro hit: Regenerate on an
      // Interrupted card queued behind the already-dead turn it was
      // regenerating, then sat on "Queued — will send when the current reply
      // finishes" indefinitely.
      const wedgeCheckMsgs = readThreadMessagesRef.current
        ? (readThreadMessagesRef.current(programmaticOriginatingThreadId) ?? state.messages)
        : state.messages;
      // PER-TURN (concurrent-turn state stomp fix, 2026-08-16): judge
      // liveness for THIS thread's own turn — see handleSend's wedge gate.
      const hasPerTurnLedger = typeof prest0nSessionHook.getTurnForThread === 'function';
      const programmaticThreadTurn = hasPerTurnLedger
        ? prest0nSessionHook.getTurnForThread(programmaticOriginatingThreadId)
        : null;
      const programmaticThreadTurnLive = hasPerTurnLedger
        ? !!programmaticThreadTurn && programmaticThreadTurn.phase !== 'terminal'
        : (!!prest0nSessionHook.isStreaming || !!prest0nSessionHook.isPollingTask);
      if (threadHasNoLiveTurn(wedgeCheckMsgs) && !programmaticThreadTurnLive) {
        const pending = pendingFromChatMessage(userMessage, programmaticOriginatingThreadId);
        dispatch({ type: 'ENQUEUE_PENDING', payload: pending });
        console.warn(
          `[GlobalChat] processing flag held by a dead turn on thread ${programmaticOriginatingThreadId} — releasing; queued send fires now`
        );
        stopProcessing(programmaticOriginatingThreadId);
        return;
      }
      // INTERJECTION PARITY (2026-08-19): a programmatic send (follow-up
      // chip, retry, starter) during a LIVE turn attaches to the running
      // task exactly like a typed send — same fold/sweep semantics, same
      // honest lane-keyed acknowledgment. accepted:false falls through to
      // the ordinary queue, so nothing is ever dropped.
      const programmaticLiveTaskId = liveServerTaskIdOf(wedgeCheckMsgs)
        || (programmaticThreadTurn && programmaticThreadTurn.phase === 'polling'
          ? programmaticThreadTurn.taskId
          : null);
      if (programmaticLiveTaskId) {
        const { accepted, lane, verdict, ack } = await postPrest0nTaskInterjection(programmaticLiveTaskId, text);
        if (!accepted && verdict === 'parallel_task' && programmaticLedgerLiveCount < 2) {
          programmaticParallelAuthorized = true;
          addSystemMessage({
            text: ack || 'On it — running this alongside the current task.',
            sourceTag: 'queued_notice',
          }, programmaticOriginatingThreadId);
        }
        if (accepted) {
          // Dynamic ack preferred (2026-08-21); honest lane copy as fallback.
          addSystemMessage({
            text: ack || (lane === 'pipeline'
              ? 'Prest0n sees this — it will be addressed right after the current run.'
              : 'Prest0n sees this — folding it into the reply in progress.'),
            sourceTag: 'queued_notice',
          }, programmaticOriginatingThreadId);
          return;
        }
      }
      if (!programmaticParallelAuthorized) {
        const pending = pendingFromChatMessage(userMessage, programmaticOriginatingThreadId);
        dispatch({ type: 'ENQUEUE_PENDING', payload: pending });
        addSystemMessage({
          text: 'Queued — will send when the current reply finishes.',
          sourceTag: 'queued_notice',
        }, programmaticOriginatingThreadId);
        return;
      }
      // programmaticParallelAuthorized: fall through to the normal dispatch —
      // this message runs as a SECOND CONCURRENT TURN on the thread.
    }

    // Auth gate (Round J addendum): never a silent false.
    //  - auth still hydrating → defer + re-dispatch once settled (signed-in
    //    users' pre-hydration sends must eventually SEND);
    //  - settled signed-out/unauthorized → real guest turn (G1); hidden
    //    system sends keep the one-per-thread sign-in prompt (no visible
    //    user bubble exists for a guest reply to answer).
    if (USE_GLOBAL_TOOLS && !globalTools.isAuthorized) {
      if (!opts.authRetry && !authHasResolvedRef.current) {
        if (!hidden) armSendAckWatchdog(programmaticOriginatingThreadId, text);
        // Finding 2: pin the deferral to THIS send's originating thread.
        void deferSendUntilAuthSettles(text, { hidden }, programmaticOriginatingThreadId);
        return;
      }
      if (hidden) {
        // Finding 8b dedupe still applies to the hidden-send fallback. The
        // triggering text rides along for EN/ES resolution (L3).
        appendSignInPromptOnce(programmaticOriginatingThreadId, text);
        return;
      }
      // G1: settled signed-out visible send → cloud-only guest turn.
      runGuestChatTurn(programmaticOriginatingThreadId, text);
      return;
    }

    // Per-thread processing flag + immediate thinking widget + watchdog —
    // the same pre-dispatch envelope handleSend builds (visible sends only).
    let immediateThinkingRemoved = true;
    let immediateThinking: { index: number; id: string } | null = null;
    if (!hidden) {
      if (programmaticOriginatingThreadId) startProcessing(programmaticOriginatingThreadId);
      immediateThinking = startStreamingMessage({
        text: 'thinking...',
        html: getPendingWidgetHtml('thinking'),
        sourceTag: 'immediate_thinking_pending',
      }, programmaticOriginatingThreadId);
      immediateThinkingRemoved = false;
      armSendAckWatchdog(
        programmaticOriginatingThreadId,
        text,
        undefined,
        programmaticParallelAuthorized ? userMessage.id : undefined,
      );
    }
    const removeImmediateThinking = () => {
      if (immediateThinkingRemoved || !immediateThinking) return;
      if (removeMessageById) {
        removeMessageById(immediateThinking.id);
      } else {
        removeMessage(immediateThinking.index);
      }
      immediateThinkingRemoved = true;
    };

    // Phase 5 (adversarial-review Finding 2): pass the programmatic thread
    // EXPLICITLY into the bundle helpers so the awaited handler's later
    // finalize/addMessage calls can't resolve via a concurrent-send-clobbered
    // ref. Mirrors the two main handleSend bundles — including the
    // remove-thinking hooks so a downstream handler's first widget replaces
    // the immediate one instead of stacking under it.
    const streamingHelpers = {
      startStreamingMessage: (initial: SystemMessageInput) => {
        const result = startStreamingMessage(initial, programmaticOriginatingThreadId);
        removeImmediateThinking();
        return result;
      },
      updateStreamingMessage,
      updateStreamingMessageById,
      finalizeStreamingMessage: (index: number, final: Partial<ChatMessage>) => {
        finalizeStreamingMessage(index, final, programmaticOriginatingThreadId);
        removeImmediateThinking();
      },
      finalizeStreamingMessageById: (id: string, final: Partial<ChatMessage>) => {
        finalizeStreamingMessageById(id, final, programmaticOriginatingThreadId);
        removeImmediateThinking();
      },
      removeMessage: (index: number) => {
        removeMessage(index);
        if (immediateThinking && index === immediateThinking.index) immediateThinkingRemoved = true;
      },
      removeMessageById: (id: string) => {
        removeMessageById(id);
        if (immediateThinking && id === immediateThinking.id) immediateThinkingRemoved = true;
      },
    };
    const helperBundle: any = {
      addSystemMessage: (input: SystemMessageInput) => addSystemMessage(input, programmaticOriginatingThreadId),
      addMessage: (message: ChatMessage) => addMessage(message, programmaticOriginatingThreadId),
      originatingThreadId: programmaticOriginatingThreadId,
      // Standing-connect-offer guard (2026-08-20) — see the primary bundle.
      readThreadMessages: (threadId: string) =>
        (readThreadMessagesRef.current ? readThreadMessagesRef.current(threadId) ?? [] : []),
      streaming: streamingHelpers,
    };

    // Route through GlobalTools (which routes to Prest0n VM)
    let handled = false;
    if (USE_GLOBAL_TOOLS && globalTools.isAuthorized) {
      try {
        const activeForHistory =
          (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
        const baseHistory = resolveSendHistoryFailClosed({
          originatingThreadId: programmaticOriginatingThreadId,
          activeThreadId: activeForHistory,
          activeHistory: state.messages,
          readThreadMessages: readThreadMessagesRef.current,
        }) as ChatMessage[];
        const alreadyIncluded = !!(userMessage.id && baseHistory.some(m => m.id === userMessage.id));
        const updatedHistory = alreadyIncluded ? baseHistory : [...baseHistory, userMessage];
        globalTools.sharedContext.setMessageHistory(updatedHistory);
        const handler = globalTools.createMessageHandler();
        const result = await handler(userMessage, helperBundle);
        handled = result === true
          || (typeof result === 'object' && result !== null && (result as any).handled === true);
      } catch (err) {
        console.error('[GlobalChat] sendProgrammaticMessage handler failed', err);
      }
    }

    if (handled) {
      removeImmediateThinking();
      return;
    }

    // Round J Category-A surfacing: a programmatic send must never die
    // silently — handler throw AND handled===false both land here. Release
    // the thread (visible sends started it) and append the retryable bubble.
    // Hidden sends never touched the processing flag, so releasing here
    // could stomp an unrelated live turn — skip the release for them.
    console.warn('[GlobalChat] Programmatic message not handled by any handler');
    removeImmediateThinking();
    if (!hidden) {
      stopProcessing(programmaticOriginatingThreadId);
    }
    appendSendFailureBubble(programmaticOriginatingThreadId, text);
  }, [addMessage, config, state.messages, state.processingByThread, globalTools, addSystemMessage,
      startStreamingMessage, updateStreamingMessage, updateStreamingMessageById,
      finalizeStreamingMessage, finalizeStreamingMessageById, removeMessage, removeMessageById,
      prest0nSessionAccountId, prest0nSessionHook, lowBalanceState.shouldBlock,
      startProcessing, stopProcessing, generateMessageId, armSendAckWatchdog,
      deferSendUntilAuthSettles, appendSendFailureBubble, appendSignInPromptOnce,
      runGuestChatTurn, pinToBottom]);

  // Round J: latest-envelope ref for the auth-hydration deferral (assigned
  // every commit so a deferred re-dispatch enters through fresh closures).
  useEffect(() => {
    sendProgrammaticMessageRef.current = sendProgrammaticMessage;
  }, [sendProgrammaticMessage]);

  // ============================================================================
  // Unified state machine synchronizer
  //
  // RENDER-LOOP FIX (OOM incident, Miles 2026-07-12): `globalTools` is a fresh
  // object literal every render (useGlobalTools returns an unmemoized literal),
  // so this effect re-runs after EVERY render — and it used to dispatch
  // unconditionally, and the reducer always returns new state: render →
  // effect → dispatch → render, an unconditional passive-effect loop on every
  // mount of the chat system (hidden persistent mounts included). Dispatch
  // only on an actual status TRANSITION so the cycle converges; the effect
  // still re-runs per render (cheap boolean probe), but a no-op probe no
  // longer schedules another render.
  useEffect(() => {
    const nextStatus: typeof state.inputState.status = globalTools?.isAnyToolAwaitingInput?.()
      ? 'awaiting_user_input'
      : (state.isProcessing ? 'computing' : 'idle');
    if (state.inputState.status !== nextStatus) {
      dispatch({ type: 'SET_INPUT_STATE', payload: { status: nextStatus } });
    }
  }, [state.isProcessing, state.inputState.status, globalTools]);

  // ============================================================================
  // BUG-DR-COMPOSER-REPOP-LONG-TURN — turn-lifecycle bridge to the shared
  // draft-suppression singleton. Bumps an in-flight refcount as long as
  // state.isProcessing is true so EVERY draft-rehydrate path (this hook's
  // poll/sync/hydrate, and the LandingPage hero composer's
  // poll/storage-listener) skips for the entire turn duration -- not just
  // the legacy 1500 ms post-send window. Deep Research turns legitimately
  // run 5-8 min; by the time the SSE / task-poll response lands the 1500 ms
  // window has long expired.
  //
  // The effect uses a ref to remember whether WE bumped the refcount so the
  // matching decrement fires exactly once on transition true->false (or on
  // unmount mid-turn).
  const draftSuppressionBumpedRef = useRef<boolean>(false);
  useEffect(() => {
    if (state.isProcessing && !draftSuppressionBumpedRef.current) {
      draftMarkProcessingStarted();
      draftSuppressionBumpedRef.current = true;
    } else if (!state.isProcessing && draftSuppressionBumpedRef.current) {
      draftMarkProcessingEnded();
      draftSuppressionBumpedRef.current = false;
    }
  }, [state.isProcessing]);
  // Drain the refcount on hook unmount if a turn was in flight (e.g. SPA
  // navigation away during streaming). Without this the cooldown timer
  // would never start ticking and any subsequent mount would see a stale
  // permanent suppression.
  useEffect(() => {
    return () => {
      if (draftSuppressionBumpedRef.current) {
        draftMarkProcessingEnded();
        draftSuppressionBumpedRef.current = false;
      }
    };
  }, []);

  /**
   * See UseGlobalChatResult.clearComposerForSuppressedDelivery. Mirrors
   * handleSend's post-send clear block, in the same order: suppression stamps
   * FIRST so any in-flight debounced draft save sees the window open and
   * bails, then the state + DOM + persisted-draft clears. CLEAR_ATTACHMENTS
   * is part of that block (2026-09-09): every group-thread send without an
   * @Prest0n mention rides this path, and the files it delivered must leave
   * the strip exactly as they do on the handleSend path — pinned by
   * composerSendClearsAttachments.test.ts.
   */
  const clearComposerForSuppressedDelivery = useCallback(() => {
    lastSendAtRef.current = Date.now();
    draftMarkSentNow();
    try {
      const text = state.prompt || '';
      const html = state.promptHtml || '';
      if (text || html) draftMarkSentText(text, html);
    } catch { /* defensive — sentinel is best-effort */ }
    dispatch({ type: 'RESET_PROMPT' });
    dispatch({ type: 'CLEAR_ATTACHMENTS' });
    if (draftPersistTimeoutRef.current) {
      window.clearTimeout(draftPersistTimeoutRef.current);
      draftPersistTimeoutRef.current = null;
    }
    draftPendingFlushRef.current = null;
    draftLastSavedRef.current = null;
    if (config.persistDraft) {
      removeFromLocalStorage(storageKeys.CHAT_DRAFT);
    }
    if (promptInputRef.current) {
      promptInputRef.current.innerHTML = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.prompt, state.promptHtml, config.persistDraft, storageKeys.CHAT_DRAFT]);

  // Return Value
  // ============================================================================

  const handlers: GlobalChatHandlers = {
    handleSend,
    handleInput,
    handlePaste,
    handleKeyDown,
    handleToggleDock,
    handleToggleFullScreen,
    handleCycleMode,
    handleFileSelect,
    handleFileChange,
    handleRemoveAttachment,
    handleClearAttachments,
    toggleAttachmentPanel,
    handleAttachmentButtonClick,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleHide,
    handleOpen,
    getCurrentPlainText,
    handlePromptFocus,
    handlePromptBlur,
    handleClearChat,
    confirmClearChat,
    hideClearConfirmation,
  };

  return {
    state,
    dispatch,
    config,
    handlers,
    refs: {
      promptInputRef,
      chatMessagesRef,
      transcriptContentRef,
      fileInputRef,
      promptBarRef,
      floatingPromptBarRef,
      sendRouteOverrideRef,
      chatContainerRef,
      onProcessingBlockedRef,
      getActiveThreadIdRef,
      writeThreadMessagesRef,
      readThreadMessagesRef,
      isThreadSharedRef,
      composeGreetingRef,
      messagesSyncRef,
    },
    waitlistController,
    showScrollToBottom,
    isPinnedNow,
    isNearTop,
    newMessageCount,
    hasUnseenActivity,
    scrollToBottom,
    pinToBottom,
    scrollToTopAnimated,
    isAtThreadTop,
    isAtThreadBottom,
    stepPosition,
    stepToPreviousMessage,
    stepToNextMessage,
    // Agentic workflows state (for event delegation)
    agenticWorkflows,
    // GlobalTools state (new unified tool system)
    globalTools,
    // Processing state control (prevents sending during active operations)
    isSystemProcessing,
    startProcessing,
    stopProcessing,
    // Cancel the active thread's in-flight turn AND flush its pending
    // queue. Use this from the "stop" UI instead of calling
    // globalTools.cancelRunningTool() directly so queued messages are
    // not silently fired after the user pressed stop.
    // (PR feat/same-thread-queue-while-streaming, Miles 2026-05-09.)
    cancelActiveThread: () => {
      const tid = (getActiveThreadIdRef.current && getActiveThreadIdRef.current()) || null;
      // 1. Flush any queued messages on the active thread. Each is
      //    re-rendered (via routedUpdateById on the user bubbles
      //    themselves) with a recoverable abort note. Today we just
      //    drop them from the queue and surface a system message in
      //    the thread; the user bubbles for the queued entries stay
      //    in history (so the user can copy/paste/re-send).
      if (tid) {
        const aborted = (state.pendingByThread || {})[tid] || [];
        if (aborted.length > 0) {
          dispatch({ type: 'FLUSH_PENDING', payload: { threadId: tid } });
          try {
            addSystemMessage({
              text: `${aborted.length} queued message${aborted.length === 1 ? '' : 's'} not sent — first turn stopped. Re-send to continue.`,
              sourceTag: 'queue_flushed_on_abort',
            });
          } catch { /* ignore */ }
        }
      }
      // 2. Abort the in-flight orchestrator on this thread.
      if (globalTools?.cancelRunningTool) {
        try { globalTools.cancelRunningTool(tid); } catch { /* ignore */ }
      }
    },
    // Finding 8c (2026-07-15): thread archive/delete watchdog teardown.
    cancelThreadSendWatchdog,
    // Owner directive 2026-08-22 ("still working"): the pane pokes discovery
    // on thread open/switch so server-started work in the newly opened thread
    // lights its chip within one round-trip, not one poll interval.
    pokeActiveTaskDiscovery: runActiveTaskDiscovery,
    // Low balance warning state (balance below $3 USD threshold)
    lowBalanceState,
    // Expired-trial chat gate (subscription-gated; owner directive 2026-07-31)
    trialExpiredBlock,
    // Programmatic message send (onboarding welcome, etc.)
    sendProgrammaticMessage,
    // Full composer clear for sends delivered outside handleSend (W3b).
    clearComposerForSuppressedDelivery,
    // True once Prest0n VM session is fully initialized
    isPrest0nSessionReady: Boolean(prest0nSessionHook.session),
    // Canonical resolved account / workspace id (null until the migration gate
    // passes). Exposed for account-scoped UI gating (e.g. the thread choice panel).
    prest0nSessionAccountId,
    workspaceUnresolved,
    // Cold-boot fail-closed (2026-08-21): true while the migration gate's
    // verdict for this auth identity is still in flight — the host composes
    // this with the confirmed-workspace seed to hold thread subscriptions
    // instead of rendering the unfiltered cross-workspace union.
    isMigrationGateChecking,
  };
}

export default useGlobalChat;
